import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { 
  ShoppingCart, Menu, X, Globe, User, LogOut, ChevronRight, 
  Mail, Phone, MapPin, Instagram, Facebook, ArrowRight,
  Minus, Plus, Trash2, CreditCard, Shield, Truck, Edit
} from 'lucide-react';

// TikTok icon component (not in lucide-react)
const TikTokIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Cookie Consent Banner
// GDPR Cookie Consent Context
const CookieConsentContext = createContext();
export const useCookieConsent = () => useContext(CookieConsentContext);

function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => {
    const stored = localStorage.getItem('gdpr_consent');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });

  const updateConsent = (newConsent) => {
    localStorage.setItem('gdpr_consent', JSON.stringify(newConsent));
    setConsent(newConsent);
  };

  const hasConsent = (category) => {
    if (!consent) return false;
    return consent[category] === true;
  };

  const revokeConsent = () => {
    localStorage.removeItem('gdpr_consent');
    setConsent(null);
    // Clear all non-essential cookies
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (name && name !== 'gdpr_consent') {
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      }
    });
  };

  return (
    <CookieConsentContext.Provider value={{ consent, updateConsent, hasConsent, revokeConsent }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { lang } = useLanguage();
  const { consent, updateConsent } = useCookieConsent();
  
  // Cookie categories
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    external: false // YouTube, external scripts
  });

  useEffect(() => {
    if (!consent) {
      setShowBanner(true);
    }
  }, [consent]);

  const acceptAll = () => {
    const newConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      external: true,
      timestamp: new Date().toISOString()
    };
    updateConsent(newConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    const newConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      external: false,
      timestamp: new Date().toISOString()
    };
    updateConsent(newConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  const savePreferences = () => {
    const newConsent = {
      ...preferences,
      necessary: true,
      timestamp: new Date().toISOString()
    };
    updateConsent(newConsent);
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner && !showSettings) return null;

  const texts = {
    title: lang === 'it' ? 'Privacy e Cookie' : 'Privacy & Cookies',
    description: lang === 'it' 
      ? 'Utilizziamo cookie per offrirti la migliore esperienza possibile.'
      : 'We use cookies to give you the best possible experience.',
    necessary: lang === 'it' ? 'Cookie Necessari' : 'Necessary Cookies',
    necessaryDesc: lang === 'it' ? 'Essenziali per il funzionamento del sito (sempre attivi)' : 'Essential for the site to function (always active)',
    analytics: lang === 'it' ? 'Cookie Analitici' : 'Analytics Cookies',
    analyticsDesc: lang === 'it' ? 'Ci aiutano a capire come usi il sito' : 'Help us understand how you use the site',
    marketing: lang === 'it' ? 'Cookie Marketing' : 'Marketing Cookies',
    marketingDesc: lang === 'it' ? 'Utilizzati per pubblicità personalizzata' : 'Used for personalized advertising',
    external: lang === 'it' ? 'Contenuti Esterni' : 'External Content',
    externalDesc: lang === 'it' ? 'YouTube e altri servizi esterni' : 'YouTube and other external services',
    acceptAll: lang === 'it' ? 'Accetta' : 'Accept',
    rejectAll: lang === 'it' ? 'Rifiuta' : 'Decline',
    settings: lang === 'it' ? 'Personalizza' : 'Customize',
    save: lang === 'it' ? 'Salva' : 'Save',
    moreInfo: lang === 'it' ? 'Maggiori informazioni' : 'More information',
    cookiePolicy: lang === 'it' ? 'Cookie Policy' : 'Cookie Policy',
    privacyPolicy: lang === 'it' ? 'Privacy' : 'Privacy'
  };

  return (
    <>
      {/* Elegant Minimal Banner */}
      {showBanner && !showSettings && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 md:max-w-md z-50"
          data-testid="cookie-banner"
        >
          <div className="bg-background/95 backdrop-blur-md border border-border/50 shadow-xl rounded-lg p-5">
            <div className="space-y-4">
              <div>
                <h3 className="font-heading text-base tracking-wide mb-2">{texts.title}</h3>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">
                  {texts.description}
                </p>
              </div>
              
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <a href="/cookie-policy" className="hover:text-foreground transition-colors underline-offset-2 hover:underline">{texts.cookiePolicy}</a>
                <span className="opacity-30">|</span>
                <a href="/privacy" className="hover:text-foreground transition-colors underline-offset-2 hover:underline">{texts.privacyPolicy}</a>
              </div>
              
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex-1 px-4 py-2.5 text-xs font-medium border border-border rounded hover:bg-muted transition-colors"
                >
                  {texts.settings}
                </button>
                <button
                  onClick={rejectAll}
                  className="flex-1 px-4 py-2.5 text-xs font-medium border border-border rounded hover:bg-muted transition-colors"
                  data-testid="cookie-decline"
                >
                  {texts.rejectAll}
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 px-4 py-2.5 text-xs font-medium bg-foreground text-background rounded hover:bg-foreground/90 transition-colors"
                  data-testid="cookie-accept"
                >
                  {texts.acceptAll}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Elegant Settings Modal */}
      {showSettings && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowSettings(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="bg-background rounded-xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-hidden"
          >
            <div className="p-6 border-b border-border/50">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg tracking-wide">
                  {lang === 'it' ? 'Preferenze Cookie' : 'Cookie Preferences'}
                </h2>
                <button 
                  onClick={() => setShowSettings(false)} 
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-3 overflow-auto max-h-[50vh]">
              {/* Necessary Cookies - Always On */}
              <div className="p-4 bg-muted/30 rounded-lg border border-border/30">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-sm">{texts.necessary}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{texts.necessaryDesc}</p>
                  </div>
                  <span className="text-xs text-muted-foreground italic">
                    {lang === 'it' ? 'Sempre attivi' : 'Always on'}
                  </span>
                </div>
              </div>

              {/* Analytics */}
              <div className="p-4 rounded-lg border border-border/50 hover:border-border transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-4">
                    <h3 className="font-medium text-sm">{texts.analytics}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{texts.analyticsDesc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-muted rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border after:border-border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-foreground"></div>
                  </label>
                </div>
              </div>

              {/* Marketing */}
              <div className="p-4 rounded-lg border border-border/50 hover:border-border transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-4">
                    <h3 className="font-medium text-sm">{texts.marketing}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{texts.marketingDesc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-muted rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border after:border-border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-foreground"></div>
                  </label>
                </div>
              </div>

              {/* External Content */}
              <div className="p-4 rounded-lg border border-border/50 hover:border-border transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-4">
                    <h3 className="font-medium text-sm">{texts.external}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{texts.externalDesc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={preferences.external}
                      onChange={(e) => setPreferences({...preferences, external: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-muted rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border after:border-border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-foreground"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-border/50 bg-muted/20">
              <div className="flex gap-2">
                <button 
                  onClick={rejectAll} 
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  {texts.rejectAll}
                </button>
                <button 
                  onClick={savePreferences} 
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  {texts.save}
                </button>
                <button 
                  onClick={acceptAll} 
                  className="flex-1 px-4 py-2.5 text-sm font-medium bg-foreground text-background rounded-lg hover:bg-foreground/90 transition-colors"
                >
                  {texts.acceptAll}
                </button>
              </div>
              
              <div className="mt-4 text-center">
                <a href="/cookie-policy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  {texts.moreInfo} →
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}

// Cookie Settings Button for Footer
function CookieSettingsButton() {
  const [showSettings, setShowSettings] = useState(false);
  const { lang } = useLanguage();
  const { consent, updateConsent, revokeConsent } = useCookieConsent();
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: consent?.analytics || false,
    marketing: consent?.marketing || false,
    external: consent?.external || false
  });

  useEffect(() => {
    if (consent) {
      setPreferences({
        necessary: true,
        analytics: consent.analytics || false,
        marketing: consent.marketing || false,
        external: consent.external || false
      });
    }
  }, [consent]);

  const savePreferences = () => {
    updateConsent({
      ...preferences,
      necessary: true,
      timestamp: new Date().toISOString()
    });
    setShowSettings(false);
  };

  const texts = {
    button: lang === 'it' ? '🍪 Gestisci Cookie' : '🍪 Manage Cookies',
    title: lang === 'it' ? 'Impostazioni Cookie' : 'Cookie Settings',
    necessary: lang === 'it' ? 'Cookie Necessari' : 'Necessary Cookies',
    analytics: lang === 'it' ? 'Cookie Analitici' : 'Analytics',
    marketing: lang === 'it' ? 'Cookie Marketing' : 'Marketing',
    external: lang === 'it' ? 'Contenuti Esterni' : 'External Content',
    save: lang === 'it' ? 'Salva' : 'Save',
    revoke: lang === 'it' ? 'Revoca Consenso' : 'Revoke Consent'
  };

  return (
    <>
      <button
        onClick={() => setShowSettings(true)}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {texts.button}
      </button>

      {showSettings && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowSettings(false)}
        >
          <div className="bg-background rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg">{texts.title}</h2>
              <button onClick={() => setShowSettings(false)}>✕</button>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                <span className="text-sm">🔒 {texts.necessary}</span>
                <span className="text-xs text-green-600">{lang === 'it' ? 'Sempre Attivo' : 'Always On'}</span>
              </div>
              <div className="flex items-center justify-between p-3 border rounded">
                <span className="text-sm">📊 {texts.analytics}</span>
                <input type="checkbox" checked={preferences.analytics} onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})} className="w-4 h-4" />
              </div>
              <div className="flex items-center justify-between p-3 border rounded">
                <span className="text-sm">📢 {texts.marketing}</span>
                <input type="checkbox" checked={preferences.marketing} onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})} className="w-4 h-4" />
              </div>
              <div className="flex items-center justify-between p-3 border rounded">
                <span className="text-sm">🌐 {texts.external}</span>
                <input type="checkbox" checked={preferences.external} onChange={(e) => setPreferences({...preferences, external: e.target.checked})} className="w-4 h-4" />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => { revokeConsent(); setShowSettings(false); }} className="flex-1 text-red-600">
                {texts.revoke}
              </Button>
              <Button size="sm" onClick={savePreferences} className="flex-1 bg-primary">
                {texts.save}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// WhatsApp Floating Button
function WhatsAppButton() {
  const [whatsappNumber, setWhatsappNumber] = useState('+393206215750');
  const [isVisible, setIsVisible] = useState(true);
  const { lang } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/site-settings`)
      .then(res => {
        if (res.data.whatsapp_number) {
          setWhatsappNumber(res.data.whatsapp_number);
        }
        if (res.data.whatsapp_visible === false) {
          setIsVisible(false);
        }
      })
      .catch(console.error);
  }, []);

  if (!isVisible) return null;

  // Clean number for WhatsApp link (remove spaces and special chars except +)
  const cleanNumber = whatsappNumber.replace(/[^0-9+]/g, '');
  const whatsappUrl = `https://wa.me/${cleanNumber.replace('+', '')}`;

  return (
    <motion.a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 1, type: "spring", stiffness: 200 }}
      className="fixed bottom-24 right-6 z-40 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
      title={lang === 'it' ? 'Contattaci su WhatsApp' : 'Contact us on WhatsApp'}
      data-testid="whatsapp-button"
    >
      <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </motion.a>
  );
}
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from './components/ui/sheet';
import { Separator } from './components/ui/separator';
import { BACKEND_URL, API, translations, LanguageContext, useLanguage, AuthContext, useAuth, CartContext, useCart, WatermarkContext, useWatermark, SiteSettingsContext, useSiteSettings } from './constants';

// ============ PROVIDERS ============

function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState({
    home_visible: true,
    whatsapp_visible: true,
    menu_home_en: 'Home',
    menu_home_it: 'Home',
    menu_gallery_en: 'Gallery',
    menu_gallery_it: 'Galleria',
    menu_about_en: 'About',
    menu_about_it: 'Chi Sono',
    menu_contact_en: 'Contact',
    menu_contact_it: 'Contatti',
    hero_button_en: 'View Collection',
    hero_button_it: 'Scopri la Collezione'
  });

  useEffect(() => {
    axios.get(`${API}/site-settings`)
      .then(res => setSettings(prev => ({ ...prev, ...res.data })))
      .catch(console.error);
  }, []);

  return (
    <SiteSettingsContext.Provider value={settings}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const t = translations[lang];
  
  const toggleLang = () => {
    const newLang = lang === 'en' ? 'it' : 'en';
    setLang(newLang);
    localStorage.setItem('lang', newLang);
  };
  
  return (
    <LanguageContext.Provider value={{ lang, t, toggleLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => { localStorage.removeItem('token'); setToken(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
    
    // Track cart on server
    if (items.length > 0) {
      const trackCart = async () => {
        try {
          await axios.post(`${API}/track-cart`, {
            items: items.map(item => ({
              painting_id: item.id,
              title: item.title,
              price: item.price,
              quantity: item.quantity || 1
            }))
          });
        } catch (err) {
          // Silent fail - tracking is optional
        }
      };
      trackCart();
    }
  }, [items]);

  const addItem = (painting) => {
    setItems(prev => {
      const exists = prev.find(i => i.id === painting.id);
      if (exists) return prev;
      return [...prev, { ...painting, quantity: 1 }];
    });
    toast.success('Added to cart');
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const clearCart = () => setItems([]);

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, clearCart, total }}>
      {children}
    </CartContext.Provider>
  );
}

// Watermark Provider - loads watermark settings once
function WatermarkProvider({ children }) {
  const [watermarkSettings, setWatermarkSettings] = useState({
    text: '',
    enabled: false,
    opacity: 15
  });

  useEffect(() => {
    axios.get(`${API}/site-settings`)
      .then(res => {
        setWatermarkSettings({
          text: res.data.watermark_text || '',
          enabled: res.data.watermark_enabled || false,
          opacity: res.data.watermark_opacity || 15
        });
      })
      .catch(() => {});
  }, []);

  return (
    <WatermarkContext.Provider value={watermarkSettings}>
      {children}
    </WatermarkContext.Provider>
  );
}

// Watermark Overlay Component
function WatermarkOverlay() {
  const watermark = useWatermark();
  
  if (!watermark.enabled || !watermark.text) return null;
  
  return (
    <div 
      className="absolute inset-0 pointer-events-none overflow-hidden z-10"
      style={{ userSelect: 'none' }}
    >
      <div 
        className="absolute whitespace-nowrap font-heading tracking-widest"
        style={{
          fontSize: '14px',
          color: `rgba(255, 255, 255, ${watermark.opacity / 100})`,
          textShadow: `0 0 8px rgba(0, 0, 0, ${watermark.opacity / 100})`,
          transform: 'rotate(-35deg) scale(1.5)',
          letterSpacing: '4px',
          top: '50%',
          left: '50%',
          marginLeft: '-150%',
          marginTop: '-50%',
          width: '400%'
        }}
      >
        {Array(8).fill(watermark.text).join('       ')}
      </div>
    </div>
  );
}

// ============ COMPONENTS ============

function Navbar() {
  const { t, lang, toggleLang } = useLanguage();
  const { user, logout } = useAuth();
  const { items } = useCart();
  const siteSettings = useSiteSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Get menu labels from site settings or fallback to translations
  const menuLabels = {
    home: lang === 'en' ? (siteSettings.menu_home_en || t.nav.home) : (siteSettings.menu_home_it || t.nav.home),
    gallery: lang === 'en' ? (siteSettings.menu_gallery_en || t.nav.gallery) : (siteSettings.menu_gallery_it || t.nav.gallery),
    about: lang === 'en' ? (siteSettings.menu_about_en || t.nav.about) : (siteSettings.menu_about_it || t.nav.about),
    contact: lang === 'en' ? (siteSettings.menu_contact_en || t.nav.contact) : (siteSettings.menu_contact_it || t.nav.contact)
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-background/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="font-heading text-2xl md:text-3xl tracking-tight" data-testid="logo">
            
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {siteSettings.home_visible !== false && (
              <Link to="/" className="link-underline font-body text-sm tracking-wide uppercase" data-testid="nav-home">{menuLabels.home}</Link>
            )}
            <Link to="/gallery" className="link-underline font-body text-sm tracking-wide uppercase" data-testid="nav-gallery">{menuLabels.gallery}</Link>
            <Link to="/about" className="link-underline font-body text-sm tracking-wide uppercase" data-testid="nav-about">{menuLabels.about}</Link>
            <Link to="/contact" className="link-underline font-body text-sm tracking-wide uppercase" data-testid="nav-contact">{menuLabels.contact}</Link>
            {user?.is_admin && (
              <Link to="/admin" className="link-underline font-body text-sm tracking-wide uppercase text-primary" data-testid="nav-admin">{t.nav.admin}</Link>
            )}
          </div>

          <div className="flex items-center gap-4">
            <button onClick={toggleLang} className="flex items-center gap-1 text-sm font-body" data-testid="lang-toggle">
              <Globe size={16} />
              <span className="uppercase">{lang}</span>
            </button>

            {user ? (
              <button onClick={logout} className="hidden md:flex items-center gap-1 text-sm" data-testid="logout-btn">
                <LogOut size={16} />
              </button>
            ) : (
              <Link to="/admin/login" className="hidden md:block" data-testid="login-link">
                <User size={18} />
              </Link>
            )}

            <Sheet>
              <SheetTrigger asChild>
                <button className="relative" data-testid="cart-btn">
                  <ShoppingCart size={20} />
                  {items.length > 0 && (
                    <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {items.length}
                    </span>
                  )}
                </button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md">
                <CartDrawer />
              </SheetContent>
            </Sheet>

            <button onClick={() => setIsOpen(true)} className="md:hidden" data-testid="mobile-menu-btn">
              <Menu size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background z-50 md:hidden"
          >
            <div className="flex flex-col h-full p-6">
              <div className="flex justify-between items-center mb-12">
                <span className="font-heading text-2xl">Menu</span>
                <button onClick={() => setIsOpen(false)} data-testid="close-mobile-menu">
                  <X size={24} />
                </button>
              </div>
              <nav className="flex flex-col gap-6">
                {siteSettings.home_visible !== false && (
                  <Link to="/" onClick={() => setIsOpen(false)} className="font-heading text-3xl">{menuLabels.home}</Link>
                )}
                <Link to="/gallery" onClick={() => setIsOpen(false)} className="font-heading text-3xl">{menuLabels.gallery}</Link>
                <Link to="/about" onClick={() => setIsOpen(false)} className="font-heading text-3xl">{menuLabels.about}</Link>
                <Link to="/contact" onClick={() => setIsOpen(false)} className="font-heading text-3xl">{menuLabels.contact}</Link>
                {user?.is_admin && (
                  <Link to="/admin" onClick={() => setIsOpen(false)} className="font-heading text-3xl text-primary">{t.nav.admin}</Link>
                )}
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

function CartDrawer() {
  const { t, lang } = useLanguage();
  const { items, removeItem, total } = useCart();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full" data-testid="cart-drawer">
      <h2 className="font-heading text-2xl mb-6">{t.cart.title}</h2>
      
      {items.length === 0 ? (
        <p className="text-muted-foreground">{t.cart.empty}</p>
      ) : (
        <>
          <div className="flex-1 overflow-auto hide-scrollbar">
            {items.map(item => (
              <div key={item.id} className="flex gap-4 mb-4 pb-4 border-b" data-testid={`cart-item-${item.id}`}>
                <img src={item.image_url} alt={item.title} className="w-20 h-20 object-cover" />
                <div className="flex-1">
                  <h4 className="font-heading text-sm">{lang === 'it' ? item.title_it : item.title}</h4>
                  <p className="text-secondary font-body text-sm">€{item.price.toLocaleString()}</p>
                  <button 
                    onClick={() => removeItem(item.id)} 
                    className="text-xs text-muted-foreground flex items-center gap-1 mt-2"
                    data-testid={`remove-item-${item.id}`}
                  >
                    <Trash2 size={12} /> {t.cart.remove}
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="pt-4 border-t">
            <div className="flex justify-between mb-4">
              <span className="font-body">{t.cart.total}</span>
              <span className="font-heading text-xl">€{total.toLocaleString()}</span>
            </div>
            <SheetClose asChild>
              <Button 
                onClick={() => navigate('/checkout')} 
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="checkout-btn"
              >
                {t.cart.checkout} <ArrowRight size={16} className="ml-2" />
              </Button>
            </SheetClose>
          </div>
        </>
      )}
    </div>
  );
}

function PaintingCard({ painting }) {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();

  const description = lang === 'it' ? painting.description_it : painting.description;
  const shortDesc = description ? description.substring(0, 80) + (description.length > 80 ? '...' : '') : '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="group cursor-pointer"
      data-testid={`painting-card-${painting.id}`}
      onClick={() => navigate(`/painting/${painting.id}`)}
    >
      <div className={`relative overflow-hidden mb-4 ${painting.is_promotion ? 'painting-promotion p-4' : ''}`}>
        {/* Badges row - top left */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
          {/* Promotion Badge - Gold button */}
          {painting.is_promotion && (
            <div className="bg-gradient-to-r from-amber-600 to-yellow-500 text-white px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold shadow-md">
              {lang === 'it' ? 'Promo' : 'Promo'}
            </div>
          )}
          {/* Unique Art Badge - Fluorescent green button */}
          {painting.is_unique_art && (
            <div className="bg-gradient-to-r from-lime-500 to-green-400 text-white px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold shadow-md">
              {lang === 'it' ? 'Unica' : 'Unique'}
            </div>
          )}
        </div>
        <div className="painting-frame relative">
          <img
            src={painting.image_url}
            alt={painting.title}
            className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <WatermarkOverlay />
        </div>
        {painting.is_sold && (
          <div className="absolute top-3 right-3 bg-red-800 text-white px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold shadow-md">
            {t.gallery.sold}
          </div>
        )}
        {/* View details overlay on hover */}
        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <span className="bg-background/90 text-foreground px-4 py-2 text-sm font-medium rounded">
            {lang === 'it' ? 'Vedi dettagli' : 'View Details'}
          </span>
        </div>
      </div>
      <div className="text-center">
        <h3 className="font-heading text-lg mb-1">{lang === 'it' ? painting.title_it : painting.title}</h3>
        <p className="text-sm text-muted-foreground mb-2 px-2 line-clamp-2">{shortDesc}</p>
        {!painting.is_sold && (
          <p className="text-secondary font-body tracking-wider">€{painting.price.toLocaleString()}</p>
        )}
      </div>
    </motion.div>
  );
}

function Footer() {
  const { t, lang } = useLanguage();
  const [email, setEmail] = useState('');
  const [footerText, setFooterText] = useState('© 2024 Elena Candalia');
  const [contactInfo, setContactInfo] = useState({
    email: '', phone: '', website: '', address: '', city: '', country: '',
    instagram: '', facebook: '', tiktok: ''
  });

  useEffect(() => {
    axios.get(`${API}/contact-settings`).then(res => {
      if (res.data) setContactInfo(res.data);
    }).catch(() => {});
    
    axios.get(`${API}/site-settings`).then(res => {
      if (res.data.footer_text) setFooterText(res.data.footer_text);
    }).catch(() => {});
  }, []);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Subscribed successfully!');
      setEmail('');
    } catch {
      toast.error('Failed to subscribe');
    }
  };

  // Build location string from available fields
  const locationStr = [contactInfo.city, contactInfo.country].filter(Boolean).join(', ') || 'Spoleto, Italy';

  return (
    <footer className="bg-muted border-t border-border py-16 mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <div className="flex gap-4">
              {contactInfo.instagram && (
                <a href={contactInfo.instagram} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors" data-testid="social-instagram"><Instagram size={20} /></a>
              )}
              {contactInfo.facebook && (
                <a href={contactInfo.facebook} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors" data-testid="social-facebook"><Facebook size={20} /></a>
              )}
              {contactInfo.tiktok && (
                <a href={contactInfo.tiktok} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors" data-testid="social-tiktok"><TikTokIcon size={20} /></a>
              )}
              {/* Show default icons if no social media configured */}
              {!contactInfo.instagram && !contactInfo.facebook && !contactInfo.tiktok && (
                <>
                  <a href="https://www.instagram.com/studiodartecandalia" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors" data-testid="social-instagram"><Instagram size={20} /></a>
                  <a href="https://www.facebook.com/share/1CT3nqPmCv/" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors" data-testid="social-facebook"><Facebook size={20} /></a>
                  <a href="https://www.tiktok.com/@studiodartecandalia" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors" data-testid="social-tiktok"><TikTokIcon size={20} /></a>
                </>
              )}
            </div>
          </div>
          
          <div>
            <h4 className="font-accent text-sm tracking-[0.2em] uppercase mb-4 text-foreground">Contact</h4>
            <div className="space-y-3 text-sm font-body text-muted-foreground">
              <p className="flex items-center gap-2"><Mail size={14} /> {contactInfo.email || 'studioart@elenacandalia.it'}</p>
              <p className="flex items-center gap-2"><Globe size={14} /> {contactInfo.website || 'elenacandalia.it'}</p>
              <p className="flex items-center gap-2"><MapPin size={14} /> {locationStr}</p>
            </div>
          </div>
          
          <div>
            <h4 className="font-accent text-sm tracking-[0.2em] uppercase mb-4 text-foreground">{t.contact.newsletter}</h4>
            <form onSubmit={handleNewsletter} className="flex gap-2" data-testid="newsletter-form">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                data-testid="newsletter-email"
              />
              <Button type="submit" variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background" data-testid="newsletter-submit">
                <ArrowRight size={16} />
              </Button>
            </form>
          </div>
        </div>
        
        <Separator className="bg-border mb-6" />
        
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm font-body text-muted-foreground">
          <p>{footerText}. {t.footer.rights}</p>
          <div className="flex items-center gap-6">
            {/* Payment Methods */}
            <div className="flex items-center gap-2">
              <img src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.0.0/flags/4x3/eu.svg" alt="EUR" className="h-5 w-auto" title="EUR Payments" />
              <svg viewBox="0 0 38 24" className="h-6 w-auto" aria-label="Visa"><path fill="#1434CB" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.3 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3z"/><path fill="#fff" d="M14.7 16.5l1.6-9h2.5l-1.6 9h-2.5zm10.5-8.8c-.5-.2-1.3-.4-2.2-.4-2.5 0-4.2 1.3-4.2 3.1 0 1.4 1.2 2.1 2.2 2.6 1 .5 1.3.8 1.3 1.2 0 .7-.8 1-1.5 1-1 0-1.5-.1-2.4-.5l-.3-.1-.4 2.1c.6.3 1.7.5 2.8.5 2.6 0 4.3-1.3 4.3-3.2 0-1.1-.7-1.9-2.1-2.6-.9-.4-1.4-.7-1.4-1.2 0-.4.5-.8 1.4-.8.8 0 1.4.2 1.9.4l.2.1.4-2.2zm6.4-.2h-2c-.6 0-1.1.2-1.4.8l-3.9 9.2h2.8l.6-1.5h3.4l.3 1.5h2.4l-2.2-10zm-3.3 6.5l1.1-2.9.3-.9.2.8.6 3h-2.2zM13.2 7.5l-2.4 6.2-.3-1.3c-.4-1.5-1.9-3.2-3.5-4l2.4 8.6h2.8l4.2-9.5h-3.2z"/><path fill="#F9A51A" d="M8.2 7.5H4.1l-.1.2c3.3.8 5.4 2.8 6.3 5.2l-.9-4.6c-.2-.6-.6-.8-1.2-.8z"/></svg>
              <svg viewBox="0 0 38 24" className="h-6 w-auto" aria-label="Mastercard"><path fill="#000" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.3 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3z"/><circle fill="#EB001B" cx="15" cy="12" r="7"/><circle fill="#F79E1B" cx="23" cy="12" r="7"/><path fill="#FF5F00" d="M19 7.5c-1.3 1.1-2.2 2.7-2.2 4.5s.9 3.4 2.2 4.5c1.3-1.1 2.2-2.7 2.2-4.5s-.9-3.4-2.2-4.5z"/></svg>
              <svg viewBox="0 0 38 24" className="h-6 w-auto" aria-label="Amex"><path fill="#006FCF" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.3 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3z"/><path fill="#fff" d="M10 16.5V7.6h6.8l.8 1.8.8-1.8h6.8v8.9h-5.8l-.9-1.8-.9 1.8H10zm1.4-1.3h2.3V14h-2v-1.2h2.2v-1.3h-2.3v-1.2h2.3V9h-3.6v6.2h1.1zm4.8 0l2.2-3.1-2.1-3.1h1.7l1.3 2 1.3-2h1.6l-2.1 3.1 2.2 3.1h-1.7l-1.4-2.1-1.4 2.1h-1.6zm6.4 0V9h1.4v6.2h-1.4z"/></svg>
            </div>
            <a href="https://www.protectmywork.com" target="_blank" rel="noopener noreferrer">
              <img 
                src="https://customer-assets.emergentagent.com/job_elena-art-gallery/artifacts/33p90wlg_copyright-300x298.webp" 
                alt="Copyright Protected by ProtectMyWork.com" 
                className="h-12 w-auto opacity-80 hover:opacity-100 transition-opacity"
              />
            </a>
            <div className="flex flex-wrap gap-4 md:gap-6 items-center">
              <Link to="/privacy" className="hover:text-foreground transition-colors">{t.footer.privacy}</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">{t.footer.terms}</Link>
              <Link to="/refund" className="hover:text-foreground transition-colors">{t.footer.refund}</Link>
              <Link to="/cookie-policy" className="hover:text-foreground transition-colors">
                {lang === 'it' ? 'Cookie Policy' : 'Cookie Policy'}
              </Link>
              <CookieSettingsButton />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============ PAGES ============

function HomePage() {
  const { t, lang } = useLanguage();
  const [featured, setFeatured] = useState([]);
  const [coverImage, setCoverImage] = useState('https://customer-assets.emergentagent.com/job_elena-art-gallery/artifacts/5pup7imy_FB_IMG_1766394920850.jpg');
  const [heroTitle, setHeroTitle] = useState('Elena Candalia');
  const [heroTitleColor, setHeroTitleColor] = useState('#1a1a1a');
  const [heroButtonEn, setHeroButtonEn] = useState('View Collection');
  const [heroButtonIt, setHeroButtonIt] = useState('Scopri la Collezione');
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/paintings?featured=true`)
      .then(res => setFeatured(res.data))
      .catch(console.error);
    
    axios.get(`${API}/site-settings`)
      .then(res => {
        if (res.data.cover_image_url) {
          setCoverImage(res.data.cover_image_url);
        }
        if (res.data.hero_title) {
          setHeroTitle(res.data.hero_title);
        }
        if (res.data.hero_title_color) {
          setHeroTitleColor(res.data.hero_title_color);
        }
        if (res.data.hero_button_en) {
          setHeroButtonEn(res.data.hero_button_en);
        }
        if (res.data.hero_button_it) {
          setHeroButtonIt(res.data.hero_button_it);
        }
      })
      .catch(console.error);
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={coverImage}
            alt="Elena Candalia Oil Painting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/50 to-white" />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="relative text-center z-10 px-6"
        >
          <h1 
            className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tight mb-8" 
            data-testid="hero-title"
            style={{ color: heroTitleColor }}
          >
            {heroTitle}
          </h1>
          <Button
            onClick={() => navigate('/gallery')}
            size="lg"
            className="bg-primary hover:bg-primary/90 px-8"
            data-testid="hero-cta"
          >
            {(lang === 'en' ? heroButtonEn : heroButtonIt) || t.hero.cta} <ChevronRight size={18} className="ml-2" />
          </Button>
        </motion.div>
      </section>

      {/* About Preview */}
      <section className="bg-muted py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_elena-art-gallery/artifacts/jzvkhg9l_FB_IMG_1766394923482.jpg"
                alt="Elena Candalia"
                className="painting-frame"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="font-accent text-xs tracking-[0.3em] uppercase text-secondary">{t.about.title}</span>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight mt-2 mb-6">The Artist Behind the Canvas</h2>
              <p className="font-body text-muted-foreground leading-relaxed mb-6">
                {t.about.bio}
              </p>
              <p className="font-body text-muted-foreground leading-relaxed mb-8">
                Her works have been exhibited in prestigious galleries across Venice, Florence, and Rome, earning acclaim from critics for their emotional depth and technical mastery.
              </p>
              <Button onClick={() => navigate('/about')} variant="outline" data-testid="learn-more-btn">
                Learn More <ChevronRight size={16} className="ml-1" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

function GalleryPage() {
  const { t, lang } = useLanguage();
  const [paintings, setPaintings] = useState([]);
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  // Fetch categories from API
  useEffect(() => {
    axios.get(`${API}/categories`)
      .then(res => setCategories(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    const url = category === 'all' ? `${API}/paintings` : `${API}/paintings?category=${category}`;
    axios.get(url)
      .then(res => {
        // Sort: pinned first, then available paintings, then sold paintings at the end
        const sorted = res.data.sort((a, b) => {
          // Sold paintings always go last
          if (a.is_sold && !b.is_sold) return 1;
          if (!a.is_sold && b.is_sold) return -1;
          // Among non-sold, pinned first
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          return 0;
        });
        setPaintings(sorted);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div className="pt-32 pb-24" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-4xl md:text-6xl tracking-tight mb-4">{t.gallery.title}</h1>
          <p className="font-body text-muted-foreground">
            A curated selection of the most recent artworks
          </p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {/* All filter button */}
          <button
            key="all"
            onClick={() => setCategory('all')}
            className={`px-4 py-2 text-sm font-body uppercase tracking-wider transition-colors ${
              category === 'all' ? 'bg-foreground text-background' : 'bg-muted hover:bg-muted/80'
            }`}
            data-testid="filter-all"
          >
            {t.gallery.all}
          </button>
          {/* Dynamic category buttons */}
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.name)}
              className={`px-4 py-2 text-sm font-body uppercase tracking-wider transition-colors ${
                category === cat.name ? 'bg-foreground text-background' : 'bg-muted hover:bg-muted/80'
              }`}
              data-testid={`filter-${cat.id}`}
            >
              {lang === 'it' && cat.name_it ? cat.name_it : cat.name}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">{t.common.loading}</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {paintings.map(painting => (
              <PaintingCard key={painting.id} painting={painting} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PaintingDetailPage() {
  const { id } = useParams();
  const { lang, t } = useLanguage();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [painting, setPainting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    axios.get(`${API}/paintings/${id}`)
      .then(res => {
        setPainting(res.data);
        setSelectedImage(res.data.image_url);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="pt-32 text-center">{t.common.loading}</div>;
  if (!painting) return <div className="pt-32 text-center">Painting not found</div>;

  // Combine main image with additional images for gallery
  const allImages = [painting.image_url, ...(painting.additional_images || [])];

  return (
    <div className="pt-32 pb-24" data-testid="painting-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="md:sticky md:top-32"
          >
            {/* Main Image */}
            <div className="painting-frame relative">
              <img
                src={selectedImage || painting.image_url}
                alt={painting.title}
                className="w-full"
                data-testid="painting-image"
              />
              <WatermarkOverlay />
            </div>
            
            {/* Thumbnail Gallery */}
            {allImages.length > 1 && (
              <div className="flex gap-2 mt-4 flex-wrap">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(img)}
                    className={`relative w-20 h-20 border-2 rounded overflow-hidden transition-all ${
                      selectedImage === img ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                    <WatermarkOverlay />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="font-accent text-xs tracking-[0.3em] uppercase text-secondary">{painting.category}</span>
            <h1 className="font-heading text-3xl md:text-5xl tracking-tight mt-2 mb-4" data-testid="painting-title">
              {lang === 'it' ? painting.title_it : painting.title}
            </h1>
            
            {!painting.is_sold && (
              <p className="text-secondary font-heading text-3xl mb-6" data-testid="painting-price">
                €{painting.price.toLocaleString()}
              </p>
            )}

            <Separator className="my-6" />

            <p className="font-body text-muted-foreground leading-relaxed mb-6" data-testid="painting-description">
              {lang === 'it' ? painting.description_it : painting.description}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div>
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Dimensions</span>
                <p className="font-body">{painting.dimensions}</p>
              </div>
              <div>
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Year</span>
                <p className="font-body">{painting.year}</p>
              </div>
            </div>

            {painting.is_sold ? (
              <div className="bg-muted p-4 text-center">
                <span className="font-accent text-sm tracking-[0.2em] uppercase">{t.gallery.sold}</span>
              </div>
            ) : (
              <div className="space-y-3">
                {painting.payment_link ? (
                  <a
                    href={painting.payment_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <Button
                      size="lg"
                      className="w-full bg-[#635BFF] hover:bg-[#5349e0] text-white"
                      data-testid="buy-now-payment-link"
                    >
                      {lang === 'it' ? 'Acquista Ora (Klarna / Card)' : 'Buy Now (Klarna / Card)'}
                    </Button>
                  </a>
                ) : null}
                <Button
                  onClick={() => { addItem(painting); navigate('/checkout'); }}
                  size="lg"
                  className={`w-full ${painting.payment_link ? 'bg-muted-foreground/80 hover:bg-muted-foreground' : 'bg-primary hover:bg-primary/90'}`}
                  data-testid="add-to-cart-detail"
                >
                  {painting.payment_link ? (lang === 'it' ? 'Aggiungi al Carrello' : 'Add to Cart') : t.gallery.addToCart}
                </Button>
              </div>
            )}

            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Truck size={16} /> {painting.shipping_cost > 0 ? `Shipping: €${painting.shipping_cost}` : (lang === 'it' ? 'Spedizione gratuita' : 'Free shipping')}
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Shield size={16} /> Certificate of authenticity included
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <CreditCard size={16} /> Secure payment via Stripe
              </div>
            </div>

            {/* Share Buttons */}
            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3">{lang === 'it' ? 'Condividi questa opera' : 'Share this artwork'}</p>
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    const shareUrl = `${window.location.origin}/painting/${painting.id}`;
                    const text = `${painting.title} - ${lang === 'it' ? 'Opera di Elena Candalia' : 'Artwork by Elena Candalia'}`;
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(text)}`, '_blank');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1877f2] hover:bg-[#166fe5] text-white rounded-lg transition-colors"
                  title="Share on Facebook"
                >
                  <Facebook size={18} />
                  <span className="text-sm">Facebook</span>
                </button>
                <button 
                  onClick={() => {
                    const shareUrl = `${window.location.origin}/painting/${painting.id}`;
                    const text = `${painting.title} - ${lang === 'it' ? 'Opera di Elena Candalia' : 'Artwork by Elena Candalia'}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + shareUrl)}`, '_blank');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#25d366] hover:bg-[#20bd5a] text-white rounded-lg transition-colors"
                  title="Share on WhatsApp"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                  <span className="text-sm">WhatsApp</span>
                </button>
                <button 
                  onClick={() => {
                    const shareUrl = `${window.location.origin}/painting/${painting.id}`;
                    const text = `${painting.title} - ${lang === 'it' ? 'Opera di Elena Candalia' : 'Artwork by Elena Candalia'}`;
                    navigator.clipboard.writeText(`${text}\n${shareUrl}`);
                    toast.success(lang === 'it' ? 'Link copiato negli appunti!' : 'Link copied to clipboard!');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary/20 hover:bg-secondary/30 text-foreground rounded-lg transition-colors"
                  title={lang === 'it' ? 'Copia Link' : 'Copy Link'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                  <span className="text-sm">{lang === 'it' ? 'Copia Link' : 'Copy Link'}</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function AboutPage() {
  const { t, lang } = useLanguage();
  const watermark = useWatermark();
  const defaultImage = 'https://customer-assets.emergentagent.com/job_elena-art-gallery/artifacts/jzvkhg9l_FB_IMG_1766394923482.jpg';
  const [artistImage, setArtistImage] = useState(defaultImage);
  const [imageError, setImageError] = useState(false);

  // Resolve relative URLs (e.g. /api/uploads/...) to full URLs
  const resolveImageUrl = (url) => {
    if (!url || url.trim() === '') return '';
    if (url.startsWith('/api/') || url.startsWith('/uploads/')) {
      return `${BACKEND_URL}${url}`;
    }
    return url;
  };
  const [critics, setCritics] = useState([]);
  const [aboutTitle, setAboutTitle] = useState('');
  const [aboutContent, setAboutContent] = useState('');
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [videoType, setVideoType] = useState('youtube');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');

  useEffect(() => {
    axios.get(`${API}/about-settings`)
      .then(res => {
        // Only set artist image if it exists and is a valid URL
        if (res.data.artist_image_url && res.data.artist_image_url.trim() !== '') {
          const imageUrl = resolveImageUrl(res.data.artist_image_url);
          const img = new Image();
          img.onload = () => {
            setArtistImage(imageUrl);
            setImageError(false);
          };
          img.onerror = () => {
            console.log('Artist image failed to load, using default');
            setArtistImage(defaultImage);
            setImageError(true);
          };
          img.src = imageUrl;
        }
        // Set title based on language
        if (lang === 'it') {
          setAboutTitle(res.data.about_title_it || t.about.title);
          setAboutContent(res.data.about_content_it || '');
          setVideoTitle(res.data.video_title_it || 'Guardami dipingere');
        } else {
          setAboutTitle(res.data.about_title_en || t.about.title);
          setAboutContent(res.data.about_content_en || '');
          setVideoTitle(res.data.video_title_en || 'Watch me paint');
        }
        setVideoEnabled(res.data.video_enabled || false);
        setVideoType(res.data.video_type || 'youtube');
        setVideoUrl(res.data.video_url || '');
      })
      .catch(console.error);
    
    axios.get(`${API}/critics`)
      .then(res => setCritics(res.data))
      .catch(console.error);
  }, [lang, t.about.title]);

  const [exhibitions, setExhibitions] = useState([]);
  
  useEffect(() => {
    axios.get(`${API}/exhibitions`).then(res => setExhibitions(res.data)).catch(() => {});
  }, []);

  // Helper to extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return '';
    if (url.includes('youtu.be/')) {
      return url.split('youtu.be/')[1]?.split('?')[0] || '';
    }
    return url.split('v=')[1]?.split('&')[0] || '';
  };

  return (
    <div className="pt-32 pb-24" data-testid="about-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-4xl md:text-6xl tracking-tight mb-4">{aboutTitle || t.about.title}</h1>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 mb-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img
              src={artistImage}
              alt="Elena Candalia"
              className="painting-frame w-full"
              onError={(e) => {
                console.log('Image load error, falling back to default');
                e.target.src = defaultImage;
              }}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col justify-center"
          >
            {aboutContent ? (
              <div className="font-body text-lg leading-relaxed text-muted-foreground whitespace-pre-line">
                {aboutContent}
              </div>
            ) : (
              <>
                <p className="font-body text-lg leading-relaxed text-muted-foreground mb-6">
                  {t.about.bio}
                </p>
                <p className="font-body text-lg leading-relaxed text-muted-foreground mb-6">
                  Her palettes are extremely vivid and dynamic, characterized by strong and saturated tones that express a decidedly hyper-modern language. These colors not only capture the viewer&apos;s attention but also serve as a means to communicate deep and complex emotions. The speed and confidence with which the artist applies colors to the canvas add a sense of urgency and vitality to her works.
                </p>
                <p className="font-body text-lg leading-relaxed text-muted-foreground">
                  Based in Spoleto, the city of artists, Elena continues to surprise and fascinate the art world, leaving an indelible and significant mark on the contemporary artistic landscape. Her works are a tribute to the immortal beauty of 18th-century classical painting, enriched by a modern and contemporary language that makes them unique and unmistakable.
                </p>
              </>
            )}
          </motion.div>
        </div>

        {/* Artist Video Section */}
        {videoEnabled && videoUrl && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <h2 className="font-heading text-3xl tracking-tight mb-8 text-center">
              🎬 {videoTitle}
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
                {videoType === 'youtube' ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${getYouTubeId(videoUrl)}`}
                    className="w-full h-full"
                    allowFullScreen
                    title={videoTitle}
                  />
                ) : (
                  <video 
                    src={videoUrl} 
                    controls 
                    className="w-full h-full"
                    poster=""
                  />
                )}
                {/* Watermark Overlay on Video */}
                {watermark.enabled && watermark.text && (
                  <div 
                    className="absolute inset-0 pointer-events-none overflow-hidden z-10"
                    style={{ userSelect: 'none' }}
                  >
                    <div 
                      className="absolute whitespace-nowrap font-heading tracking-widest"
                      style={{
                        fontSize: '18px',
                        color: `rgba(255, 255, 255, ${watermark.opacity / 100})`,
                        textShadow: `0 0 10px rgba(0, 0, 0, ${watermark.opacity / 100})`,
                        transform: 'rotate(-35deg) scale(2)',
                        letterSpacing: '6px',
                        top: '50%',
                        left: '50%',
                        marginLeft: '-200%',
                        marginTop: '-50%',
                        width: '500%'
                      }}
                    >
                      {Array(10).fill(watermark.text).join('       ')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.section>
        )}

        {/* Exhibitions */}
        {exhibitions.length > 0 && (
        <section className="mb-24">
          <h2 className="font-heading text-3xl tracking-tight mb-8 text-center">
            {lang === 'it' ? 'Mostre e Presentazioni' : 'Exhibitions & Presentations'}
          </h2>
          <div className="max-w-2xl mx-auto">
            {exhibitions.map((ex, i) => (
              <motion.div
                key={ex.id || i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center justify-between py-4 border-b"
              >
                <div>
                  <span className="text-secondary font-body text-sm">{ex.date}</span>
                  <h3 className="font-heading text-lg">{lang === 'it' ? (ex.title_it || ex.title) : ex.title}</h3>
                </div>
                <span className="font-body text-muted-foreground text-sm">{ex.location}</span>
              </motion.div>
            ))}
          </div>
        </section>
        )}

        {/* Critics */}
        <section>
          <h2 className="font-heading text-3xl tracking-tight mb-8 text-center">Press & Critics</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {critics.map((critic, i) => (
              <motion.blockquote
                key={critic.id || i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-muted p-8"
              >
                <p className="font-heading italic text-lg mb-6">"{lang === 'it' ? critic.text_it || critic.text : critic.text}"</p>
                <footer>
                  <cite className="font-body not-italic">
                    <span className="font-medium">{critic.author}</span>
                    <br />
                    <span className="text-sm text-muted-foreground">{critic.publication}</span>
                  </cite>
                </footer>
              </motion.blockquote>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function ContactPage() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    email: '', phone: '', website: '', address: '', city: '', country: '',
    instagram: '', facebook: '', tiktok: ''
  });
  
  // Simple math CAPTCHA
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: '' });
  
  useEffect(() => {
    generateCaptcha();
    // Fetch contact info
    axios.get(`${API}/contact-settings`).then(res => {
      if (res.data) setContactInfo(res.data);
    }).catch(() => {});
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2, answer: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Verify GDPR consent
    if (!gdprConsent) {
      toast.error(lang === 'it' ? 'Devi accettare il trattamento dei dati personali' : 'You must accept the data processing terms');
      return;
    }
    
    // Verify CAPTCHA
    const correctAnswer = captcha.num1 + captcha.num2;
    if (parseInt(captcha.answer) !== correctAnswer) {
      toast.error(lang === 'it' ? 'Risposta CAPTCHA errata' : 'Incorrect CAPTCHA answer');
      generateCaptcha();
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, gdpr_consent: true });
      toast.success(lang === 'it' ? 'Messaggio inviato con successo!' : 'Message sent successfully!');
      setForm({ name: '', email: '', subject: '', message: '' });
      setGdprConsent(false);
      generateCaptcha();
    } catch {
      toast.error(lang === 'it' ? 'Invio fallito' : 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  // Build full address string
  const fullAddress = [contactInfo.address, contactInfo.city, contactInfo.country].filter(Boolean).join(', ') || 'Viale Martiri della Resistenza 33, Spoleto, Italy';

  return (
    <div className="pt-32 pb-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-4xl md:text-6xl tracking-tight mb-4">{t.contact.title}</h1>
          <p className="font-body text-muted-foreground max-w-xl mx-auto">
            Interested in commissioning a painting or purchasing a piece? Get in touch.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl mb-6">Studio Information</h2>
            <div className="space-y-4 font-body text-muted-foreground">
              <p className="flex items-center gap-3"><MapPin size={18} className="text-primary" /> {fullAddress}</p>
              <p className="flex items-center gap-3"><Phone size={18} className="text-primary" /> {contactInfo.phone || '+39 041 123 4567'}</p>
              <p className="flex items-center gap-3"><Mail size={18} className="text-primary" /> {contactInfo.email || 'studioart@elenacandalia.it'}</p>
              <p className="flex items-center gap-3"><Globe size={18} className="text-primary" /> <a href={`https://${(contactInfo.website || 'elenacandalia.it').replace(/^https?:\/\//, '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary">{contactInfo.website || 'elenacandalia.it'}</a></p>
            </div>
            <Separator className="my-8" />
            <h3 className="font-heading text-xl mb-4">Studio Hours</h3>
            <p className="font-body text-muted-foreground">
              By appointment only. Please contact us to schedule a visit to the studio.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="space-y-6"
            data-testid="contact-form"
          >
            <div>
              <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">{t.contact.name}</label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                required
                className="mt-2"
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">{t.contact.email}</label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                required
                className="mt-2"
                data-testid="contact-email"
              />
            </div>
            <div>
              <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">{t.contact.subject}</label>
              <Input
                value={form.subject}
                onChange={e => setForm({ ...form, subject: e.target.value })}
                required
                className="mt-2"
                data-testid="contact-subject"
              />
            </div>
            <div>
              <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">{t.contact.message}</label>
              <Textarea
                value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })}
                required
                rows={5}
                className="mt-2"
                data-testid="contact-message"
              />
            </div>
            <div>
              <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">
                {lang === 'it' ? 'Verifica di sicurezza' : 'Security Check'}
              </label>
              <div className="mt-2 flex items-center gap-3">
                <div className="bg-muted px-4 py-2 font-heading text-lg">
                  {captcha.num1} + {captcha.num2} = ?
                </div>
                <Input
                  type="number"
                  value={captcha.answer}
                  onChange={e => setCaptcha({ ...captcha, answer: e.target.value })}
                  required
                  placeholder={lang === 'it' ? 'Risposta' : 'Answer'}
                  className="w-24"
                  data-testid="contact-captcha"
                />
                <button 
                  type="button" 
                  onClick={generateCaptcha}
                  className="text-muted-foreground hover:text-foreground text-sm"
                  title={lang === 'it' ? 'Nuova domanda' : 'New question'}
                >
                  ↻
                </button>
              </div>
            </div>
            
            {/* GDPR Consent Checkbox */}
            <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg border">
              <input
                type="checkbox"
                id="gdpr-consent"
                checked={gdprConsent}
                onChange={(e) => setGdprConsent(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-2 border-primary"
                required
              />
              <label htmlFor="gdpr-consent" className="text-sm text-muted-foreground cursor-pointer">
                {lang === 'it' 
                  ? <>Acconsento al trattamento dei miei dati personali per rispondere al mio messaggio, in conformità con la <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>. *</>
                  : <>I consent to the processing of my personal data to respond to my message, in accordance with the <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>. *</>
                }
              </label>
            </div>
            
            <Button type="submit" disabled={loading || !gdprConsent} className="w-full bg-primary hover:bg-primary/90" data-testid="contact-submit">
              {loading ? t.common.loading : t.contact.send}
            </Button>
          </motion.form>
        </div>
      </div>
    </div>
  );
}

// ============ POLICY PAGES ============

function PrivacyPage() {
  const { lang } = useLanguage();
  const [content, setContent] = useState('');

  useEffect(() => {
    axios.get(`${API}/policies`)
      .then(res => {
        setContent(lang === 'it' ? res.data.privacy_it : res.data.privacy_en);
      })
      .catch(console.error);
  }, [lang]);

  // Default GDPR-compliant privacy policy sections
  const defaultSections = lang === 'it' ? [
    { title: '1. Titolare del Trattamento', content: 'Elena Candalia, con sede in Italia, è il Titolare del trattamento dei dati personali raccolti attraverso questo sito web.' },
    { title: '2. Dati Raccolti', content: 'Raccogliamo i seguenti dati personali:\n• Dati di contatto (nome, email) tramite il modulo di contatto\n• Dati di pagamento elaborati da Stripe (non memorizziamo dati delle carte)\n• Indirizzi di spedizione per gli ordini\n• Indirizzi email per la newsletter (con consenso)' },
    { title: '3. Finalità del Trattamento', content: '• Rispondere alle richieste inviate tramite il modulo di contatto\n• Elaborare ordini e pagamenti\n• Inviare newsletter (solo con consenso esplicito)\n• Migliorare i nostri servizi' },
    { title: '4. Base Giuridica', content: '• Consenso: per newsletter e cookie non essenziali\n• Esecuzione contrattuale: per elaborazione ordini\n• Interesse legittimo: per comunicazioni relative a ordini esistenti' },
    { title: '5. Servizi di Terze Parti', content: '• Stripe: elaborazione pagamenti sicura (https://stripe.com/privacy)\n• PayPal: pagamenti alternativi (https://www.paypal.com/privacy)\n• YouTube: video incorporati (solo con consenso)' },
    { title: '6. Diritti degli Interessati', content: 'Hai diritto di:\n• Accedere ai tuoi dati personali\n• Richiedere la rettifica o cancellazione\n• Opporti al trattamento\n• Richiedere la portabilità dei dati\n• Revocare il consenso in qualsiasi momento\n\nPer esercitare i tuoi diritti, contattaci tramite il modulo di contatto.' },
    { title: '7. Conservazione dei Dati', content: 'I dati sono conservati per il tempo necessario alle finalità per cui sono stati raccolti, e comunque non oltre i termini di legge.' },
    { title: '8. Sicurezza', content: 'Adottiamo misure tecniche e organizzative appropriate per proteggere i tuoi dati personali.' }
  ] : [
    { title: '1. Data Controller', content: 'Elena Candalia, based in Italy, is the Data Controller for personal data collected through this website.' },
    { title: '2. Data Collected', content: 'We collect the following personal data:\n• Contact information (name, email) via contact form\n• Payment data processed by Stripe (we do not store card details)\n• Shipping addresses for orders\n• Email addresses for newsletter (with consent)' },
    { title: '3. Purposes of Processing', content: '• Responding to inquiries submitted via contact form\n• Processing orders and payments\n• Sending newsletters (only with explicit consent)\n• Improving our services' },
    { title: '4. Legal Basis', content: '• Consent: for newsletter and non-essential cookies\n• Contract performance: for order processing\n• Legitimate interest: for communications related to existing orders' },
    { title: '5. Third-Party Services', content: '• Stripe: secure payment processing (https://stripe.com/privacy)\n• PayPal: alternative payments (https://www.paypal.com/privacy)\n• YouTube: embedded videos (only with consent)' },
    { title: '6. Data Subject Rights', content: 'You have the right to:\n• Access your personal data\n• Request rectification or erasure\n• Object to processing\n• Request data portability\n• Withdraw consent at any time\n\nTo exercise your rights, contact us via the contact form.' },
    { title: '7. Data Retention', content: 'Data is retained for the time necessary for the purposes for which it was collected, and in any case no longer than required by law.' },
    { title: '8. Security', content: 'We adopt appropriate technical and organizational measures to protect your personal data.' }
  ];

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-4">
          {lang === 'it' ? 'Informativa sulla Privacy' : 'Privacy Policy'}
        </h1>
        <p className="text-muted-foreground mb-8">
          {lang === 'it' ? 'Ultimo aggiornamento: Dicembre 2025' : 'Last updated: December 2025'}
        </p>
        
        {content ? (
          <div className="prose prose-lg max-w-none font-body text-muted-foreground whitespace-pre-line">
            {content}
          </div>
        ) : (
          <div className="space-y-8">
            {defaultSections.map((section, i) => (
              <section key={i}>
                <h2 className="font-heading text-xl mb-3">{section.title}</h2>
                <p className="font-body text-muted-foreground whitespace-pre-line">{section.content}</p>
              </section>
            ))}
          </div>
        )}
        
        <div className="mt-8 pt-8 border-t">
          <a href="/cookie-policy" className="text-primary hover:underline">
            {lang === 'it' ? '→ Politica sui Cookie' : '→ Cookie Policy'}
          </a>
        </div>
      </div>
    </div>
  );
}

function TermsPage() {
  const { lang } = useLanguage();
  const [content, setContent] = useState('');

  useEffect(() => {
    axios.get(`${API}/policies`)
      .then(res => {
        setContent(lang === 'it' ? res.data.terms_it : res.data.terms_en);
      })
      .catch(console.error);
  }, [lang]);

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-8">
          {lang === 'it' ? 'Termini e Condizioni' : 'Terms & Conditions'}
        </h1>
        <div className="prose prose-lg max-w-none font-body text-muted-foreground whitespace-pre-line">
          {content || (lang === 'it' ? 'Contenuto in arrivo...' : 'Content coming soon...')}
        </div>
      </div>
    </div>
  );
}

function RefundPage() {
  const { lang } = useLanguage();
  const [content, setContent] = useState('');

  useEffect(() => {
    axios.get(`${API}/policies`)
      .then(res => {
        setContent(lang === 'it' ? res.data.refund_it : res.data.refund_en);
      })
      .catch(console.error);
  }, [lang]);

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-8">
          {lang === 'it' ? 'Politica di Rimborso' : 'Refund Policy'}
        </h1>
        <div className="prose prose-lg max-w-none font-body text-muted-foreground whitespace-pre-line">
          {content || (lang === 'it' ? 'Contenuto in arrivo...' : 'Content coming soon...')}
        </div>
      </div>
    </div>
  );
}

function CookiePolicyPage() {
  const { lang } = useLanguage();
  const [customContent, setCustomContent] = useState('');

  useEffect(() => {
    axios.get(`${API}/policies`)
      .then(res => {
        const content = lang === 'it' ? res.data.cookie_it : res.data.cookie_en;
        setCustomContent(content || '');
      })
      .catch(() => {});
  }, [lang]);

  const cookieData = [
    {
      name: 'gdpr_consent',
      provider: 'elenacandalia.it',
      purpose: lang === 'it' ? 'Memorizza le preferenze di consenso cookie' : 'Stores cookie consent preferences',
      duration: lang === 'it' ? '1 anno' : '1 year',
      type: lang === 'it' ? 'Necessario' : 'Necessary'
    },
    {
      name: 'cart_session',
      provider: 'elenacandalia.it',
      purpose: lang === 'it' ? 'Gestisce il carrello della spesa' : 'Manages shopping cart',
      duration: lang === 'it' ? 'Sessione' : 'Session',
      type: lang === 'it' ? 'Necessario' : 'Necessary'
    },
    {
      name: 'auth_token',
      provider: 'elenacandalia.it',
      purpose: lang === 'it' ? 'Autenticazione amministratore' : 'Admin authentication',
      duration: lang === 'it' ? '24 ore' : '24 hours',
      type: lang === 'it' ? 'Necessario' : 'Necessary'
    },
    {
      name: 'language',
      provider: 'elenacandalia.it',
      purpose: lang === 'it' ? 'Memorizza la preferenza lingua' : 'Stores language preference',
      duration: lang === 'it' ? '1 anno' : '1 year',
      type: lang === 'it' ? 'Necessario' : 'Necessary'
    },
    {
      name: '_stripe_*',
      provider: 'Stripe',
      purpose: lang === 'it' ? 'Elaborazione pagamenti sicuri' : 'Secure payment processing',
      duration: lang === 'it' ? 'Sessione/2 anni' : 'Session/2 years',
      type: lang === 'it' ? 'Necessario (durante checkout)' : 'Necessary (during checkout)'
    },
    {
      name: 'YSC, VISITOR_INFO1_LIVE',
      provider: 'YouTube',
      purpose: lang === 'it' ? 'Tracciamento video YouTube incorporati' : 'Tracking for embedded YouTube videos',
      duration: lang === 'it' ? 'Sessione/180 giorni' : 'Session/180 days',
      type: lang === 'it' ? 'Contenuti Esterni' : 'External Content'
    },
    {
      name: '_ga, _gid',
      provider: 'Google Analytics',
      purpose: lang === 'it' ? 'Analisi del traffico del sito' : 'Site traffic analysis',
      duration: lang === 'it' ? '2 anni/24 ore' : '2 years/24 hours',
      type: lang === 'it' ? 'Analitici' : 'Analytics'
    }
  ];

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-4">
          🍪 {lang === 'it' ? 'Politica sui Cookie' : 'Cookie Policy'}
        </h1>
        <p className="text-muted-foreground mb-8">
          {lang === 'it' 
            ? 'Ultimo aggiornamento: Dicembre 2025'
            : 'Last updated: December 2025'}
        </p>

        <div className="prose prose-lg max-w-none font-body space-y-8">
          {/* Custom Content from Admin (if available) */}
          {customContent && (
            <section className="mb-8 p-6 bg-muted/30 rounded-lg border">
              <div className="whitespace-pre-line text-muted-foreground">
                {customContent}
              </div>
            </section>
          )}

          {/* Introduction */}
          <section>
            <h2 className="font-heading text-2xl mb-4">
              {lang === 'it' ? '1. Cosa sono i Cookie' : '1. What are Cookies'}
            </h2>
            <p className="text-muted-foreground">
              {lang === 'it' 
                ? 'I cookie sono piccoli file di testo che vengono memorizzati sul tuo dispositivo quando visiti un sito web. Vengono utilizzati per ricordare le tue preferenze, migliorare la tua esperienza di navigazione e raccogliere informazioni analitiche.'
                : 'Cookies are small text files stored on your device when you visit a website. They are used to remember your preferences, improve your browsing experience, and collect analytical information.'}
            </p>
          </section>

          {/* Cookie Categories */}
          <section>
            <h2 className="font-heading text-2xl mb-4">
              {lang === 'it' ? '2. Tipi di Cookie che Utilizziamo' : '2. Types of Cookies We Use'}
            </h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200">
                <h3 className="font-heading text-lg text-green-800 dark:text-green-200">
                  🔒 {lang === 'it' ? 'Cookie Necessari' : 'Necessary Cookies'}
                </h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {lang === 'it' 
                    ? 'Essenziali per il funzionamento del sito. Non possono essere disattivati. Includono cookie per il carrello, autenticazione e preferenze di consenso.'
                    : 'Essential for the site to function. Cannot be disabled. Include cookies for cart, authentication, and consent preferences.'}
                </p>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200">
                <h3 className="font-heading text-lg text-blue-800 dark:text-blue-200">
                  📊 {lang === 'it' ? 'Cookie Analitici' : 'Analytics Cookies'}
                </h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {lang === 'it' 
                    ? 'Ci aiutano a capire come i visitatori interagiscono con il sito, raccogliendo informazioni in forma anonima.'
                    : 'Help us understand how visitors interact with the site by collecting information anonymously.'}
                </p>
              </div>

              <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200">
                <h3 className="font-heading text-lg text-purple-800 dark:text-purple-200">
                  📢 {lang === 'it' ? 'Cookie di Marketing' : 'Marketing Cookies'}
                </h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {lang === 'it' 
                    ? 'Utilizzati per tracciare i visitatori sui siti web e mostrare annunci pertinenti.'
                    : 'Used to track visitors across websites and display relevant advertisements.'}
                </p>
              </div>

              <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200">
                <h3 className="font-heading text-lg text-orange-800 dark:text-orange-200">
                  🌐 {lang === 'it' ? 'Contenuti Esterni' : 'External Content'}
                </h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {lang === 'it' 
                    ? 'Cookie impostati da servizi di terze parti come YouTube per i video incorporati.'
                    : 'Cookies set by third-party services like YouTube for embedded videos.'}
                </p>
              </div>
            </div>
          </section>

          {/* Cookie Table */}
          <section>
            <h2 className="font-heading text-2xl mb-4">
              {lang === 'it' ? '3. Elenco Dettagliato dei Cookie' : '3. Detailed Cookie List'}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-muted">
                    <th className="p-3 text-left border">{lang === 'it' ? 'Nome' : 'Name'}</th>
                    <th className="p-3 text-left border">{lang === 'it' ? 'Fornitore' : 'Provider'}</th>
                    <th className="p-3 text-left border">{lang === 'it' ? 'Scopo' : 'Purpose'}</th>
                    <th className="p-3 text-left border">{lang === 'it' ? 'Durata' : 'Duration'}</th>
                    <th className="p-3 text-left border">{lang === 'it' ? 'Tipo' : 'Type'}</th>
                  </tr>
                </thead>
                <tbody>
                  {cookieData.map((cookie, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
                      <td className="p-3 border font-mono text-xs">{cookie.name}</td>
                      <td className="p-3 border">{cookie.provider}</td>
                      <td className="p-3 border">{cookie.purpose}</td>
                      <td className="p-3 border">{cookie.duration}</td>
                      <td className="p-3 border">{cookie.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Managing Cookies */}
          <section>
            <h2 className="font-heading text-2xl mb-4">
              {lang === 'it' ? '4. Come Gestire i Cookie' : '4. How to Manage Cookies'}
            </h2>
            <p className="text-muted-foreground mb-4">
              {lang === 'it' 
                ? 'Puoi gestire le tue preferenze sui cookie in qualsiasi momento:'
                : 'You can manage your cookie preferences at any time:'}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>
                {lang === 'it' 
                  ? 'Clicca sul pulsante "🍪 Gestisci Cookie" nel footer del sito'
                  : 'Click the "🍪 Manage Cookies" button in the site footer'}
              </li>
              <li>
                {lang === 'it' 
                  ? 'Usa le impostazioni del tuo browser per bloccare o eliminare i cookie'
                  : 'Use your browser settings to block or delete cookies'}
              </li>
              <li>
                {lang === 'it' 
                  ? 'Revoca il consenso in qualsiasi momento dalle impostazioni cookie'
                  : 'Revoke consent at any time from cookie settings'}
              </li>
            </ul>
          </section>

          {/* Third Parties */}
          <section>
            <h2 className="font-heading text-2xl mb-4">
              {lang === 'it' ? '5. Servizi di Terze Parti' : '5. Third-Party Services'}
            </h2>
            <p className="text-muted-foreground mb-4">
              {lang === 'it' 
                ? 'Utilizziamo i seguenti servizi di terze parti che potrebbero impostare cookie:'
                : 'We use the following third-party services that may set cookies:'}
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Stripe</strong> - {lang === 'it' ? 'Elaborazione pagamenti' : 'Payment processing'}</li>
              <li><strong>YouTube</strong> - {lang === 'it' ? 'Video incorporati' : 'Embedded videos'}</li>
              <li><strong>PayPal</strong> - {lang === 'it' ? 'Pagamenti alternativi' : 'Alternative payments'}</li>
            </ul>
          </section>

          {/* Contact */}
          <section>
            <h2 className="font-heading text-2xl mb-4">
              {lang === 'it' ? '6. Contatti' : '6. Contact Us'}
            </h2>
            <p className="text-muted-foreground">
              {lang === 'it' 
                ? 'Per domande sulla nostra politica sui cookie, contattaci all\'indirizzo email indicato nella pagina Contatti.'
                : 'For questions about our cookie policy, contact us at the email address listed on our Contact page.'}
            </p>
          </section>
        </div>

        <div className="mt-8 pt-8 border-t">
          <a href="/privacy" className="text-primary hover:underline mr-4">
            {lang === 'it' ? '← Privacy Policy' : '← Privacy Policy'}
          </a>
          <a href="/terms" className="text-primary hover:underline">
            {lang === 'it' ? 'Termini e Condizioni →' : 'Terms & Conditions →'}
          </a>
        </div>
      </div>
    </div>
  );
}

// Full country list with codes
const ALL_COUNTRIES = [
  { code: 'AF', en: 'Afghanistan', it: 'Afghanistan' }, { code: 'AL', en: 'Albania', it: 'Albania' },
  { code: 'DZ', en: 'Algeria', it: 'Algeria' }, { code: 'AD', en: 'Andorra', it: 'Andorra' },
  { code: 'AO', en: 'Angola', it: 'Angola' }, { code: 'AG', en: 'Antigua and Barbuda', it: 'Antigua e Barbuda' },
  { code: 'AR', en: 'Argentina', it: 'Argentina' }, { code: 'AM', en: 'Armenia', it: 'Armenia' },
  { code: 'AU', en: 'Australia', it: 'Australia' }, { code: 'AT', en: 'Austria', it: 'Austria' },
  { code: 'AZ', en: 'Azerbaijan', it: 'Azerbaigian' }, { code: 'BS', en: 'Bahamas', it: 'Bahamas' },
  { code: 'BH', en: 'Bahrain', it: 'Bahrein' }, { code: 'BD', en: 'Bangladesh', it: 'Bangladesh' },
  { code: 'BB', en: 'Barbados', it: 'Barbados' }, { code: 'BY', en: 'Belarus', it: 'Bielorussia' },
  { code: 'BE', en: 'Belgium', it: 'Belgio' }, { code: 'BZ', en: 'Belize', it: 'Belize' },
  { code: 'BJ', en: 'Benin', it: 'Benin' }, { code: 'BT', en: 'Bhutan', it: 'Bhutan' },
  { code: 'BO', en: 'Bolivia', it: 'Bolivia' }, { code: 'BA', en: 'Bosnia and Herzegovina', it: 'Bosnia ed Erzegovina' },
  { code: 'BW', en: 'Botswana', it: 'Botswana' }, { code: 'BR', en: 'Brazil', it: 'Brasile' },
  { code: 'BN', en: 'Brunei', it: 'Brunei' }, { code: 'BG', en: 'Bulgaria', it: 'Bulgaria' },
  { code: 'BF', en: 'Burkina Faso', it: 'Burkina Faso' }, { code: 'BI', en: 'Burundi', it: 'Burundi' },
  { code: 'KH', en: 'Cambodia', it: 'Cambogia' }, { code: 'CM', en: 'Cameroon', it: 'Camerun' },
  { code: 'CA', en: 'Canada', it: 'Canada' }, { code: 'CV', en: 'Cape Verde', it: 'Capo Verde' },
  { code: 'CF', en: 'Central African Republic', it: 'Repubblica Centrafricana' }, { code: 'TD', en: 'Chad', it: 'Ciad' },
  { code: 'CL', en: 'Chile', it: 'Cile' }, { code: 'CN', en: 'China', it: 'Cina' },
  { code: 'CO', en: 'Colombia', it: 'Colombia' }, { code: 'KM', en: 'Comoros', it: 'Comore' },
  { code: 'CG', en: 'Congo', it: 'Congo' }, { code: 'CR', en: 'Costa Rica', it: 'Costa Rica' },
  { code: 'HR', en: 'Croatia', it: 'Croazia' }, { code: 'CU', en: 'Cuba', it: 'Cuba' },
  { code: 'CY', en: 'Cyprus', it: 'Cipro' }, { code: 'CZ', en: 'Czech Republic', it: 'Repubblica Ceca' },
  { code: 'DK', en: 'Denmark', it: 'Danimarca' }, { code: 'DJ', en: 'Djibouti', it: 'Gibuti' },
  { code: 'DM', en: 'Dominica', it: 'Dominica' }, { code: 'DO', en: 'Dominican Republic', it: 'Repubblica Dominicana' },
  { code: 'EC', en: 'Ecuador', it: 'Ecuador' }, { code: 'EG', en: 'Egypt', it: 'Egitto' },
  { code: 'SV', en: 'El Salvador', it: 'El Salvador' }, { code: 'GQ', en: 'Equatorial Guinea', it: 'Guinea Equatoriale' },
  { code: 'ER', en: 'Eritrea', it: 'Eritrea' }, { code: 'EE', en: 'Estonia', it: 'Estonia' },
  { code: 'SZ', en: 'Eswatini', it: 'Eswatini' }, { code: 'ET', en: 'Ethiopia', it: 'Etiopia' },
  { code: 'FJ', en: 'Fiji', it: 'Figi' }, { code: 'FI', en: 'Finland', it: 'Finlandia' },
  { code: 'FR', en: 'France', it: 'Francia' }, { code: 'GA', en: 'Gabon', it: 'Gabon' },
  { code: 'GM', en: 'Gambia', it: 'Gambia' }, { code: 'GE', en: 'Georgia', it: 'Georgia' },
  { code: 'DE', en: 'Germany', it: 'Germania' }, { code: 'GH', en: 'Ghana', it: 'Ghana' },
  { code: 'GR', en: 'Greece', it: 'Grecia' }, { code: 'GD', en: 'Grenada', it: 'Grenada' },
  { code: 'GT', en: 'Guatemala', it: 'Guatemala' }, { code: 'GN', en: 'Guinea', it: 'Guinea' },
  { code: 'GW', en: 'Guinea-Bissau', it: 'Guinea-Bissau' }, { code: 'GY', en: 'Guyana', it: 'Guyana' },
  { code: 'HT', en: 'Haiti', it: 'Haiti' }, { code: 'HN', en: 'Honduras', it: 'Honduras' },
  { code: 'HU', en: 'Hungary', it: 'Ungheria' }, { code: 'IS', en: 'Iceland', it: 'Islanda' },
  { code: 'IN', en: 'India', it: 'India' }, { code: 'ID', en: 'Indonesia', it: 'Indonesia' },
  { code: 'IR', en: 'Iran', it: 'Iran' }, { code: 'IQ', en: 'Iraq', it: 'Iraq' },
  { code: 'IE', en: 'Ireland', it: 'Irlanda' }, { code: 'IL', en: 'Israel', it: 'Israele' },
  { code: 'IT', en: 'Italy', it: 'Italia' }, { code: 'CI', en: 'Ivory Coast', it: "Costa d'Avorio" },
  { code: 'JM', en: 'Jamaica', it: 'Giamaica' }, { code: 'JP', en: 'Japan', it: 'Giappone' },
  { code: 'JO', en: 'Jordan', it: 'Giordania' }, { code: 'KZ', en: 'Kazakhstan', it: 'Kazakistan' },
  { code: 'KE', en: 'Kenya', it: 'Kenya' }, { code: 'KI', en: 'Kiribati', it: 'Kiribati' },
  { code: 'KW', en: 'Kuwait', it: 'Kuwait' }, { code: 'KG', en: 'Kyrgyzstan', it: 'Kirghizistan' },
  { code: 'LA', en: 'Laos', it: 'Laos' }, { code: 'LV', en: 'Latvia', it: 'Lettonia' },
  { code: 'LB', en: 'Lebanon', it: 'Libano' }, { code: 'LS', en: 'Lesotho', it: 'Lesotho' },
  { code: 'LR', en: 'Liberia', it: 'Liberia' }, { code: 'LY', en: 'Libya', it: 'Libia' },
  { code: 'LI', en: 'Liechtenstein', it: 'Liechtenstein' }, { code: 'LT', en: 'Lithuania', it: 'Lituania' },
  { code: 'LU', en: 'Luxembourg', it: 'Lussemburgo' }, { code: 'MG', en: 'Madagascar', it: 'Madagascar' },
  { code: 'MW', en: 'Malawi', it: 'Malawi' }, { code: 'MY', en: 'Malaysia', it: 'Malesia' },
  { code: 'MV', en: 'Maldives', it: 'Maldive' }, { code: 'ML', en: 'Mali', it: 'Mali' },
  { code: 'MT', en: 'Malta', it: 'Malta' }, { code: 'MH', en: 'Marshall Islands', it: 'Isole Marshall' },
  { code: 'MR', en: 'Mauritania', it: 'Mauritania' }, { code: 'MU', en: 'Mauritius', it: 'Mauritius' },
  { code: 'MX', en: 'Mexico', it: 'Messico' }, { code: 'FM', en: 'Micronesia', it: 'Micronesia' },
  { code: 'MD', en: 'Moldova', it: 'Moldavia' }, { code: 'MC', en: 'Monaco', it: 'Monaco' },
  { code: 'MN', en: 'Mongolia', it: 'Mongolia' }, { code: 'ME', en: 'Montenegro', it: 'Montenegro' },
  { code: 'MA', en: 'Morocco', it: 'Marocco' }, { code: 'MZ', en: 'Mozambique', it: 'Mozambico' },
  { code: 'MM', en: 'Myanmar', it: 'Myanmar' }, { code: 'NA', en: 'Namibia', it: 'Namibia' },
  { code: 'NR', en: 'Nauru', it: 'Nauru' }, { code: 'NP', en: 'Nepal', it: 'Nepal' },
  { code: 'NL', en: 'Netherlands', it: 'Paesi Bassi' }, { code: 'NZ', en: 'New Zealand', it: 'Nuova Zelanda' },
  { code: 'NI', en: 'Nicaragua', it: 'Nicaragua' }, { code: 'NE', en: 'Niger', it: 'Niger' },
  { code: 'NG', en: 'Nigeria', it: 'Nigeria' }, { code: 'KP', en: 'North Korea', it: 'Corea del Nord' },
  { code: 'MK', en: 'North Macedonia', it: 'Macedonia del Nord' }, { code: 'NO', en: 'Norway', it: 'Norvegia' },
  { code: 'OM', en: 'Oman', it: 'Oman' }, { code: 'PK', en: 'Pakistan', it: 'Pakistan' },
  { code: 'PW', en: 'Palau', it: 'Palau' }, { code: 'PS', en: 'Palestine', it: 'Palestina' },
  { code: 'PA', en: 'Panama', it: 'Panama' }, { code: 'PG', en: 'Papua New Guinea', it: 'Papua Nuova Guinea' },
  { code: 'PY', en: 'Paraguay', it: 'Paraguay' }, { code: 'PE', en: 'Peru', it: 'Perù' },
  { code: 'PH', en: 'Philippines', it: 'Filippine' }, { code: 'PL', en: 'Poland', it: 'Polonia' },
  { code: 'PT', en: 'Portugal', it: 'Portogallo' }, { code: 'QA', en: 'Qatar', it: 'Qatar' },
  { code: 'RO', en: 'Romania', it: 'Romania' }, { code: 'RU', en: 'Russia', it: 'Russia' },
  { code: 'RW', en: 'Rwanda', it: 'Ruanda' }, { code: 'KN', en: 'Saint Kitts and Nevis', it: 'Saint Kitts e Nevis' },
  { code: 'LC', en: 'Saint Lucia', it: 'Santa Lucia' }, { code: 'VC', en: 'Saint Vincent', it: 'Saint Vincent' },
  { code: 'WS', en: 'Samoa', it: 'Samoa' }, { code: 'SM', en: 'San Marino', it: 'San Marino' },
  { code: 'ST', en: 'Sao Tome and Principe', it: 'São Tomé e Príncipe' }, { code: 'SA', en: 'Saudi Arabia', it: 'Arabia Saudita' },
  { code: 'SN', en: 'Senegal', it: 'Senegal' }, { code: 'RS', en: 'Serbia', it: 'Serbia' },
  { code: 'SC', en: 'Seychelles', it: 'Seychelles' }, { code: 'SL', en: 'Sierra Leone', it: 'Sierra Leone' },
  { code: 'SG', en: 'Singapore', it: 'Singapore' }, { code: 'SK', en: 'Slovakia', it: 'Slovacchia' },
  { code: 'SI', en: 'Slovenia', it: 'Slovenia' }, { code: 'SB', en: 'Solomon Islands', it: 'Isole Salomone' },
  { code: 'SO', en: 'Somalia', it: 'Somalia' }, { code: 'ZA', en: 'South Africa', it: 'Sudafrica' },
  { code: 'KR', en: 'South Korea', it: 'Corea del Sud' }, { code: 'SS', en: 'South Sudan', it: 'Sudan del Sud' },
  { code: 'ES', en: 'Spain', it: 'Spagna' }, { code: 'LK', en: 'Sri Lanka', it: 'Sri Lanka' },
  { code: 'SD', en: 'Sudan', it: 'Sudan' }, { code: 'SR', en: 'Suriname', it: 'Suriname' },
  { code: 'SE', en: 'Sweden', it: 'Svezia' }, { code: 'CH', en: 'Switzerland', it: 'Svizzera' },
  { code: 'SY', en: 'Syria', it: 'Siria' }, { code: 'TW', en: 'Taiwan', it: 'Taiwan' },
  { code: 'TJ', en: 'Tajikistan', it: 'Tagikistan' }, { code: 'TZ', en: 'Tanzania', it: 'Tanzania' },
  { code: 'TH', en: 'Thailand', it: 'Thailandia' }, { code: 'TL', en: 'Timor-Leste', it: 'Timor Est' },
  { code: 'TG', en: 'Togo', it: 'Togo' }, { code: 'TO', en: 'Tonga', it: 'Tonga' },
  { code: 'TT', en: 'Trinidad and Tobago', it: 'Trinidad e Tobago' }, { code: 'TN', en: 'Tunisia', it: 'Tunisia' },
  { code: 'TR', en: 'Turkey', it: 'Turchia' }, { code: 'TM', en: 'Turkmenistan', it: 'Turkmenistan' },
  { code: 'TV', en: 'Tuvalu', it: 'Tuvalu' }, { code: 'UG', en: 'Uganda', it: 'Uganda' },
  { code: 'UA', en: 'Ukraine', it: 'Ucraina' }, { code: 'AE', en: 'United Arab Emirates', it: 'Emirati Arabi Uniti' },
  { code: 'GB', en: 'United Kingdom', it: 'Regno Unito' }, { code: 'US', en: 'United States', it: 'Stati Uniti' },
  { code: 'UY', en: 'Uruguay', it: 'Uruguay' }, { code: 'UZ', en: 'Uzbekistan', it: 'Uzbekistan' },
  { code: 'VU', en: 'Vanuatu', it: 'Vanuatu' }, { code: 'VA', en: 'Vatican City', it: 'Città del Vaticano' },
  { code: 'VE', en: 'Venezuela', it: 'Venezuela' }, { code: 'VN', en: 'Vietnam', it: 'Vietnam' },
  { code: 'YE', en: 'Yemen', it: 'Yemen' }, { code: 'ZM', en: 'Zambia', it: 'Zambia' },
  { code: 'ZW', en: 'Zimbabwe', it: 'Zimbabwe' }
];

const EU_COUNTRY_CODES = new Set([
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE',
  'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE'
]);

function getShippingRegion(countryCode) {
  if (countryCode === 'IT') return 'italy';
  if (EU_COUNTRY_CODES.has(countryCode)) return 'europe';
  return 'worldwide';
}

function CheckoutPage() {
  const { t, lang } = useLanguage();
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ 
    email: '', 
    name: '', 
    phone: '',
    address: '', 
    city: '',
    postal_code: '',
    country: 'IT'
  });
  const [loading, setLoading] = useState(false);
  const [shippingRates, setShippingRates] = useState({ italy: 50, europe: 100, worldwide: 200, custom_tax_percent: 0, customs_duty_percent: 0 });
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [paypalMeLink, setPaypalMeLink] = useState('');
  const [revolutEnabled, setRevolutEnabled] = useState(false);
  const [revolutLink, setRevolutLink] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/shipping-rates`)
      .then(res => setShippingRates(res.data))
      .catch(console.error);
    axios.get(`${API}/paypal-enabled`)
      .then(res => {
        setPaypalEnabled(res.data.enabled);
        setPaypalMeLink(res.data.paypal_me_link || '');
      })
      .catch(console.error);
    axios.get(`${API}/revolut-enabled`)
      .then(res => {
        setRevolutEnabled(res.data.enabled);
        setRevolutLink(res.data.revolut_link || '');
      })
      .catch(console.error);
  }, []);

  // Derive region from selected country
  const shippingRegion = getShippingRegion(form.country);

  // Check if ALL items have free shipping (shipping_cost === 0)
  const allFreeShipping = items.every(item => item.shipping_cost === 0 || item.shipping_cost === undefined);
  
  const baseShippingCost = shippingRegion === 'italy' ? shippingRates.italy : 
                           shippingRegion === 'europe' ? shippingRates.europe : 
                           shippingRates.worldwide;
  const shippingCost = allFreeShipping ? 0 : baseShippingCost;
  const taxAmount = total * (shippingRates.custom_tax_percent / 100);
  const customsDutyAmount = shippingRegion === 'worldwide' ? total * (shippingRates.customs_duty_percent / 100) : 0;
  const grandTotal = total + shippingCost + taxAmount + customsDutyAmount;

  const validateForm = () => {
    if (!form.email || !form.name || !form.phone || !form.address || !form.city || !form.postal_code) {
      toast.error(lang === 'it' ? 'Compila tutti i campi obbligatori' : 'Please fill all required fields');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error(lang === 'it' ? 'Email non valida' : 'Invalid email address');
      return false;
    }
    return true;
  };

  const getFullAddress = () => {
    const countryName = ALL_COUNTRIES.find(c => c.code === form.country)?.[lang] || form.country;
    return `${form.address}, ${form.postal_code} ${form.city}, ${countryName}`;
  };

  const handleStripeCheckout = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/stripe`, {
        items: items.map(i => ({ painting_id: i.id, quantity: i.quantity })),
        customer_email: form.email,
        customer_name: form.name,
        customer_phone: form.phone,
        shipping_address: getFullAddress(),
        shipping_region: shippingRegion,
        origin_url: window.location.origin
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="pt-32 pb-24 text-center" data-testid="checkout-empty">
        <div className="max-w-md mx-auto px-6">
          <div className="text-6xl mb-6">🛒</div>
          <h1 className="font-heading text-3xl mb-4">{t.cart.empty}</h1>
          <p className="text-muted-foreground mb-8">
            {lang === 'it' 
              ? 'Non hai ancora aggiunto opere al carrello.' 
              : 'You haven\'t added any artworks to your cart yet.'}
          </p>
          <Button onClick={() => navigate('/gallery')} className="bg-primary">
            {lang === 'it' ? 'Esplora la Galleria' : 'Browse Gallery'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24 bg-gradient-to-b from-background to-muted/30" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight mb-2">{t.checkout.title}</h1>
          <p className="text-muted-foreground">
            {lang === 'it' ? 'Completa i tuoi dati per finalizzare l\'ordine' : 'Complete your details to finalize your order'}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form Section - 3 columns */}
          <div className="lg:col-span-3">
            <div className="bg-card border rounded-xl p-6 md:p-8 shadow-sm">
              {/* Contact Info */}
              <div className="mb-8">
                <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">1</span>
                  {lang === 'it' ? 'Informazioni di Contatto' : 'Contact Information'}
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">
                      {lang === 'it' ? 'Nome Completo' : 'Full Name'} *
                    </label>
                    <Input
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder={lang === 'it' ? 'Mario Rossi' : 'John Doe'}
                      className="h-11"
                      data-testid="checkout-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">
                      {lang === 'it' ? 'Telefono' : 'Phone'} *
                    </label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      placeholder="+39 333 1234567"
                      className="h-11"
                      data-testid="checkout-phone"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-muted-foreground mb-1.5 block">Email *</label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="email@example.com"
                      className="h-11"
                      data-testid="checkout-email"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="mb-8">
                <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">2</span>
                  {t.checkout.shipping}
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">
                      {lang === 'it' ? 'Indirizzo' : 'Street Address'} *
                    </label>
                    <Input
                      value={form.address}
                      onChange={e => setForm({ ...form, address: e.target.value })}
                      placeholder={lang === 'it' ? 'Via Roma, 123' : '123 Main Street'}
                      className="h-11"
                      data-testid="checkout-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground mb-1.5 block">
                        {lang === 'it' ? 'CAP / Codice Postale' : 'Postal Code'} *
                      </label>
                      <Input
                        value={form.postal_code}
                        onChange={e => setForm({ ...form, postal_code: e.target.value })}
                        placeholder="00100"
                        className="h-11"
                        data-testid="checkout-postal"
                      />
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <label className="text-sm text-muted-foreground mb-1.5 block">
                        {lang === 'it' ? 'Città' : 'City'} *
                      </label>
                      <Input
                        value={form.city}
                        onChange={e => setForm({ ...form, city: e.target.value })}
                        placeholder={lang === 'it' ? 'Roma' : 'Rome'}
                        className="h-11"
                        data-testid="checkout-city"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">
                      {lang === 'it' ? 'Paese' : 'Country'} *
                    </label>
                    <select
                      value={form.country}
                      onChange={e => setForm({ ...form, country: e.target.value })}
                      className="w-full h-11 px-3 border border-input bg-background rounded-md text-sm"
                      data-testid="checkout-country"
                    >
                      {ALL_COUNTRIES
                        .sort((a, b) => a[lang].localeCompare(b[lang]))
                        .map(c => (
                          <option key={c.code} value={c.code}>{c[lang]}</option>
                        ))
                      }
                    </select>
                    {shippingRegion !== 'italy' && (
                      <p className="text-xs mt-1 text-muted-foreground">
                        {shippingRegion === 'europe' 
                          ? (lang === 'it' ? 'Spedizione Europa (UE)' : 'Europe (EU) shipping')
                          : (lang === 'it' ? 'Spedizione Internazionale' : 'International shipping')
                        }
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div>
                <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">3</span>
                  {t.checkout.payment}
                </h2>
                <div className="space-y-3">
                  <Button
                    onClick={handleStripeCheckout}
                    disabled={loading}
                    className="w-full h-12 bg-primary hover:bg-primary/90 text-base"
                    data-testid="stripe-checkout-btn"
                  >
                    <CreditCard size={20} className="mr-2" />
                    {loading ? t.common.loading : t.checkout.stripe}
                  </Button>
                  
                  {paypalEnabled && (
                    <Button
                      onClick={async () => {
                        if (!validateForm()) return;
                        setLoading(true);
                        try {
                          const res = await axios.post(`${API}/checkout/external`, {
                            items: items.map(i => ({ painting_id: i.id, quantity: i.quantity })),
                            customer_email: form.email,
                            customer_name: form.name,
                            customer_phone: form.phone,
                            shipping_address: getFullAddress(),
                            shipping_region: shippingRegion,
                            origin_url: window.location.origin,
                            payment_method: 'paypal'
                          });
                          const paypalUrl = paypalMeLink.includes('?') 
                            ? `${paypalMeLink}&amount=${res.data.amount}` 
                            : `${paypalMeLink}/${res.data.amount}EUR`;
                          window.open(paypalUrl, '_blank');
                          clearCart();
                          navigate(`/checkout/success?method=paypal&ref=${res.data.order_ref}&amount=${res.data.amount}`);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Errore');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="w-full h-12 bg-[#0070ba] hover:bg-[#003087] text-white text-base"
                      data-testid="paypal-checkout-btn"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
                      </svg>
                      {t.checkout.paypal}
                    </Button>
                  )}
                  
                  {revolutEnabled && (
                    <Button
                      onClick={async () => {
                        if (!validateForm()) return;
                        setLoading(true);
                        try {
                          const res = await axios.post(`${API}/checkout/external`, {
                            items: items.map(i => ({ painting_id: i.id, quantity: i.quantity })),
                            customer_email: form.email,
                            customer_name: form.name,
                            customer_phone: form.phone,
                            shipping_address: getFullAddress(),
                            shipping_region: shippingRegion,
                            origin_url: window.location.origin,
                            payment_method: 'revolut'
                          });
                          window.open(revolutLink, '_blank');
                          clearCart();
                          navigate(`/checkout/success?method=revolut&ref=${res.data.order_ref}&amount=${res.data.amount}`);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Errore');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="w-full h-12 bg-[#0075EB] hover:bg-[#0066CC] text-white text-base"
                      data-testid="revolut-checkout-btn"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="10" fill="currentColor"/>
                        <path d="M8 12h8M12 8v8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      {t.checkout.revolut || 'Pay with Revolut'}
                    </Button>
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground text-center mt-4">
                  🔒 {lang === 'it' ? 'Pagamento sicuro e protetto' : 'Secure and protected payment'}
                </p>
              </div>
            </div>
          </div>

          {/* Order Summary - 2 columns */}
          <div className="lg:col-span-2">
            <div className="bg-card border rounded-xl p-6 shadow-sm sticky top-28">
              <h2 className="font-heading text-lg mb-4">
                {lang === 'it' ? 'Riepilogo Ordine' : 'Order Summary'}
              </h2>
              
              {/* Items */}
              <div className="space-y-3 mb-4">
                {items.map(item => (
                  <div key={item.id} className="flex gap-3">
                    {item.image_url && (
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {lang === 'it' ? item.title_it : item.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.dimensions}
                      </p>
                    </div>
                    <p className="font-medium">€{item.price.toLocaleString()}</p>
                  </div>
                ))}
              </div>
              
              <Separator className="my-4" />
              
              {/* Totals */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>{lang === 'it' ? 'Subtotale' : 'Subtotal'}</span>
                  <span>€{total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>{lang === 'it' ? 'Spedizione' : 'Shipping'}</span>
                  <span>{allFreeShipping 
                    ? (lang === 'it' ? 'Gratuita' : 'Free') 
                    : `€${shippingCost.toLocaleString()}`
                  }</span>
                </div>
                {shippingRates.custom_tax_percent > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>{lang === 'it' ? 'IVA' : 'Tax'} ({shippingRates.custom_tax_percent}%)</span>
                    <span>€{taxAmount.toFixed(2)}</span>
                  </div>
                )}
                {shippingRegion === 'worldwide' && customsDutyAmount > 0 && (
                  <div className="flex justify-between text-amber-600">
                    <span>{lang === 'it' ? '🌍 Taxe Vamale' : '🌍 Customs Duty'} ({shippingRates.customs_duty_percent}%)</span>
                    <span>€{customsDutyAmount.toFixed(2)}</span>
                  </div>
                )}
              </div>
              
              <Separator className="my-4" />
              
              <div className="flex justify-between items-center">
                <span className="font-heading text-lg">{t.cart.total}</span>
                <span className="font-heading text-2xl text-primary">€{grandTotal.toLocaleString()}</span>
              </div>
              
              {/* Trust badges */}
              <div className="mt-6 pt-4 border-t border-dashed">
                <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
                  <span>🚚 {lang === 'it' ? 'Spedizione Assicurata' : 'Insured Shipping'}</span>
                  <span>🎨 {lang === 'it' ? 'Opera Originale' : 'Original Artwork'}</span>
                  <span>📜 {lang === 'it' ? 'Certificato' : 'Certificate'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Unsubscribe Page
function UnsubscribePage() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error, not_found
  const { t } = useLanguage();

  useEffect(() => {
    // Check if email is in URL params
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [location]);

  const handleUnsubscribe = async () => {
    if (!email) return;
    setStatus('loading');
    try {
      const res = await axios.post(`${API}/newsletter/unsubscribe`, { email });
      if (res.data.success) {
        setStatus('success');
      } else {
        setStatus('not_found');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div className="pt-32 pb-24 text-center">
      <div className="max-w-xl mx-auto px-6">
        {status === 'idle' && (
          <>
            <h1 className="font-heading text-3xl mb-4">Unsubscribe from Newsletter</h1>
            <p className="font-body text-muted-foreground mb-6">
              Enter your email address to unsubscribe from our newsletter.
            </p>
            <div className="flex gap-2 max-w-md mx-auto">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1"
              />
              <Button onClick={handleUnsubscribe} disabled={!email}>
                Unsubscribe
              </Button>
            </div>
          </>
        )}
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mx-auto mb-6"></div>
            <h1 className="font-heading text-3xl mb-4">Processing...</h1>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="font-heading text-3xl mb-4">Successfully Unsubscribed</h1>
            <p className="font-body text-muted-foreground mb-8">
              You have been removed from our newsletter. We're sorry to see you go!
            </p>
            <Button onClick={() => window.location.href = '/'}>
              Return Home
            </Button>
          </>
        )}
        {status === 'not_found' && (
          <>
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl">🔍</span>
            </div>
            <h1 className="font-heading text-3xl mb-4">Email Not Found</h1>
            <p className="font-body text-muted-foreground mb-8">
              This email address is not in our subscription list.
            </p>
            <Button onClick={() => setStatus('idle')}>
              Try Another Email
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl">❌</span>
            </div>
            <h1 className="font-heading text-3xl mb-4">Error</h1>
            <p className="font-body text-muted-foreground mb-8">
              Something went wrong. Please try again later.
            </p>
            <Button onClick={() => setStatus('idle')}>
              Try Again
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function CheckoutSuccessPage() {
  const location = useLocation();
  const { clearCart } = useCart();
  const { lang } = useLanguage();
  const [status, setStatus] = useState('checking');
  const [orderRef, setOrderRef] = useState('');
  const [orderAmount, setOrderAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const method = params.get('method');
    const ref = params.get('ref');
    const amount = params.get('amount');
    
    if (method && (method === 'paypal' || method === 'revolut')) {
      setPaymentMethod(method);
      setOrderRef(ref || '');
      setOrderAmount(amount || '');
      setStatus('awaiting');
      clearCart();
      return;
    }
    
    if (sessionId) {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) {
          setStatus('timeout');
          return;
        }
        try {
          const res = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            setStatus('success');
            clearCart();
          } else if (res.data.status === 'expired') {
            setStatus('expired');
          } else {
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } catch {
          setStatus('error');
        }
      };
      pollStatus();
    }
  }, [location, clearCart]);

  return (
    <div className="pt-32 pb-24 text-center" data-testid="checkout-success-page">
      <div className="max-w-xl mx-auto px-6">
        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mx-auto mb-6"></div>
            <h1 className="font-heading text-3xl mb-4">{lang === 'it' ? 'Elaborazione del pagamento...' : 'Processing your payment...'}</h1>
          </>
        )}
        {status === 'awaiting' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="font-heading text-3xl mb-4">{lang === 'it' ? 'Ordine Registrato!' : 'Order Registered!'}</h1>
            <div className="bg-card border rounded-xl p-6 text-left mb-6">
              <p className="font-body text-muted-foreground mb-3">
                {lang === 'it' 
                  ? `Completa il pagamento di €${orderAmount} tramite ${paymentMethod === 'paypal' ? 'PayPal' : 'Revolut'} nella finestra appena aperta.`
                  : `Complete the payment of €${orderAmount} via ${paymentMethod === 'paypal' ? 'PayPal' : 'Revolut'} in the window that just opened.`
                }
              </p>
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm"><strong>{lang === 'it' ? 'Riferimento ordine' : 'Order reference'}:</strong> #{orderRef}</p>
                <p className="text-sm"><strong>{lang === 'it' ? 'Importo' : 'Amount'}:</strong> €{orderAmount}</p>
                <p className="text-sm"><strong>{lang === 'it' ? 'Metodo' : 'Method'}:</strong> {paymentMethod === 'paypal' ? 'PayPal' : 'Revolut'}</p>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {lang === 'it' 
                  ? 'Riceverai una conferma via email non appena il pagamento sarà verificato.'
                  : 'You will receive an email confirmation once the payment is verified.'
                }
              </p>
            </div>
            <Button onClick={() => window.location.href = '/'}>{lang === 'it' ? 'Torna alla Home' : 'Return Home'}</Button>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="font-heading text-3xl mb-4">{lang === 'it' ? 'Grazie!' : 'Thank You!'}</h1>
            <p className="font-body text-muted-foreground mb-8">
              {lang === 'it' ? 'Il tuo ordine è confermato. Riceverai una email con i dettagli della spedizione.' : 'Your order has been confirmed. You will receive an email with shipping details shortly.'}
            </p>
            <Button onClick={() => window.location.href = '/'}>{lang === 'it' ? 'Torna alla Home' : 'Return Home'}</Button>
          </>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <h1 className="font-heading text-3xl mb-4">{lang === 'it' ? 'Qualcosa è andato storto' : 'Something went wrong'}</h1>
            <p className="font-body text-muted-foreground mb-8">
              {lang === 'it' ? 'Contattaci se hai domande sul tuo ordine.' : 'Please contact us if you have any questions about your order.'}
            </p>
            <Button onClick={() => window.location.href = '/contact'}>{lang === 'it' ? 'Contattaci' : 'Contact Us'}</Button>
          </>
        )}
      </div>
    </div>
  );
}

function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(form.email, form.password);
      toast.success('Welcome back!');
      if (data.user.is_admin) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24" data-testid="login-page">
      <div className="max-w-md mx-auto px-6">
        <h1 className="font-heading text-3xl text-center mb-8">Sign In</h1>
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
          <div>
            <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Username</label>
            <Input
              type="text"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              required
              className="mt-2"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Password</label>
            <Input
              type="password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              required
              className="mt-2"
              data-testid="login-password"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit">
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
}

function AdminPage() {
  const { user, token, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [paintings, setPaintings] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [shippingRates, setShippingRates] = useState({ italy: 50, europe: 100, worldwide: 200, custom_tax_percent: 0 });
  const [savingRates, setSavingRates] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberSearch, setSubscriberSearch] = useState('');
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [newsletter, setNewsletter] = useState({ subject: '', content: '', image_url: '', image_width: 100, language: 'en' });
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [importingFromClients, setImportingFromClients] = useState(false);
  const [deletingSubscribers, setDeletingSubscribers] = useState(false);
  // Email Settings
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', smtp_use_tls: true,
    imap_host: '', imap_port: 993, imap_user: '', imap_password: '',
    from_email: '', from_name: 'Elena Candalia Art'
  });
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  // Order email template
  const [orderEmailTpl, setOrderEmailTpl] = useState({
    subject_it: '', subject_en: '', greeting_it: '', greeting_en: '',
    message_it: '', message_en: '', footer_it: '', footer_en: '',
    color_banner: '#8B4513', color_banner_end: '#A0522D', color_accent: '#8B4513',
    color_bg: '#ffffff', color_text: '#333333', color_footer_bg: '#f5f0eb',
    color_card_bg: '#faf7f2', color_card_border: '#e8dfd5'
  });
  const [savingOrderEmail, setSavingOrderEmail] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  // Report language
  const [reportLanguage, setReportLanguage] = useState('it');
  const [editingPainting, setEditingPainting] = useState(null);
  const [savingPainting, setSavingPainting] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newPainting, setNewPainting] = useState({
    title: '', title_it: '', description: '', description_it: '',
    price: 0, dimensions: '', year: 2024, category: 'Portrait',
    image_url: '', additional_images: [], is_sold: false, is_featured: false, is_pinned: false, is_promotion: false, is_unique_art: false, shipping_cost: 0, payment_link: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('+393206215750');
  const [footerText, setFooterText] = useState('© 2024 Elena Candalia');
  const [watermarkText, setWatermarkText] = useState('');
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [watermarkOpacity, setWatermarkOpacity] = useState(15);
  const [heroTitle, setHeroTitle] = useState('Elena Candalia');
  const [heroTitleColor, setHeroTitleColor] = useState('#1a1a1a');
  const [heroButtonEn, setHeroButtonEn] = useState('View Collection');
  const [heroButtonIt, setHeroButtonIt] = useState('Scopri la Collezione');
  // Navigation & Menu Settings
  const [homeVisible, setHomeVisible] = useState(true);
  const [whatsappVisible, setWhatsappVisible] = useState(true);
  const [menuHomeEn, setMenuHomeEn] = useState('Home');
  const [menuHomeIt, setMenuHomeIt] = useState('Home');
  const [menuGalleryEn, setMenuGalleryEn] = useState('Gallery');
  const [menuGalleryIt, setMenuGalleryIt] = useState('Galleria');
  const [menuAboutEn, setMenuAboutEn] = useState('About');
  const [menuAboutIt, setMenuAboutIt] = useState('Chi Sono');
  const [menuContactEn, setMenuContactEn] = useState('Contact');
  const [menuContactIt, setMenuContactIt] = useState('Contatti');
  const [savingCover, setSavingCover] = useState(false);
  const [artistImageUrl, setArtistImageUrl] = useState('');

  // Resolve relative URLs (e.g. /api/uploads/...) to full URLs
  const resolveImageUrl = (url) => {
    if (!url || url.trim() === '') return '';
    if (url.startsWith('/api/') || url.startsWith('/uploads/')) {
      return `${BACKEND_URL}${url}`;
    }
    return url;
  };
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [videoType, setVideoType] = useState('youtube');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitleEn, setVideoTitleEn] = useState('Watch me paint');
  const [videoTitleIt, setVideoTitleIt] = useState('Guardami dipingere');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [critics, setCritics] = useState([]);
  const [editingCritic, setEditingCritic] = useState(null);
  const [isAddingCritic, setIsAddingCritic] = useState(false);
  const [newCritic, setNewCritic] = useState({ text: '', text_it: '', author: '', publication: '' });
  const [savingAbout, setSavingAbout] = useState(false);
  
  // Exhibitions
  const [exhibitions, setExhibitions] = useState([]);
  const [editingExhibition, setEditingExhibition] = useState(null);
  const [isAddingExhibition, setIsAddingExhibition] = useState(false);
  const [newExhibition, setNewExhibition] = useState({ title: '', title_it: '', location: '', date: '', description: '', description_it: '' });
  
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', name_it: '' });
  const [aboutTitleEn, setAboutTitleEn] = useState('About the Artist');
  const [aboutTitleIt, setAboutTitleIt] = useState("L'Artista");
  const [aboutContentEn, setAboutContentEn] = useState('');
  const [aboutContentIt, setAboutContentIt] = useState('');
  const [policies, setPolicies] = useState({
    privacy_en: '', privacy_it: '',
    terms_en: '', terms_it: '',
    refund_en: '', refund_it: '',
    cookie_en: '', cookie_it: ''
  });
  const [savingPolicies, setSavingPolicies] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    email: '', phone: '', website: '', address: '', city: '', country: '',
    instagram: '', facebook: '', tiktok: ''
  });
  const [savingContact, setSavingContact] = useState(false);
  const [contactMessages, setContactMessages] = useState([]);
  const [selectedMessages, setSelectedMessages] = useState([]);
  
  // Stripe Settings
  const [stripeApiKey, setStripeApiKey] = useState('');
  const [stripeKeyType, setStripeKeyType] = useState('');
  const [savingStripe, setSavingStripe] = useState(false);
  
  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    block_bots: false,
    block_vpn_datacenter: false,
    block_suspicious_ua: false,
    blocked_ips: [],
    allowed_countries: []
  });
  
  // Active Carts
  const [activeCarts, setActiveCarts] = useState([]);
  const [showActiveCarts, setShowActiveCarts] = useState(false);
  const [loadingCarts, setLoadingCarts] = useState(false);
  
  // Visitors
  const [visitorStats, setVisitorStats] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [showVisitors, setShowVisitors] = useState(false);
  const [loadingVisitors, setLoadingVisitors] = useState(false);
  
  // PayPal Settings
  const [paypalSettings, setPaypalSettings] = useState({ enabled: false, seller_email: '', paypal_me_link: '' });
  const [savingPaypal, setSavingPaypal] = useState(false);
  
  // Revolut Settings
  const [revolutSettings, setRevolutSettings] = useState({ enabled: false, revolut_link: '', revolut_tag: '' });
  const [savingRevolut, setSavingRevolut] = useState(false);
  
  // SEO Settings
  const [seoSettings, setSeoSettings] = useState({
    site_title: '', meta_description: '', meta_keywords: '', canonical_url: '',
    og_title: '', og_description: '', og_image: '', robots_txt: 'User-agent: *\nAllow: /',
    additional_head_scripts: ''
  });
  const [savingSeo, setSavingSeo] = useState(false);
  
  // Theme/Design Settings
  const [designSettings, setDesignSettings] = useState({
    text_color: '#1a1a1a', background_color: '#ffffff', button_color: '#1a1a1a',
    button_text_color: '#ffffff', primary_color: '#8B2332', hero_image_url: ''
  });
  const [savingDesign, setSavingDesign] = useState(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  const [currentHeroImage, setCurrentHeroImage] = useState(''); // Track actual hero image on site
  
  // Messages (contact form submissions)
  const [messages, setMessages] = useState([]);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [replyingMessage, setReplyingMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // Newsletter Import/Add
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddSubscriberModal, setShowAddSubscriberModal] = useState(false);
  const [newSubscriberEmail, setNewSubscriberEmail] = useState('');
  const [importingSubscribers, setImportingSubscribers] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [showNewsletterTestModal, setShowNewsletterTestModal] = useState(false);
  const [newsletterTestEmail, setNewsletterTestEmail] = useState('');
  const [sendingNewsletterTest, setSendingNewsletterTest] = useState(false);
  
  // Orders & Invoices
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  // Edit Order Modal
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  // Backup
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [backups, setBackups] = useState([]);
  const [showBackupList, setShowBackupList] = useState(false);
  // Modular Backup
  const [showModularBackup, setShowModularBackup] = useState(false);
  const [modularBackupOptions, setModularBackupOptions] = useState({
    include_db: true,
    include_uploads: true,
    include_config: true,
    include_backend: false,
    include_frontend: false
  });
  const [creatingModularBackup, setCreatingModularBackup] = useState(false);
  const [creatingGalleryBackup, setCreatingGalleryBackup] = useState(false);
  // Restore Backup
  const [restoringBackup, setRestoringBackup] = useState(false);
  // File Explorer
  const [fileStructure, setFileStructure] = useState(null);
  const [showFileExplorer, setShowFileExplorer] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  // Clients
  const [clients, setClients] = useState([]);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientPage, setClientPage] = useState(1);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', phone: '', address: '', vat: '', codice_fiscale: '' });
  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [newExpense, setNewExpense] = useState({ description: '', amount: '', date: new Date().toISOString().split('T')[0], category: '', notes: '', attachment_url: '', attachment_filename: '' });
  const [expenseFilterStart, setExpenseFilterStart] = useState('');
  const [expenseFilterEnd, setExpenseFilterEnd] = useState('');
  const [expenseFilterCat, setExpenseFilterCat] = useState('');
  const [uploadingExpenseFile, setUploadingExpenseFile] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingExpCat, setEditingExpCat] = useState(null);
  const [expenseTotals, setExpenseTotals] = useState(null);
  const [totalsYear, setTotalsYear] = useState(new Date().getFullYear());
  const [totalsMonth, setTotalsMonth] = useState(new Date().getMonth() + 1);
  const [extractingInvoice, setExtractingInvoice] = useState(false);
  // Search and pagination for orders/invoices
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [orderPage, setOrderPage] = useState(1);
  const [invoicePage, setInvoicePage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const [invoiceData, setInvoiceData] = useState({
    language: 'en',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    customer_vat: '',
    customer_codice_fiscale: '',
    payment_terms: 30,
    payment_method: '',
    items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
    subtotal: 0,
    shipping: 0,
    tax_rate: 0,
    tax_amount: 0,
    total: 0,
    notes: '',
    order_id: ''
  });
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);

  // Auto-fill customer data from clients or previous invoices when phone changes
  const handlePhoneChange = (phone) => {
    setInvoiceData(prev => ({ ...prev, customer_phone: phone }));
    if (phone && phone.length >= 8) {
      // First check clients list
      const existingClient = clients.find(c => c.phone === phone || c.email === phone);
      if (existingClient) {
        setInvoiceData(prev => ({
          ...prev,
          customer_phone: existingClient.phone || phone,
          customer_name: existingClient.name || prev.customer_name,
          customer_email: existingClient.email || prev.customer_email,
          customer_address: existingClient.address || prev.customer_address,
          customer_vat: existingClient.vat || prev.customer_vat,
          customer_codice_fiscale: existingClient.codice_fiscale || prev.customer_codice_fiscale,
        }));
        toast.info('Customer data auto-filled from client database');
        return;
      }
      // Fallback to invoices
      const existingInvoice = invoices.find(inv => inv.customer_phone === phone);
      if (existingInvoice) {
        setInvoiceData(prev => ({
          ...prev,
          customer_phone: phone,
          customer_name: existingInvoice.customer_name || prev.customer_name,
          customer_email: existingInvoice.customer_email || prev.customer_email,
          customer_address: existingInvoice.customer_address || prev.customer_address,
          customer_vat: existingInvoice.customer_vat || prev.customer_vat,
          customer_codice_fiscale: existingInvoice.customer_codice_fiscale || prev.customer_codice_fiscale,
        }));
        toast.info('Customer data auto-filled from previous invoice');
      }
    }
  };

  useEffect(() => {
    // Wait for auth loading to complete before checking user
    if (loading) return;
    
    if (!user?.is_admin) {
      navigate('/admin/login');
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/admin/stats`, { headers }).then(res => setStats(res.data));
    axios.get(`${API}/paintings`).then(res => setPaintings(res.data));
    axios.get(`${API}/shipping-rates`).then(res => setShippingRates(res.data));
    axios.get(`${API}/admin/newsletter`, { headers }).then(res => setSubscribers(res.data));
    axios.get(`${API}/policies`).then(res => setPolicies(res.data));
    axios.get(`${API}/contact-settings`).then(res => setContactInfo(res.data));
    axios.get(`${API}/admin/contacts`, { headers }).then(res => setContactMessages(res.data));
    axios.get(`${API}/admin/orders`, { headers }).then(res => setOrders(res.data));
    axios.get(`${API}/admin/invoices`, { headers }).then(res => setInvoices(res.data));
    axios.get(`${API}/admin/clients`, { headers }).then(res => setClients(res.data));
    axios.get(`${API}/admin/active-carts`, { headers }).then(res => setActiveCarts(res.data)).catch(() => {});
    axios.get(`${API}/admin/expense-categories`, { headers }).then(res => setExpenseCategories(res.data)).catch(() => {});
    axios.get(`${API}/admin/expenses`, { headers }).then(res => setExpenses(res.data)).catch(() => {});
    axios.get(`${API}/admin/expenses/totals?year=${new Date().getFullYear()}&month=${new Date().getMonth() + 1}`, { headers }).then(res => setExpenseTotals(res.data)).catch(() => {});
    axios.get(`${API}/admin/order-email-template`, { headers }).then(res => setOrderEmailTpl(res.data)).catch(() => {});
    axios.get(`${API}/admin/expense-settings`, { headers }).then(res => setReportLanguage(res.data.report_language || 'it')).catch(() => {});
    axios.get(`${API}/admin/visitors`, { headers }).then(res => {
      setVisitorStats(res.data.stats);
      setVisitors(res.data.visitors);
    }).catch(() => {});
    axios.get(`${API}/admin/stripe-settings`, { headers }).then(res => {
      setStripeApiKey(res.data.stripe_api_key || '');
      setStripeKeyType(res.data.key_type || '');
    }).catch(() => {});
    axios.get(`${API}/admin/security-settings`, { headers }).then(res => {
      setSecuritySettings(res.data);
    }).catch(() => {});
    axios.get(`${API}/admin/email-settings`, { headers }).then(res => {
      if (res.data) {
        setEmailSettings(res.data);
      }
    }).catch((err) => {
      console.error('Failed to load email settings:', err);
    });
    axios.get(`${API}/site-settings`).then(res => {
      setCoverImageUrl(res.data.cover_image_url || '');
      setCurrentHeroImage(res.data.cover_image_url || 'https://customer-assets.emergentagent.com/job_elena-art-gallery/artifacts/5pup7imy_FB_IMG_1766394920850.jpg');
      setWhatsappNumber(res.data.whatsapp_number || '+393206215750');
      setFooterText(res.data.footer_text || '© 2024 Elena Candalia');
      setWatermarkText(res.data.watermark_text || '');
      setWatermarkEnabled(res.data.watermark_enabled || false);
      setWatermarkOpacity(res.data.watermark_opacity || 15);
      setHeroTitle(res.data.hero_title || 'Elena Candalia');
      setHeroTitleColor(res.data.hero_title_color || '#1a1a1a');
      setHeroButtonEn(res.data.hero_button_en || 'View Collection');
      setHeroButtonIt(res.data.hero_button_it || 'Scopri la Collezione');
      // Navigation & Menu settings
      setHomeVisible(res.data.home_visible !== false);
      setWhatsappVisible(res.data.whatsapp_visible !== false);
      setMenuHomeEn(res.data.menu_home_en || 'Home');
      setMenuHomeIt(res.data.menu_home_it || 'Home');
      setMenuGalleryEn(res.data.menu_gallery_en || 'Gallery');
      setMenuGalleryIt(res.data.menu_gallery_it || 'Galleria');
      setMenuAboutEn(res.data.menu_about_en || 'About');
      setMenuAboutIt(res.data.menu_about_it || 'Chi Sono');
      setMenuContactEn(res.data.menu_contact_en || 'Contact');
      setMenuContactIt(res.data.menu_contact_it || 'Contatti');
    });
    axios.get(`${API}/about-settings`).then(res => {
      setArtistImageUrl(res.data.artist_image_url || '');
      setAboutTitleEn(res.data.about_title_en || 'About the Artist');
      setAboutTitleIt(res.data.about_title_it || "L'Artista");
      setAboutContentEn(res.data.about_content_en || '');
      setAboutContentIt(res.data.about_content_it || '');
      setVideoEnabled(res.data.video_enabled || false);
      setVideoType(res.data.video_type || 'youtube');
      setVideoUrl(res.data.video_url || '');
      setVideoTitleEn(res.data.video_title_en || 'Watch me paint');
      setVideoTitleIt(res.data.video_title_it || 'Guardami dipingere');
    });
    axios.get(`${API}/critics`).then(res => setCritics(res.data));
    axios.get(`${API}/exhibitions`).then(res => setExhibitions(res.data));
    axios.get(`${API}/categories`).then(res => setCategories(res.data));
    // Fetch PayPal settings
    axios.get(`${API}/admin/paypal-settings`, { headers }).then(res => {
      setPaypalSettings(res.data);
    }).catch(() => {});
    // Fetch Revolut settings
    axios.get(`${API}/admin/revolut-settings`, { headers }).then(res => {
      setRevolutSettings(res.data);
    }).catch(() => {});
    // Fetch SEO settings
    axios.get(`${API}/admin/seo-settings`, { headers }).then(res => {
      setSeoSettings(res.data);
    }).catch(() => {});
    // Fetch Design settings
    axios.get(`${API}/admin/design-settings`, { headers }).then(res => {
      setDesignSettings(res.data);
    }).catch(() => {});
    // Fetch Messages
    axios.get(`${API}/admin/contacts`, { headers }).then(res => {
      setMessages(res.data);
    }).catch(() => {});
  }, [user, token, navigate, loading]);

  // Save Email Settings
  const saveEmailSettings = async () => {
    setSavingEmailSettings(true);
    try {
      const settingsToSave = {
        smtp_host: emailSettings.smtp_host || '',
        smtp_port: emailSettings.smtp_port || 587,
        smtp_user: emailSettings.smtp_user || '',
        smtp_password: emailSettings.smtp_password || '',
        smtp_use_tls: emailSettings.smtp_use_tls !== false,
        imap_host: emailSettings.imap_host || '',
        imap_port: emailSettings.imap_port || 993,
        imap_user: emailSettings.imap_user || '',
        imap_password: emailSettings.imap_password || '',
        from_email: emailSettings.from_email || '',
        from_name: emailSettings.from_name || 'Elena Candalia Art'
      };
      await axios.put(`${API}/admin/email-settings`, settingsToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Email settings saved successfully!');
    } catch (err) {
      console.error('Save email settings error:', err);
      toast.error(err.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSavingEmailSettings(false);
    }
  };

  const saveOrderEmailTemplate = async () => {
    setSavingOrderEmail(true);
    try {
      await axios.put(`${API}/admin/order-email-template`, orderEmailTpl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template email ordine salvat!');
    } catch (err) {
      toast.error('Errore nel salvare il template');
    } finally {
      setSavingOrderEmail(false);
    }
  };


  const testEmailSettings = async () => {
    setTestingEmail(true);
    try {
      await axios.post(`${API}/admin/email-settings/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test email sent! Check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  // Save Stripe settings
  const saveStripeSettings = async () => {
    if (!stripeApiKey) {
      toast.error('Please enter a Stripe API key');
      return;
    }
    if (!stripeApiKey.startsWith('sk_live_') && !stripeApiKey.startsWith('sk_test_')) {
      toast.error('Invalid Stripe API key. Must start with sk_live_ or sk_test_');
      return;
    }
    setSavingStripe(true);
    try {
      const res = await axios.put(`${API}/admin/stripe-settings`, 
        { stripe_api_key: stripeApiKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStripeKeyType(res.data.key_type);
      toast.success('Stripe API key updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update Stripe key');
    } finally {
      setSavingStripe(false);
    }
  };

  // Save PayPal settings
  const savePaypalSettings = async () => {
    setSavingPaypal(true);
    try {
      await axios.put(`${API}/admin/paypal-settings`, paypalSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PayPal settings saved!');
    } catch (err) {
      toast.error('Failed to save PayPal settings');
    } finally {
      setSavingPaypal(false);
    }
  };

  // Save Revolut settings
  const saveRevolutSettings = async () => {
    setSavingRevolut(true);
    try {
      await axios.put(`${API}/admin/revolut-settings`, revolutSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Revolut settings saved!');
    } catch (err) {
      toast.error('Failed to save Revolut settings');
    } finally {
      setSavingRevolut(false);
    }
  };

  // Save SEO settings
  const saveSeoSettings = async () => {
    setSavingSeo(true);
    try {
      await axios.put(`${API}/admin/seo-settings`, seoSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SEO settings saved!');
    } catch (err) {
      toast.error('Failed to save SEO settings');
    } finally {
      setSavingSeo(false);
    }
  };

  // Save Design/Theme settings
  const saveDesignSettings = async () => {
    setSavingDesign(true);
    try {
      // Save design settings (colors)
      await axios.put(`${API}/admin/design-settings`, designSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Also save hero image to site-settings
      await axios.put(`${API}/admin/site-settings`, {
        cover_image_url: coverImageUrl,
        whatsapp_number: whatsappNumber,
        footer_text: footerText,
        watermark_text: watermarkText,
        watermark_enabled: watermarkEnabled,
        watermark_opacity: watermarkOpacity,
        hero_title: heroTitle,
        hero_title_color: heroTitleColor,
        hero_button_en: heroButtonEn,
        hero_button_it: heroButtonIt,
        home_visible: homeVisible,
        whatsapp_visible: whatsappVisible,
        menu_home_en: menuHomeEn,
        menu_home_it: menuHomeIt,
        menu_gallery_en: menuGalleryEn,
        menu_gallery_it: menuGalleryIt,
        menu_about_en: menuAboutEn,
        menu_about_it: menuAboutIt,
        menu_contact_en: menuContactEn,
        menu_contact_it: menuContactIt
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Design settings saved!');
    } catch (err) {
      toast.error('Failed to save design settings');
    } finally {
      setSavingDesign(false);
    }
  };

  // Upload hero image
  const uploadHeroImage = async (file) => {
    if (!file) return;
    setUploadingHeroImage(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setDesignSettings({ ...designSettings, hero_image_url: res.data.url });
      toast.success('Hero image uploaded!');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingHeroImage(false);
    }
  };

  // Delete message
  const deleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(messages.filter(m => m.id !== id));
      setSelectedMessageIds(selectedMessageIds.filter(mid => mid !== id));
      toast.success('Message deleted');
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  // Delete multiple messages
  const deleteSelectedMessages = async () => {
    if (selectedMessageIds.length === 0) return;
    try {
      await axios.post(`${API}/admin/contacts/delete-multiple`, { ids: selectedMessageIds }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(messages.filter(m => !selectedMessageIds.includes(m.id)));
      setSelectedMessageIds([]);
      toast.success(`Deleted ${selectedMessageIds.length} messages`);
    } catch (err) {
      toast.error('Failed to delete messages');
    }
  };

  // Reply to message using saved email settings
  const sendReply = async () => {
    if (!replyingMessage || !replyContent.trim()) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/admin/contacts/${replyingMessage.id}/reply`, 
        { content: replyContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reply sent!');
      setReplyingMessage(null);
      setReplyContent('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  // Add newsletter subscriber manually
  const addSubscriberManually = async () => {
    if (!newSubscriberEmail || !newSubscriberEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/newsletter/add`, { email: newSubscriberEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setSubscribers([...subscribers, { email: newSubscriberEmail, subscribed_at: new Date().toISOString() }]);
        toast.success('Subscriber added!');
      } else {
        toast.info(res.data.message);
      }
      setNewSubscriberEmail('');
      setShowAddSubscriberModal(false);
    } catch (err) {
      toast.error('Failed to add subscriber');
    }
  };

  // Import subscribers from file
  const importSubscribersFromFile = async (file) => {
    if (!file) return;
    setImportingSubscribers(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/newsletter/import`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported: ${res.data.added} added, ${res.data.skipped} already existed`);
      // Refresh subscribers list
      const subRes = await axios.get(`${API}/admin/newsletter`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscribers(subRes.data);
      setShowImportModal(false);
    } catch (err) {
      toast.error('Failed to import subscribers');
    } finally {
      setImportingSubscribers(false);
    }
  };

  // Test email with specific recipient
  const sendTestEmailToRecipient = async () => {
    if (!testEmailRecipient) {
      toast.error('Please select a recipient');
      return;
    }
    setTestingEmail(true);
    try {
      await axios.post(`${API}/admin/email-settings/test`, { recipient: testEmailRecipient }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test email sent to ${testEmailRecipient}!`);
      setShowTestEmailModal(false);
      setTestEmailRecipient('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  // Send test newsletter to specific recipient
  const sendNewsletterTestEmail = async () => {
    if (!newsletterTestEmail) {
      toast.error('Please select a recipient');
      return;
    }
    if (!newsletter.subject || !newsletter.content) {
      toast.error('Please fill newsletter subject and content');
      return;
    }
    setSendingNewsletterTest(true);
    try {
      await axios.post(`${API}/admin/newsletter/send-test`, {
        subject: newsletter.subject,
        content: newsletter.content,
        image_url: newsletter.image_url,
        recipient: newsletterTestEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test newsletter sent to ${newsletterTestEmail}!`);
      setShowNewsletterTestModal(false);
      setNewsletterTestEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test newsletter');
    } finally {
      setSendingNewsletterTest(false);
    }
  };

  const saveShippingRates = async () => {
    setSavingRates(true);
    try {
      await axios.put(`${API}/admin/shipping-rates`, shippingRates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Shipping rates saved!');
    } catch (err) {
      toast.error('Failed to save rates');
    } finally {
      setSavingRates(false);
    }
  };

  // Fetch active carts
  const fetchActiveCarts = async () => {
    setLoadingCarts(true);
    try {
      const res = await axios.get(`${API}/admin/active-carts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveCarts(res.data);
    } catch (err) {
      toast.error('Failed to load active carts');
    } finally {
      setLoadingCarts(false);
    }
  };

  const deleteActiveCart = async (ipAddress) => {
    try {
      await axios.delete(`${API}/admin/active-carts/${encodeURIComponent(ipAddress)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveCarts(activeCarts.filter(c => c.ip_address !== ipAddress));
      toast.success('Cart removed');
    } catch (err) {
      toast.error('Failed to delete cart');
    }
  };

  // Fetch visitor statistics
  const fetchVisitors = async () => {
    setLoadingVisitors(true);
    try {
      const res = await axios.get(`${API}/admin/visitors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVisitorStats(res.data.stats);
      setVisitors(res.data.visitors);
    } catch (err) {
      toast.error('Failed to load visitors');
    } finally {
      setLoadingVisitors(false);
    }
  };

  // Country code to flag emoji
  const getCountryFlag = (countryCode) => {
    if (!countryCode || countryCode === 'UN') return '🌍';
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  // Backup functions
  const createFullBackup = async () => {
    setCreatingBackup(true);
    try {
      toast.info('Creating backup... This may take a moment');
      const res = await axios.get(`${API}/admin/backup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download the backup file
      const downloadUrl = `${BACKEND_URL}${res.data.backup_file}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = res.data.filename;
      
      // Need to fetch with auth header for protected endpoint
      const downloadRes = await axios.get(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([downloadRes.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup created and downloaded!');
      fetchBackups();
    } catch (err) {
      console.error('Backup error:', err);
      toast.error('Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${API}/admin/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(res.data);
    } catch (err) {
      console.error('Failed to fetch backups');
    }
  };

  const downloadBackup = async (filename) => {
    try {
      const downloadUrl = `${BACKEND_URL}/api/admin/backup/download/${filename}`;
      const res = await axios.get(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (err) {
      toast.error('Failed to download backup');
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Delete backup ${filename}?`)) return;
    try {
      await axios.delete(`${API}/admin/backup/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(backups.filter(b => b.filename !== filename));
      toast.success('Backup deleted');
    } catch (err) {
      toast.error('Failed to delete backup');
    }
  };

  // Modular Backup (VPS Portable)
  const createModularBackup = async () => {
    setCreatingModularBackup(true);
    try {
      toast.info('Creating portable backup... This may take a moment');
      const res = await axios.post(`${API}/admin/backup/modular`, modularBackupOptions, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download the backup file
      const downloadUrl = `${BACKEND_URL}${res.data.backup_file}`;
      const downloadRes = await axios.get(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([downloadRes.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Portable backup created! Included: ${res.data.included.join(', ')}`);
      fetchBackups();
    } catch (err) {
      console.error('Modular backup error:', err);
      toast.error('Failed to create portable backup');
    } finally {
      setCreatingModularBackup(false);
    }
  };

  // Restore Backup
  const restoreBackup = async (file) => {
    if (!file) return;
    if (!window.confirm('⚠️ ATENȚIE: Restaurarea va suprascrie datele existente. Continuați?')) return;
    
    setRestoringBackup(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/admin/backup/restore`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Backup restaurat! ${res.data.restored.length} elemente restaurate.`);
      // Refresh page to see restored data
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restaurare eșuată');
    } finally {
      setRestoringBackup(false);
    }
  };

  // File Explorer
  const fetchFileStructure = async () => {
    setLoadingFiles(true);
    try {
      const res = await axios.get(`${API}/admin/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFileStructure(res.data);
    } catch (err) {
      toast.error('Failed to load file structure');
    } finally {
      setLoadingFiles(false);
    }
  };

  const toggleFolder = (path) => {
    setExpandedFolders(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const viewFileContent = async (filePath) => {
    try {
      const res = await axios.get(`${API}/admin/files/content/${encodeURIComponent(filePath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedFile(filePath);
      setFileContent(res.data.content);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot view file');
    }
  };

  // Render file tree recursively
  const renderFileTree = (items, depth = 0) => {
    return items.map((item, idx) => (
      <div key={idx} style={{ marginLeft: depth * 16 }}>
        {item.type === 'folder' ? (
          <div>
            <button
              onClick={() => toggleFolder(item.path)}
              className="flex items-center gap-1 hover:bg-muted p-1 rounded w-full text-left"
            >
              <span>{expandedFolders[item.path] ? '📂' : '📁'}</span>
              <span className="font-medium">{item.name}/</span>
            </button>
            {expandedFolders[item.path] && item.children && (
              <div>{renderFileTree(item.children, depth + 1)}</div>
            )}
          </div>
        ) : (
          <button
            onClick={() => viewFileContent(item.path)}
            className={`flex items-center gap-1 hover:bg-muted p-1 rounded w-full text-left text-sm ${selectedFile === item.path ? 'bg-primary/10' : ''}`}
          >
            <span>{getFileIcon(item.ext)}</span>
            <span className="flex-1 truncate">{item.name}</span>
            <span className="text-xs text-muted-foreground">{item.size}</span>
          </button>
        )}
      </div>
    ));
  };

  const getFileIcon = (ext) => {
    const icons = {
      '.py': '🐍',
      '.js': '📜',
      '.jsx': '⚛️',
      '.json': '📋',
      '.html': '🌐',
      '.css': '🎨',
      '.md': '📝',
      '.env': '🔐',
      '.txt': '📄',
      '.png': '🖼️',
      '.jpg': '🖼️',
      '.jpeg': '🖼️',
      '.gif': '🖼️',
      '.svg': '🖼️',
      '.zip': '📦'
    };
    return icons[ext?.toLowerCase()] || '📄';
  };

  const saveCoverImage = async () => {
    setSavingCover(true);
    try {
      await axios.put(`${API}/admin/site-settings`, { 
        cover_image_url: coverImageUrl,
        whatsapp_number: whatsappNumber,
        footer_text: footerText,
        watermark_text: watermarkText,
        watermark_enabled: watermarkEnabled,
        watermark_opacity: watermarkOpacity,
        hero_title: heroTitle,
        hero_title_color: heroTitleColor,
        hero_button_en: heroButtonEn,
        hero_button_it: heroButtonIt,
        home_visible: homeVisible,
        whatsapp_visible: whatsappVisible,
        menu_home_en: menuHomeEn,
        menu_home_it: menuHomeIt,
        menu_gallery_en: menuGalleryEn,
        menu_gallery_it: menuGalleryIt,
        menu_about_en: menuAboutEn,
        menu_about_it: menuAboutIt,
        menu_contact_en: menuContactEn,
        menu_contact_it: menuContactIt
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved!');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSavingCover(false);
    }
  };

  // Invoice functions
  const startNewInvoice = async (order = null) => {
    try {
      const res = await axios.get(`${API}/admin/next-invoice-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (order) {
        const items = order.items?.map(item => ({
          description: item.title || item.name || 'Artwork',
          quantity: item.quantity || 1,
          unit_price: item.price || 0,
          total: (item.price || 0) * (item.quantity || 1)
        })) || [];
        
        const subtotal = items.reduce((sum, item) => sum + item.total, 0);
        
        setInvoiceData({
          language: 'en',
          invoice_number: res.data.invoice_number,
          invoice_date: new Date().toISOString().split('T')[0],
          customer_name: order.customer_name || '',
          customer_email: order.customer_email || '',
          customer_phone: order.customer_phone || '',
          customer_address: order.shipping_address || '',
          customer_vat: '',
          customer_codice_fiscale: '',
          payment_terms: 30,
          payment_method: '',
          items,
          subtotal,
          shipping: order.shipping_cost || 0,
          tax_rate: 0,
          tax_amount: 0,
          total: subtotal + (order.shipping_cost || 0),
          notes: '',
          order_id: order.id || ''
        });
      } else {
        setInvoiceData({
          language: 'en',
          invoice_number: res.data.invoice_number,
          invoice_date: new Date().toISOString().split('T')[0],
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          customer_address: '',
          customer_vat: '',
          customer_codice_fiscale: '',
          payment_terms: 30,
          payment_method: '',
          items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
          subtotal: 0,
          shipping: 0,
          tax_rate: 0,
          tax_amount: 0,
          total: 0,
          notes: '',
          order_id: ''
        });
      }
      setEditingInvoiceId(null);
      setShowInvoiceForm(true);
    } catch (err) {
      toast.error('Failed to generate invoice number');
    }
  };

  // Edit existing invoice
  const editInvoice = (invoice) => {
    setInvoiceData({
      language: invoice.language || 'en',
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date || new Date().toISOString().split('T')[0],
      customer_name: invoice.customer_name || '',
      customer_email: invoice.customer_email || '',
      customer_phone: invoice.customer_phone || '',
      customer_address: invoice.customer_address || '',
      customer_vat: invoice.customer_vat || '',
      customer_codice_fiscale: invoice.customer_codice_fiscale || '',
      payment_terms: invoice.payment_terms || 30,
      payment_method: invoice.payment_method || '',
      items: invoice.items || [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
      subtotal: invoice.subtotal || 0,
      shipping: invoice.shipping || 0,
      tax_rate: invoice.tax_rate || 0,
      tax_amount: invoice.tax_amount || 0,
      total: invoice.total || 0,
      notes: invoice.notes || '',
      order_id: invoice.order_id || ''
    });
    setEditingInvoiceId(invoice.id);
    setShowInvoiceForm(true);
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceData.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
    const taxAmount = subtotal * (invoiceData.tax_rate / 100);
    setInvoiceData({
      ...invoiceData,
      items: newItems,
      subtotal,
      tax_amount: taxAmount,
      total: subtotal + invoiceData.shipping + taxAmount
    });
  };

  const addInvoiceItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [...invoiceData.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeInvoiceItem = (index) => {
    const newItems = invoiceData.items.filter((_, i) => i !== index);
    const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
    const taxAmount = subtotal * (invoiceData.tax_rate / 100);
    setInvoiceData({
      ...invoiceData,
      items: newItems,
      subtotal,
      tax_amount: taxAmount,
      total: subtotal + invoiceData.shipping + taxAmount
    });
  };

  const updateInvoiceTotals = (field, value) => {
    const newData = { ...invoiceData, [field]: parseFloat(value) || 0 };
    if (field === 'tax_rate') {
      newData.tax_amount = newData.subtotal * (newData.tax_rate / 100);
    }
    newData.total = newData.subtotal + newData.shipping + newData.tax_amount;
    setInvoiceData(newData);
  };

  const saveInvoice = async () => {
    if (!invoiceData.customer_name || !invoiceData.customer_email) {
      toast.error('Please fill customer name and email');
      return;
    }
    setSavingInvoice(true);
    try {
      if (editingInvoiceId) {
        // Update existing invoice
        const res = await axios.put(`${API}/admin/invoices/${editingInvoiceId}`, invoiceData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvoices(invoices.map(inv => inv.id === editingInvoiceId ? res.data : inv));
        toast.success('Invoice updated!');
      } else {
        // Create new invoice
        const res = await axios.post(`${API}/admin/invoices`, invoiceData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvoices([res.data, ...invoices]);
        toast.success('Invoice created!');
      }
      setShowInvoiceForm(false);
      setEditingInvoiceId(null);
    } catch (err) {
      toast.error(editingInvoiceId ? 'Failed to update invoice' : 'Failed to create invoice');
    } finally {
      setSavingInvoice(false);
    }
  };

  // Invoice Export State
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportPeriod, setExportPeriod] = useState('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Get available years from invoices
  const getAvailableYears = () => {
    const years = new Set();
    const currentYear = new Date().getFullYear();
    // Add last 5 years as options
    for (let i = 0; i < 5; i++) {
      years.add(currentYear - i);
    }
    // Also add years from existing invoices
    invoices.forEach(inv => {
      if (inv.invoice_date) {
        years.add(new Date(inv.invoice_date).getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  };
  
  // Month names
  const monthNames = [
    { value: 1, label: 'January / Gennaio' },
    { value: 2, label: 'February / Febbraio' },
    { value: 3, label: 'March / Marzo' },
    { value: 4, label: 'April / Aprile' },
    { value: 5, label: 'May / Maggio' },
    { value: 6, label: 'June / Giugno' },
    { value: 7, label: 'July / Luglio' },
    { value: 8, label: 'August / Agosto' },
    { value: 9, label: 'September / Settembre' },
    { value: 10, label: 'October / Ottobre' },
    { value: 11, label: 'November / Novembre' },
    { value: 12, label: 'December / Dicembre' }
  ];
  
  // Filter invoices by period
  const getFilteredInvoices = (period) => {
    const now = new Date();
    let startDate;
    
    switch(period) {
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return invoices.filter(inv => {
          const invDate = new Date(inv.invoice_date);
          return invDate >= startDate;
        });
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        return invoices.filter(inv => {
          const invDate = new Date(inv.invoice_date);
          return invDate >= startDate;
        });
      case 'year':
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        return invoices.filter(inv => {
          const invDate = new Date(inv.invoice_date);
          return invDate >= startDate;
        });
      default:
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        return invoices.filter(inv => {
          const invDate = new Date(inv.invoice_date);
          return invDate >= startDate;
        });
    }
  };
  
  // Filter invoices by specific year and month
  const getInvoicesByYearMonth = (year, month) => {
    return invoices.filter(inv => {
      if (!inv.invoice_date) return false;
      const invDate = new Date(inv.invoice_date);
      return invDate.getFullYear() === year && (invDate.getMonth() + 1) === month;
    });
  };
  
  // Filter invoices by specific year only
  const getInvoicesByYear = (year) => {
    return invoices.filter(inv => {
      if (!inv.invoice_date) return false;
      const invDate = new Date(inv.invoice_date);
      return invDate.getFullYear() === year;
    });
  };
  
  // Calculate total for filtered invoices
  const getFilteredTotal = (period) => {
    const filtered = getFilteredInvoices(period);
    return filtered.reduce((sum, inv) => sum + (inv.total || 0), 0);
  };
  
  // Calculate total for year/month
  const getYearMonthTotal = (year, month) => {
    const filtered = getInvoicesByYearMonth(year, month);
    return filtered.reduce((sum, inv) => sum + (inv.total || 0), 0);
  };
  
  // Calculate total for year
  const getYearTotal = (year) => {
    const filtered = getInvoicesByYear(year);
    return filtered.reduce((sum, inv) => sum + (inv.total || 0), 0);
  };
  
  // Export invoices to CSV
  const exportInvoicesToCSV = (period) => {
    const filtered = getFilteredInvoices(period);
    if (filtered.length === 0) {
      toast.error('No invoices found for this period');
      return;
    }
    
    const headers = ['Invoice Number', 'Date', 'Customer Name', 'Customer Email', 'Subtotal', 'Shipping', 'Tax', 'Total', 'Payment Method'];
    const rows = filtered.map(inv => [
      inv.invoice_number,
      inv.invoice_date,
      inv.customer_name,
      inv.customer_email,
      inv.subtotal?.toFixed(2) || '0.00',
      inv.shipping?.toFixed(2) || '0.00',
      inv.tax_amount?.toFixed(2) || '0.00',
      inv.total?.toFixed(2) || '0.00',
      inv.payment_method || ''
    ]);
    
    // Add total row
    const total = filtered.reduce((sum, inv) => sum + (inv.total || 0), 0);
    rows.push(['', '', '', 'TOTAL:', '', '', '', total.toFixed(2), '']);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    
    const periodLabel = period === 'week' ? 'last_week' : period === 'month' ? 'last_month' : 'last_year';
    link.download = `invoices_${periodLabel}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success(`Exported ${filtered.length} invoices`);
  };
  
  // Export invoices by year/month to CSV
  const exportByYearMonth = (year, month) => {
    const filtered = getInvoicesByYearMonth(year, month);
    if (filtered.length === 0) {
      toast.error('No invoices found for this period');
      return;
    }
    
    const headers = ['Invoice Number', 'Date', 'Customer Name', 'Customer Email', 'Subtotal', 'Shipping', 'Tax', 'Total', 'Payment Method'];
    const rows = filtered.map(inv => [
      inv.invoice_number,
      inv.invoice_date,
      inv.customer_name,
      inv.customer_email,
      inv.subtotal?.toFixed(2) || '0.00',
      inv.shipping?.toFixed(2) || '0.00',
      inv.tax_amount?.toFixed(2) || '0.00',
      inv.total?.toFixed(2) || '0.00',
      inv.payment_method || ''
    ]);
    
    const total = filtered.reduce((sum, inv) => sum + (inv.total || 0), 0);
    rows.push(['', '', '', 'TOTAL:', '', '', '', total.toFixed(2), '']);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    
    const monthName = monthNames.find(m => m.value === month)?.label.split(' / ')[0] || month;
    link.download = `invoices_${monthName}_${year}.csv`;
    link.click();
    
    toast.success(`Exported ${filtered.length} invoices`);
  };
  
  // Export all invoices for a year
  const exportByYear = (year) => {
    const filtered = getInvoicesByYear(year);
    if (filtered.length === 0) {
      toast.error('No invoices found for this year');
      return;
    }
    
    const headers = ['Invoice Number', 'Date', 'Customer Name', 'Customer Email', 'Subtotal', 'Shipping', 'Tax', 'Total', 'Payment Method'];
    const rows = filtered.map(inv => [
      inv.invoice_number,
      inv.invoice_date,
      inv.customer_name,
      inv.customer_email,
      inv.subtotal?.toFixed(2) || '0.00',
      inv.shipping?.toFixed(2) || '0.00',
      inv.tax_amount?.toFixed(2) || '0.00',
      inv.total?.toFixed(2) || '0.00',
      inv.payment_method || ''
    ]);
    
    const total = filtered.reduce((sum, inv) => sum + (inv.total || 0), 0);
    rows.push(['', '', '', 'TOTAL:', '', '', '', total.toFixed(2), '']);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `invoices_${year}_full_year.csv`;
    link.click();
    
    toast.success(`Exported ${filtered.length} invoices`);
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${API}/admin/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(invoices.filter(inv => inv.id !== id));
      toast.success('Invoice deleted');
    } catch (err) {
      toast.error('Failed to delete invoice');
    }
  };

  // Order management functions
  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(orders.filter(o => o.id !== orderId));
      toast.success('Order deleted');
    } catch (err) {
      toast.error('Failed to delete order');
    }
  };

  const openEditOrder = (order) => {
    setEditingOrder({ ...order });
    setShowEditOrderModal(true);
  };

  const saveEditedOrder = async () => {
    if (!editingOrder) return;
    try {
      const res = await axios.put(`${API}/admin/orders/${editingOrder.id}`, editingOrder, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(orders.map(o => o.id === editingOrder.id ? res.data : o));
      setShowEditOrderModal(false);
      setEditingOrder(null);
      toast.success('Order updated');
    } catch (err) {
      toast.error('Failed to update order');
    }
  };

  // Select a client and fill invoice data
  const selectClientForInvoice = (client) => {
    setInvoiceData(prev => ({
      ...prev,
      customer_name: client.name || prev.customer_name,
      customer_email: client.email || prev.customer_email,
      customer_phone: client.phone || prev.customer_phone,
      customer_address: client.address || prev.customer_address,
      customer_vat: client.vat || prev.customer_vat,
      customer_codice_fiscale: client.codice_fiscale || prev.customer_codice_fiscale,
    }));
    setShowClientSelector(false);
    toast.success('Client data loaded');
  };

  // Client CRUD functions
  const openEditClient = (client) => {
    setEditingClient({ ...client });
    setShowEditClientModal(true);
  };

  const saveClient = async () => {
    if (!editingClient) return;
    try {
      await axios.put(`${API}/admin/clients/${editingClient.id}`, editingClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh clients list
      const res = await axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(res.data);
      setShowEditClientModal(false);
      setEditingClient(null);
      toast.success('Client saved!');
    } catch (err) {
      toast.error('Failed to save client');
    }
  };

  const addNewClient = async () => {
    if (!newClient.name && !newClient.email && !newClient.phone) {
      toast.error('Please fill at least name, email, or phone');
      return;
    }
    try {
      await axios.post(`${API}/admin/clients`, newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh clients list
      const res = await axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(res.data);
      setShowAddClientModal(false);
      setNewClient({ name: '', email: '', phone: '', address: '', vat: '', codice_fiscale: '' });
      toast.success('Client added!');
    } catch (err) {
      toast.error('Failed to add client');
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await axios.delete(`${API}/admin/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(clients.filter(c => c.id !== clientId));
      toast.success('Client deleted');
    } catch (err) {
      toast.error('Failed to delete client');
    }
  };

  // ============ EXPENSE FUNCTIONS ============
  const loadExpenses = async () => {
    const params = new URLSearchParams();
    if (expenseFilterStart) params.append('start_date', expenseFilterStart);
    if (expenseFilterEnd) params.append('end_date', expenseFilterEnd);
    if (expenseFilterCat) params.append('category', expenseFilterCat);
    const res = await axios.get(`${API}/admin/expenses?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    setExpenses(res.data);
  };

  const saveExpense = async () => {
    if (!newExpense.description || !newExpense.amount || !newExpense.date) {
      toast.error('Compilare descrizione, importo e data');
      return;
    }
    try {
      const payload = { ...newExpense, amount: parseFloat(newExpense.amount) };
      if (editingExpense) {
        const res = await axios.put(`${API}/admin/expenses/${editingExpense.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setExpenses(expenses.map(e => e.id === editingExpense.id ? res.data : e));
        toast.success('Spesa aggiornata');
      } else {
        const res = await axios.post(`${API}/admin/expenses`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setExpenses([res.data, ...expenses]);
        toast.success('Spesa aggiunta');
      }
      setShowExpenseModal(false);
      setEditingExpense(null);
      setNewExpense({ description: '', amount: '', date: new Date().toISOString().split('T')[0], category: '', notes: '', attachment_url: '', attachment_filename: '' });
      loadExpenseTotals(totalsYear, totalsMonth || null);
    } catch (err) {
      toast.error('Errore nel salvare la spesa');
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Eliminare questa spesa?')) return;
    try {
      await axios.delete(`${API}/admin/expenses/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setExpenses(expenses.filter(e => e.id !== id));
      toast.success('Spesa eliminata');
      loadExpenseTotals(totalsYear, totalsMonth || null);
    } catch (err) {
      toast.error('Errore');
    }
  };

  const uploadExpenseAttachment = async (file) => {
    setUploadingExpenseFile(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/expenses/upload-attachment`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setNewExpense(prev => ({ ...prev, attachment_url: res.data.url, attachment_filename: res.data.filename }));
      toast.success('File caricato');
    } catch (err) {
      toast.error('Errore upload: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingExpenseFile(false);
    }
  };

  const extractFromInvoice = async (file) => {
    setExtractingInvoice(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      toast.info('Analizzo la fattura con AI...');
      const res = await axios.post(`${API}/admin/expenses/extract-from-file`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      const d = res.data;
      setNewExpense(prev => ({
        ...prev,
        attachment_url: d.url,
        attachment_filename: d.filename,
        ...(d.extracted && d.amount ? { amount: d.amount } : {}),
        ...(d.extracted && d.date ? { date: d.date } : {}),
        ...(d.extracted && d.description ? { description: d.description } : {}),
      }));
      if (d.extracted) {
        let msg = 'Dati estratti dalla fattura!';
        if (d.currency && d.currency !== 'EUR') {
          msg += ` (${d.original_amount} ${d.currency} → €${d.amount} al cambio ${d.exchange_rate})`;
        }
        toast.success(msg);
      } else {
        toast.warning('File caricato ma non ho potuto estrarre i dati: ' + (d.error || 'formato non riconosciuto'));
      }
    } catch (err) {
      toast.error('Errore AI: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExtractingInvoice(false);
    }
  };

  const loadExpenseTotals = async (year, month) => {
    try {
      const params = new URLSearchParams({ year: year.toString() });
      if (month) params.append('month', month.toString());
      const res = await axios.get(`${API}/admin/expenses/totals?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setExpenseTotals(res.data);
    } catch (err) {
      console.error('Failed to load totals');
    }
  };

  const exportExpenses = async (format) => {
    if (!expenseFilterStart || !expenseFilterEnd) {
      toast.error('Seleziona il periodo (data inizio e fine)');
      return;
    }
    try {
      const params = new URLSearchParams({ start_date: expenseFilterStart, end_date: expenseFilterEnd, format });
      if (expenseFilterCat) params.append('category', expenseFilterCat);
      const res = await axios.get(`${API}/admin/expenses/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const prefix = reportLanguage === 'ro' ? 'cheltuieli' : reportLanguage === 'en' ? 'expenses' : 'spese';
      link.download = `${prefix}_${expenseFilterStart}_${expenseFilterEnd}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} descarcat`);
    } catch (err) {
      toast.error('Errore export');
    }
  };

  const saveExpenseCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      if (editingExpCat) {
        await axios.put(`${API}/admin/expense-categories/${editingExpCat.id}`, { name: newCategoryName }, { headers: { Authorization: `Bearer ${token}` } });
        setExpenseCategories(expenseCategories.map(c => c.id === editingExpCat.id ? { ...c, name: newCategoryName } : c));
        toast.success('Categoria aggiornata');
      } else {
        const res = await axios.post(`${API}/admin/expense-categories`, { name: newCategoryName }, { headers: { Authorization: `Bearer ${token}` } });
        setExpenseCategories([...expenseCategories, res.data]);
        toast.success('Categoria creata');
      }
      setNewCategoryName('');
      setEditingExpCat(null);
    } catch (err) {
      toast.error('Errore');
    }
  };

  const deleteExpenseCategory = async (id) => {
    if (!window.confirm('Eliminare questa categoria?')) return;
    try {
      await axios.delete(`${API}/admin/expense-categories/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setExpenseCategories(expenseCategories.filter(c => c.id !== id));
      toast.success('Categoria eliminata');
    } catch (err) {
      toast.error('Errore');
    }
  };

  const sendInvoiceEmail = async (id) => {
    setSendingInvoice(true);
    try {
      await axios.post(`${API}/admin/invoices/${id}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice sent to customer!');
    } catch (err) {
      toast.error('Failed to send invoice');
    } finally {
      setSendingInvoice(false);
    }
  };

  const downloadInvoicePDF = async (invoice) => {
    const isItalian = invoice.language === 'it';
    const qrCodeUrl = `${API}/invoice/${invoice.id}/qr`;
    const digitalInvoiceUrl = `${API}/invoice/${invoice.id}?lang=${invoice.language || 'en'}`;
    
    // Fetch QR code and convert to base64
    let qrBase64 = '';
    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      qrBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.error('Failed to load QR code:', e);
    }
    
    const taxRegimeText = isItalian 
      ? 'Regime forfettario – operazione non soggetta a IVA ai sensi dell\'art. 1, commi da 54 a 89, L. 190/2014'
      : 'Flat-rate scheme – transaction not subject to VAT pursuant to art. 1, paragraphs 54-89, Law 190/2014';
    
    const paymentTermsText = invoice.payment_terms 
      ? (isItalian 
          ? `Termini di pagamento: ${invoice.payment_terms} giorni dalla data della fattura`
          : `Payment terms: ${invoice.payment_terms} days from invoice date`)
      : '';
    
    const getPaymentMethodText = () => {
      if (!invoice.payment_method) return '';
      const methods = {
        stripe: isItalian 
          ? 'Pagamento effettuato tramite carta Debito/Credito con Stripe'
          : 'Payment made via Debit/Credit card with Stripe',
        bank_transfer: isItalian
          ? 'Pagamento ricevuto tramite bonifico bancario'
          : 'Payment received via bank transfer',
        cash: isItalian
          ? 'Pagamento in contanti alla consegna'
          : 'Cash payment on delivery'
      };
      return methods[invoice.payment_method] || '';
    };
    const paymentMethodText = getPaymentMethodText();
    
    const itemsRows = invoice.items.map(item => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #eee;">${item.description}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">€${item.unit_price.toFixed(2)}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">€${item.total.toFixed(2)}</td>
      </tr>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${isItalian ? 'Fattura' : 'Invoice'} ${invoice.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
          .header { border-bottom: 3px solid #d4af37; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { margin: 0; color: #333; }
          .header p { color: #666; margin: 5px 0; }
          .parties { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .party h3 { color: #d4af37; margin-bottom: 10px; }
          .party p { margin: 0; line-height: 1.6; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: #f5f5f5; padding: 12px; text-align: left; border-bottom: 2px solid #d4af37; }
          .totals { text-align: right; margin-top: 20px; }
          .totals p { margin: 5px 0; }
          .grand-total { font-size: 1.2em; border-top: 2px solid #d4af37; padding-top: 10px; margin-top: 10px; }
          .notes { margin-top: 30px; padding: 15px; background: #f9f9f9; border-left: 4px solid #d4af37; }
          .tax-regime { margin-top: 30px; padding: 15px; background: #fff9e6; border: 1px solid #d4af37; font-size: 11px; font-style: italic; }
          .payment-terms { margin-top: 15px; font-weight: bold; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; }
          .qr-section { margin-top: 40px; padding: 20px; background: linear-gradient(135deg, #22c55e, #16a34a); border-radius: 10px; display: flex; align-items: center; gap: 20px; }
          .qr-section img { width: 100px; height: 100px; background: white; padding: 5px; border-radius: 5px; }
          .qr-text { color: white; }
          .qr-text h4 { margin: 0 0 5px 0; font-size: 14px; }
          .qr-text p { margin: 0; font-size: 11px; opacity: 0.9; }
          @media print { body { margin: 20px; } .qr-section { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${isItalian ? 'FATTURA' : 'INVOICE'}</h1>
          <p>#${invoice.invoice_number}</p>
        </div>
        
        <div class="parties">
          <div class="party">
            <h3>${isItalian ? 'Da' : 'From'}:</h3>
            <p>
              <strong>CANDALIA ROXANA ELENA</strong><br>
              Viale Martiri della Resistenza 33<br>
              Spoleto 06049<br>
              Italia<br>
              Partita IVA: IT04014360541
            </p>
          </div>
          <div class="party">
            <h3>${isItalian ? 'A' : 'To'}:</h3>
            <p>
              <strong>${invoice.customer_name}</strong><br>
              ${invoice.customer_address ? invoice.customer_address.replace(/\n/g, '<br>') + '<br>' : ''}
              ${invoice.customer_phone ? `${isItalian ? 'Tel' : 'Phone'}: ${invoice.customer_phone}<br>` : ''}
              ${invoice.customer_email}
              ${invoice.customer_codice_fiscale ? `<br>${isItalian ? 'Codice Fiscale' : 'Tax Code'}: ${invoice.customer_codice_fiscale}` : ''}
              ${invoice.customer_vat ? `<br>${isItalian ? 'Partita IVA' : 'VAT'}: ${invoice.customer_vat}` : ''}
            </p>
          </div>
        </div>
        
        <p><strong>${isItalian ? 'Data' : 'Date'}:</strong> ${invoice.invoice_date}</p>
        ${paymentTermsText ? `<p class="payment-terms">${paymentTermsText}</p>` : ''}
        ${paymentMethodText ? `<p class="payment-method">${paymentMethodText}</p>` : ''}
        
        <table>
          <thead>
            <tr>
              <th>${isItalian ? 'Descrizione' : 'Description'}</th>
              <th style="text-align: center;">${isItalian ? 'Qtà' : 'Qty'}</th>
              <th style="text-align: right;">${isItalian ? 'Prezzo' : 'Price'}</th>
              <th style="text-align: right;">${isItalian ? 'Totale' : 'Total'}</th>
            </tr>
          </thead>
          <tbody>
            ${itemsRows}
          </tbody>
        </table>
        
        <div class="totals">
          <p><strong>${isItalian ? 'Subtotale' : 'Subtotal'}:</strong> €${invoice.subtotal.toFixed(2)}</p>
          ${invoice.shipping ? `<p><strong>${isItalian ? 'Spedizione' : 'Shipping'}:</strong> €${invoice.shipping.toFixed(2)}</p>` : ''}
          ${invoice.tax_amount ? `<p><strong>${isItalian ? 'IVA' : 'Tax'} (${invoice.tax_rate}%):</strong> €${invoice.tax_amount.toFixed(2)}</p>` : ''}
          <p class="grand-total"><strong>${isItalian ? 'TOTALE' : 'TOTAL'}:</strong> €${invoice.total.toFixed(2)}</p>
        </div>
        
        <div class="tax-regime">${taxRegimeText}</div>
        
        ${invoice.notes ? `<div class="notes"><h4 style="margin: 0 0 10px 0;">${isItalian ? 'Note' : 'Notes'}:</h4><p style="margin: 0;">${invoice.notes}</p></div>` : ''}
        
        <div class="qr-section">
          ${qrBase64 ? `<img src="${qrBase64}" alt="QR Code" />` : '<div style="width:100px;height:100px;background:#fff;display:flex;align-items:center;justify-content:center;border-radius:5px;font-size:10px;color:#666;">QR Code</div>'}
          <div class="qr-text">
            <h4>✓ ${isItalian ? 'DOCUMENTO FISCALE ELETTRONICO ORIGINALE' : 'ORIGINAL ELECTRONIC TAX DOCUMENT'}</h4>
            <p>${isItalian ? 'Scansiona il QR code per verificare l\'autenticità di questa fattura' : 'Scan the QR code to verify the authenticity of this invoice'}</p>
            <p style="margin-top: 5px; font-size: 10px; opacity: 0.7;">ID: ${invoice.id}</p>
          </div>
        </div>
        
        <p class="footer">${isItalian ? 'Grazie per il vostro acquisto!' : 'Thank you for your purchase!'}</p>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Preview invoice without auto-print
  const previewInvoice = (invoice) => {
    const isItalian = invoice.language === 'it';
    
    const taxRegimeText = isItalian 
      ? 'Regime forfettario – operazione non soggetta a IVA ai sensi dell\'art. 1, commi da 54 a 89, L. 190/2014'
      : 'Flat-rate scheme – transaction not subject to VAT pursuant to art. 1, paragraphs 54-89, Law 190/2014';
    
    const paymentTermsText = invoice.payment_terms 
      ? (isItalian 
          ? `Termini di pagamento: ${invoice.payment_terms} giorni dalla data della fattura`
          : `Payment terms: ${invoice.payment_terms} days from invoice date`)
      : '';
    
    const getPaymentMethodText = () => {
      if (!invoice.payment_method) return '';
      const methods = {
        stripe: isItalian 
          ? 'Pagamento effettuato tramite carta Debito/Credito con Stripe'
          : 'Payment made via Debit/Credit card with Stripe',
        bank_transfer: isItalian
          ? 'Pagamento ricevuto tramite bonifico bancario'
          : 'Payment received via bank transfer',
        cash: isItalian
          ? 'Pagamento in contanti alla consegna'
          : 'Cash payment on delivery'
      };
      return methods[invoice.payment_method] || '';
    };
    const paymentMethodText = getPaymentMethodText();
    
    const itemsRows = invoice.items.map(item => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #eee;">${item.description}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">€${item.unit_price.toFixed(2)}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">€${item.total.toFixed(2)}</td>
      </tr>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${isItalian ? 'Fattura' : 'Invoice'} ${invoice.invoice_number} - Preview</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; color: #333; background: #f0f0f0; }
          .preview-toolbar { 
            position: fixed; top: 0; left: 0; right: 0; 
            background: #333; color: white; padding: 12px 20px; 
            display: flex; justify-content: space-between; align-items: center;
            z-index: 1000; box-shadow: 0 2px 10px rgba(0,0,0,0.3);
          }
          .preview-toolbar h3 { margin: 0; font-size: 16px; }
          .preview-toolbar button {
            background: #d4af37; color: #333; border: none; padding: 8px 20px;
            border-radius: 4px; cursor: pointer; font-weight: bold; margin-left: 10px;
          }
          .preview-toolbar button:hover { background: #c49f2f; }
          .preview-toolbar button.secondary { background: #666; color: white; }
          .preview-toolbar button.secondary:hover { background: #555; }
          .invoice-container { 
            max-width: 800px; margin: 80px auto 40px; padding: 40px; 
            background: white; box-shadow: 0 2px 20px rgba(0,0,0,0.15); 
          }
          .header { border-bottom: 3px solid #d4af37; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { margin: 0; color: #333; }
          .header p { color: #666; margin: 5px 0; }
          .parties { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .party h3 { color: #d4af37; margin-bottom: 10px; }
          .party p { margin: 0; line-height: 1.6; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: #f5f5f5; padding: 12px; text-align: left; border-bottom: 2px solid #d4af37; }
          .totals { text-align: right; margin-top: 20px; }
          .totals p { margin: 5px 0; }
          .grand-total { font-size: 1.2em; border-top: 2px solid #d4af37; padding-top: 10px; margin-top: 10px; }
          .notes { margin-top: 30px; padding: 15px; background: #f9f9f9; border-left: 4px solid #d4af37; }
          .tax-regime { margin-top: 30px; padding: 15px; background: #fff9e6; border: 1px solid #d4af37; font-size: 11px; font-style: italic; }
          .payment-terms { margin-top: 15px; font-weight: bold; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; }
          @media print { 
            .preview-toolbar { display: none; }
            body { background: white; margin: 20px; }
            .invoice-container { margin: 0; padding: 0; box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="preview-toolbar">
          <h3>📄 ${isItalian ? 'Anteprima Fattura' : 'Invoice Preview'}: #${invoice.invoice_number}</h3>
          <div>
            <button class="secondary" onclick="window.close()">✕ ${isItalian ? 'Chiudi' : 'Close'}</button>
            <button onclick="window.print()">🖨️ ${isItalian ? 'Stampa / Salva PDF' : 'Print / Save PDF'}</button>
          </div>
        </div>
        
        <div class="invoice-container">
          <div class="header">
            <h1>${isItalian ? 'FATTURA' : 'INVOICE'}</h1>
            <p>#${invoice.invoice_number}</p>
          </div>
          
          <div class="parties">
            <div class="party">
              <h3>${isItalian ? 'Da' : 'From'}:</h3>
              <p>
                <strong>CANDALIA ROXANA ELENA</strong><br>
                Viale Martiri della Resistenza 33<br>
                Spoleto 06049<br>
                Italia<br>
                Partita IVA: IT04014360541
              </p>
            </div>
            <div class="party">
              <h3>${isItalian ? 'A' : 'To'}:</h3>
              <p>
                <strong>${invoice.customer_name}</strong><br>
                ${invoice.customer_address ? invoice.customer_address.replace(/\n/g, '<br>') + '<br>' : ''}
                ${invoice.customer_phone ? `${isItalian ? 'Tel' : 'Phone'}: ${invoice.customer_phone}<br>` : ''}
                ${invoice.customer_email}
                ${invoice.customer_codice_fiscale ? `<br>${isItalian ? 'Codice Fiscale' : 'Tax Code'}: ${invoice.customer_codice_fiscale}` : ''}
                ${invoice.customer_vat ? `<br>${isItalian ? 'Partita IVA' : 'VAT'}: ${invoice.customer_vat}` : ''}
              </p>
            </div>
          </div>
          
          <p><strong>${isItalian ? 'Data' : 'Date'}:</strong> ${invoice.invoice_date}</p>
          ${paymentTermsText ? `<p class="payment-terms">${paymentTermsText}</p>` : ''}
          ${paymentMethodText ? `<p class="payment-method">${paymentMethodText}</p>` : ''}
          
          <table>
            <thead>
              <tr>
                <th>${isItalian ? 'Descrizione' : 'Description'}</th>
                <th style="text-align: center;">${isItalian ? 'Qtà' : 'Qty'}</th>
                <th style="text-align: right;">${isItalian ? 'Prezzo' : 'Price'}</th>
                <th style="text-align: right;">${isItalian ? 'Totale' : 'Total'}</th>
              </tr>
            </thead>
            <tbody>
              ${itemsRows}
            </tbody>
          </table>
          
          <div class="totals">
            <p><strong>${isItalian ? 'Subtotale' : 'Subtotal'}:</strong> €${invoice.subtotal.toFixed(2)}</p>
            ${invoice.shipping ? `<p><strong>${isItalian ? 'Spedizione' : 'Shipping'}:</strong> €${invoice.shipping.toFixed(2)}</p>` : ''}
            ${invoice.tax_amount ? `<p><strong>${isItalian ? 'IVA' : 'Tax'} (${invoice.tax_rate}%):</strong> €${invoice.tax_amount.toFixed(2)}</p>` : ''}
            <p class="grand-total"><strong>${isItalian ? 'TOTALE' : 'TOTAL'}:</strong> €${invoice.total.toFixed(2)}</p>
          </div>
          
          <div class="tax-regime">${taxRegimeText}</div>
          
          ${invoice.notes ? `<div class="notes"><h4 style="margin: 0 0 10px 0;">${isItalian ? 'Note' : 'Notes'}:</h4><p style="margin: 0;">${invoice.notes}</p></div>` : ''}
          
          <p class="footer">${isItalian ? 'Grazie per il vostro acquisto!' : 'Thank you for your purchase!'}</p>
        </div>
      </body>
      </html>
    `;
    
    const previewWindow = window.open('', '_blank');
    previewWindow.document.write(html);
    previewWindow.document.close();
  };

  const saveArtistImage = async () => {
    setSavingAbout(true);
    try {
      await axios.put(`${API}/admin/about-settings`, { 
        artist_image_url: artistImageUrl,
        about_title_en: aboutTitleEn,
        about_title_it: aboutTitleIt,
        about_content_en: aboutContentEn,
        about_content_it: aboutContentIt,
        video_enabled: videoEnabled,
        video_type: videoType,
        video_url: videoUrl,
        video_title_en: videoTitleEn,
        video_title_it: videoTitleIt
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Artist image saved!');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSavingAbout(false);
    }
  };

  const saveAboutContent = async () => {
    setSavingAbout(true);
    try {
      await axios.put(`${API}/admin/about-settings`, { 
        artist_image_url: artistImageUrl,
        about_title_en: aboutTitleEn,
        about_title_it: aboutTitleIt,
        about_content_en: aboutContentEn,
        about_content_it: aboutContentIt,
        video_enabled: videoEnabled,
        video_type: videoType,
        video_url: videoUrl,
        video_title_en: videoTitleEn,
        video_title_it: videoTitleIt
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('About content saved!');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSavingAbout(false);
    }
  };

  const saveVideoSettings = async () => {
    setSavingAbout(true);
    try {
      await axios.put(`${API}/admin/about-settings`, { 
        artist_image_url: artistImageUrl,
        about_title_en: aboutTitleEn,
        about_title_it: aboutTitleIt,
        about_content_en: aboutContentEn,
        about_content_it: aboutContentIt,
        video_enabled: videoEnabled,
        video_type: videoType,
        video_url: videoUrl,
        video_title_en: videoTitleEn,
        video_title_it: videoTitleIt
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video settings saved!');
    } catch (err) {
      toast.error('Failed to save video settings');
    } finally {
      setSavingAbout(false);
    }
  };

  const savePolicies = async () => {
    setSavingPolicies(true);
    try {
      await axios.put(`${API}/admin/policies`, policies, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Policies saved!');
    } catch (err) {
      toast.error('Failed to save policies');
    } finally {
      setSavingPolicies(false);
    }
  };

  const saveContactInfo = async () => {
    setSavingContact(true);
    try {
      await axios.put(`${API}/admin/contact-settings`, contactInfo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact info saved!');
    } catch (err) {
      toast.error('Failed to save contact info');
    } finally {
      setSavingContact(false);
    }
  };

  const deleteContactMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactMessages(contactMessages.filter(m => m.id !== id));
      toast.success('Message deleted');
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  const toggleMessageSelection = (id) => {
    setSelectedMessages(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedMessages.length === contactMessages.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(contactMessages.map(m => m.id));
    }
  };

  const addCritic = async () => {
    if (!newCritic.text || !newCritic.author) {
      toast.error('Please fill text and author');
      return;
    }
    setSavingAbout(true);
    try {
      const res = await axios.post(`${API}/admin/critics`, newCritic, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Critic added!');
      setCritics([...critics, res.data]);
      setIsAddingCritic(false);
      setNewCritic({ text: '', text_it: '', author: '', publication: '' });
    } catch (err) {
      toast.error('Failed to add critic');
    } finally {
      setSavingAbout(false);
    }
  };

  const updateCritic = async () => {
    if (!editingCritic) return;
    setSavingAbout(true);
    try {
      const { text, text_it, author, publication } = editingCritic;
      await axios.put(`${API}/admin/critics/${editingCritic.id}`, { text, text_it, author, publication }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Critic updated!');
      setCritics(critics.map(c => c.id === editingCritic.id ? editingCritic : c));
      setEditingCritic(null);
    } catch (err) {
      toast.error('Failed to update critic');
    } finally {
      setSavingAbout(false);
    }
  };

  const deleteCritic = async (id, author) => {
    if (!window.confirm(`Delete review by "${author}"?`)) return;
    try {
      await axios.delete(`${API}/admin/critics/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Critic deleted!');
      setCritics(critics.filter(c => c.id !== id));
    } catch (err) {
      toast.error('Failed to delete critic');
    }
  };

  // Exhibition functions
  const addExhibition = async () => {
    if (!newExhibition.title || !newExhibition.location) {
      toast.error('Please fill title and location');
      return;
    }
    setSavingAbout(true);
    try {
      const res = await axios.post(`${API}/admin/exhibitions`, newExhibition, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Exhibition added!');
      setExhibitions([...exhibitions, res.data]);
      setNewExhibition({ title: '', title_it: '', location: '', date: '', description: '', description_it: '' });
      setIsAddingExhibition(false);
    } catch (err) {
      toast.error('Failed to add exhibition');
    } finally {
      setSavingAbout(false);
    }
  };

  const updateExhibition = async () => {
    if (!editingExhibition) return;
    setSavingAbout(true);
    try {
      const { title, title_it, location, date, description, description_it } = editingExhibition;
      await axios.put(`${API}/admin/exhibitions/${editingExhibition.id}`, { title, title_it, location, date, description, description_it }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Exhibition updated!');
      setExhibitions(exhibitions.map(e => e.id === editingExhibition.id ? editingExhibition : e));
      setEditingExhibition(null);
    } catch (err) {
      toast.error('Failed to update exhibition');
    } finally {
      setSavingAbout(false);
    }
  };

  const deleteExhibition = async (id, title) => {
    if (!window.confirm(`Delete exhibition "${title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/exhibitions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Exhibition deleted!');
      setExhibitions(exhibitions.filter(e => e.id !== id));
    } catch (err) {
      toast.error('Failed to delete exhibition');
    }
  };

  const sendNewsletter = async () => {
    if (!newsletter.subject || !newsletter.content) {
      toast.error('Please fill subject and content');
      return;
    }
    setSendingNewsletter(true);
    try {
      const res = await axios.post(`${API}/admin/newsletter/send`, newsletter, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setNewsletter({ subject: '', content: '', image_url: '', image_width: 100 });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to send newsletter';
      toast.error(errorMsg);
    } finally {
      setSendingNewsletter(false);
    }
  };

  // Import subscribers from clients
  const importSubscribersFromClients = async () => {
    setImportingFromClients(true);
    try {
      // Get clients with email addresses
      const clientEmails = clients
        .filter(c => c.email && c.email.includes('@'))
        .map(c => c.email.toLowerCase());
      
      // Get existing subscriber emails
      const existingEmails = subscribers.map(s => s.email.toLowerCase());
      
      // Find new emails to add
      const newEmails = clientEmails.filter(email => !existingEmails.includes(email));
      
      if (newEmails.length === 0) {
        toast.info('All client emails are already subscribed!');
        return;
      }
      
      // Add each new email
      let addedCount = 0;
      for (const email of newEmails) {
        try {
          await axios.post(`${API}/admin/newsletter/add`, { email }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          addedCount++;
        } catch (e) {
          // Skip if already exists
        }
      }
      
      // Refresh subscribers list
      const res = await axios.get(`${API}/admin/newsletter`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(res.data);
      
      toast.success(`✅ Imported ${addedCount} new subscribers from clients!`);
    } catch (err) {
      toast.error('Failed to import subscribers');
    } finally {
      setImportingFromClients(false);
    }
  };

  const deleteSubscriber = async (email) => {
    if (!window.confirm(`Delete subscriber ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(subscribers.filter(s => s.email !== email));
      setSelectedSubscribers(selectedSubscribers.filter(e => e !== email));
      toast.success('Subscriber deleted');
    } catch (err) {
      toast.error('Failed to delete subscriber');
    }
  };

  // Delete multiple subscribers
  const deleteSelectedSubscribers = async () => {
    if (selectedSubscribers.length === 0) return;
    if (!window.confirm(`Delete ${selectedSubscribers.length} selected subscribers?`)) return;
    
    setDeletingSubscribers(true);
    try {
      let deletedCount = 0;
      for (const email of selectedSubscribers) {
        try {
          await axios.delete(`${API}/admin/newsletter/${encodeURIComponent(email)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          deletedCount++;
        } catch (e) {}
      }
      setSubscribers(subscribers.filter(s => !selectedSubscribers.includes(s.email)));
      setSelectedSubscribers([]);
      toast.success(`✅ Deleted ${deletedCount} subscribers`);
    } catch (err) {
      toast.error('Failed to delete subscribers');
    } finally {
      setDeletingSubscribers(false);
    }
  };

  // Toggle subscriber selection
  const toggleSubscriberSelection = (email) => {
    setSelectedSubscribers(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email) 
        : [...prev, email]
    );
  };

  // Select all visible subscribers
  const selectAllSubscribers = () => {
    const visibleEmails = subscribers
      .filter(sub => sub.email.toLowerCase().includes(subscriberSearch.toLowerCase()))
      .map(s => s.email);
    
    if (selectedSubscribers.length === visibleEmails.length) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(visibleEmails);
    }
  };

  const savePainting = async () => {
    if (!editingPainting) return;
    setSavingPainting(true);
    try {
      await axios.put(`${API}/paintings/${editingPainting.id}`, editingPainting, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Painting updated!');
      setPaintings(paintings.map(p => p.id === editingPainting.id ? editingPainting : p));
      setEditingPainting(null);
    } catch (err) {
      toast.error('Failed to update painting');
    } finally {
      setSavingPainting(false);
    }
  };

  const addPainting = async () => {
    if (!newPainting.title || !newPainting.price || !newPainting.image_url) {
      toast.error('Please fill title, price and image URL');
      return;
    }
    setSavingPainting(true);
    try {
      const res = await axios.post(`${API}/paintings`, newPainting, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Painting added!');
      setPaintings([...paintings, res.data]);
      setIsAddingNew(false);
      setNewPainting({
        title: '', title_it: '', description: '', description_it: '',
        price: 0, dimensions: '', year: 2024, category: 'Portrait',
        image_url: '', additional_images: [], is_sold: false, is_featured: false, is_pinned: false, is_promotion: false, is_unique_art: false, shipping_cost: 0
      });
    } catch (err) {
      toast.error('Failed to add painting');
    } finally {
      setSavingPainting(false);
    }
  };

  // Image upload handler
  const uploadImage = async (file, isMain = true, forEditing = false) => {
    if (!file) return;
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // Construct full URL for the uploaded image
      const imageUrl = `${BACKEND_URL}${res.data.url}`;
      console.log('Uploaded image URL:', imageUrl);
      
      if (forEditing) {
        if (isMain) {
          setEditingPainting(prev => ({ ...prev, image_url: imageUrl }));
        } else {
          setEditingPainting(prev => ({ 
            ...prev, 
            additional_images: [...(prev.additional_images || []), imageUrl] 
          }));
        }
      } else {
        if (isMain) {
          setNewPainting(prev => ({ ...prev, image_url: imageUrl }));
        } else {
          setNewPainting(prev => ({ 
            ...prev, 
            additional_images: [...(prev.additional_images || []), imageUrl] 
          }));
        }
      }
      toast.success('Image uploaded!');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Set image as main
  const setAsMainImage = (imageUrl, forEditing = false) => {
    if (forEditing) {
      const currentMain = editingPainting.image_url;
      const additionalImages = editingPainting.additional_images || [];
      // Swap: move current main to additional, set selected as main
      const newAdditional = additionalImages.filter(img => img !== imageUrl);
      if (currentMain) newAdditional.push(currentMain);
      setEditingPainting(prev => ({
        ...prev,
        image_url: imageUrl,
        additional_images: newAdditional
      }));
    } else {
      const currentMain = newPainting.image_url;
      const additionalImages = newPainting.additional_images || [];
      const newAdditional = additionalImages.filter(img => img !== imageUrl);
      if (currentMain) newAdditional.push(currentMain);
      setNewPainting(prev => ({
        ...prev,
        image_url: imageUrl,
        additional_images: newAdditional
      }));
    }
    toast.success('Main image updated!');
  };

  const removeAdditionalImage = (index, forEditing = false) => {
    if (forEditing) {
      setEditingPainting(prev => ({
        ...prev,
        additional_images: prev.additional_images.filter((_, i) => i !== index)
      }));
    } else {
      setNewPainting(prev => ({
        ...prev,
        additional_images: prev.additional_images.filter((_, i) => i !== index)
      }));
    }
  };

  const deletePainting = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await axios.delete(`${API}/paintings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Painting deleted!');
      setPaintings(paintings.filter(p => p.id !== id));
    } catch (err) {
      toast.error('Failed to delete painting');
    }
  };

  if (!user?.is_admin) return null;

  return (
    <div className="pt-32 pb-24" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h1 className="font-heading text-4xl tracking-tight mb-8">Admin Dashboard</h1>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="md:w-48 flex-shrink-0">
            <div className="flex flex-col gap-2">
              {['dashboard', 'paintings', 'categories', 'about', 'contact', 'policies', 'shipping', 'newsletter', 'messages', 'seo', 'theme', 'settings', 'deploy', 'orders', 'clients', 'expenses'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 text-sm font-body uppercase tracking-wider text-left rounded ${
                    activeTab === tab ? 'bg-foreground text-background' : 'bg-muted hover:bg-muted/80'
                  }`}
                  data-testid={`admin-tab-${tab}`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === 'dashboard' && stats && (
          <div>
            <div className="grid md:grid-cols-6 gap-4 mb-6">
              <div 
                className="bg-muted p-5 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setActiveTab('paintings')}
              >
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Paintings</span>
                <p className="font-heading text-3xl mt-2">{stats.total_paintings}</p>
                <p className="text-xs text-muted-foreground mt-1">🎨 Click to manage</p>
              </div>
              <div 
                className="bg-muted p-5 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setActiveTab('paintings')}
              >
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Available</span>
                <p className="font-heading text-3xl mt-2">{stats.available_paintings}</p>
                <p className="text-xs text-muted-foreground mt-1">🖼️ Click to view</p>
              </div>
              <div 
                className="bg-muted p-5 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setActiveTab('orders')}
              >
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Revenue</span>
                <p className="font-heading text-3xl mt-2">€{stats.total_revenue.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">💰 Click for orders</p>
              </div>
              <div 
                className="bg-muted p-5 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setActiveTab('newsletter')}
              >
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Newsletter</span>
                <p className="font-heading text-3xl mt-2">{stats.newsletter_subscribers}</p>
                <p className="text-xs text-muted-foreground mt-1">📧 Click to manage</p>
              </div>
              <div 
                className={`p-5 cursor-pointer hover:opacity-80 transition-opacity ${orders.filter(o => o.status === 'pending' || o.payment_status === 'pending' || o.payment_status === 'awaiting_payment').length > 0 ? 'bg-orange-100 dark:bg-orange-950/30 border-2 border-orange-500' : 'bg-muted'}`}
                onClick={() => setActiveTab('orders')}
              >
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Orders</span>
                <p className="font-heading text-3xl mt-2">
                  {orders.filter(o => o.status === 'pending' || o.payment_status === 'pending' || o.payment_status === 'awaiting_payment').length > 0 ? (
                    <span className="text-orange-600">{orders.filter(o => o.status === 'pending' || o.payment_status === 'pending' || o.payment_status === 'awaiting_payment').length} NEW</span>
                  ) : (
                    <span>{orders.length}</span>
                  )}
                </p>
                {orders.filter(o => o.status === 'pending' || o.payment_status === 'pending' || o.payment_status === 'awaiting_payment').length > 0 && (
                  <p className="text-xs text-orange-600 mt-1">📦 Click to view</p>
                )}
              </div>
              <div 
                className={`p-5 cursor-pointer hover:opacity-80 transition-opacity ${messages.filter(m => !m.is_read).length > 0 ? 'bg-red-100 dark:bg-red-950/30 border-2 border-red-500' : 'bg-muted'}`}
                onClick={() => setActiveTab('messages')}
              >
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Messages</span>
                <p className="font-heading text-3xl mt-2">
                  {messages.filter(m => !m.is_read).length > 0 ? (
                    <span className="text-red-600">{messages.filter(m => !m.is_read).length} NEW</span>
                  ) : (
                    <span>{messages.length}</span>
                  )}
                </p>
                {messages.filter(m => !m.is_read).length > 0 && (
                  <p className="text-xs text-red-600 mt-1">📬 Click to view</p>
                )}
              </div>
            </div>
            
            {/* Active Carts Section */}
            <div className="border rounded-lg p-4 bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-950/20 dark:to-yellow-950/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🛒</span>
                  <h3 className="font-heading text-lg">Active Shopping Carts</h3>
                  {activeCarts.length > 0 && (
                    <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                      {activeCarts.length} active
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={fetchActiveCarts}
                    variant="outline"
                    size="sm"
                  >
                    🔄 Refresh
                  </Button>
                  <Button 
                    onClick={() => setShowActiveCarts(!showActiveCarts)}
                    variant="outline"
                  >
                    {showActiveCarts ? 'Hide' : '👁️ View'}
                  </Button>
                </div>
              </div>
              
              {/* Always show summary */}
              {activeCarts.length > 0 && !showActiveCarts && (
                <p className="text-sm text-muted-foreground">
                  {activeCarts.length} visitor(s) with items in cart. Click "View" to see details.
                </p>
              )}
              
              {showActiveCarts && (
                <div className="space-y-3">
                  {loadingCarts ? (
                    <p className="text-muted-foreground">Loading...</p>
                  ) : activeCarts.length === 0 ? (
                    <p className="text-muted-foreground">No active carts at the moment</p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground mb-3">
                        {activeCarts.length} visitor(s) with items in cart
                      </p>
                      {activeCarts.map((cart, idx) => (
                        <div key={idx} className={`bg-background border rounded-lg p-4 ${cart.is_bot ? 'border-red-500 border-2 bg-red-50' : cart.is_suspicious ? 'border-amber-500 border-2 bg-amber-50' : ''}`}>
                          {/* Bot/Scam Warning */}
                          {(cart.is_bot || cart.is_suspicious) && (
                            <div className={`mb-3 p-2 rounded text-sm flex items-center gap-2 ${cart.is_bot ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                              <span className="text-xl">{cart.is_bot ? '🤖' : '⚠️'}</span>
                              <span className="font-bold">
                                {cart.is_bot ? 'BOT DETECTED' : 'SUSPICIOUS'}
                              </span>
                              {cart.bot_reason && (
                                <span className="text-xs">- {cart.bot_reason}</span>
                              )}
                            </div>
                          )}
                          
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                {/* Country Flag - larger and with emoji fallback */}
                                {cart.country_code && cart.country_code !== 'UN' && cart.country_code !== '??' ? (
                                  <img 
                                    src={`https://flagcdn.com/32x24/${cart.country_code.toLowerCase()}.png`}
                                    alt={cart.country_name || cart.country_code}
                                    className="w-8 h-6 object-cover rounded shadow border"
                                    title={cart.country_name || cart.country_code}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <span className="text-xl">🌍</span>
                                )}
                                <p className="font-mono text-sm font-bold">IP: {cart.ip_address}</p>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                📍 {cart.country_name || 'Unknown'} • Last updated: {new Date(cart.updated_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-primary">€{cart.total_value?.toLocaleString()}</p>
                              <p className="text-xs text-muted-foreground">{cart.item_count} item(s)</p>
                            </div>
                          </div>
                          
                          {/* Cart Items */}
                          <div className="mt-3 space-y-2">
                            {cart.items?.map((item, i) => (
                              <div key={i} className="flex justify-between text-sm bg-muted/50 p-2 rounded">
                                <span>{item.title}</span>
                                <span className="font-medium">€{item.price?.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                          
                          {/* User Agent */}
                          <p className="text-xs text-muted-foreground mt-2 truncate" title={cart.user_agent}>
                            Browser: {cart.user_agent?.substring(0, 60)}...
                          </p>
                          
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 mt-2"
                            onClick={() => deleteActiveCart(cart.ip_address)}
                          >
                            🗑️ Remove
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-700 mt-2 ml-2 border-red-300 hover:bg-red-100"
                            onClick={async () => {
                              if (!window.confirm(`Blochezi IP ${cart.ip_address}?\n\nAcest IP nu va mai putea adăuga în coș.`)) return;
                              try {
                                await axios.post(`${API}/admin/block-ip/${cart.ip_address}`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setSecuritySettings(prev => ({
                                  ...prev,
                                  blocked_ips: [...(prev.blocked_ips || []), cart.ip_address]
                                }));
                                setActiveCarts(activeCarts.filter(c => c.ip_address !== cart.ip_address));
                                toast.success(`IP ${cart.ip_address} blocat!`);
                              } catch (err) {
                                toast.error('Eroare la blocare');
                              }
                            }}
                          >
                            🚫 Block IP
                          </Button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Visitors Section */}
            <div className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 mt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">👥</span>
                  <h3 className="font-heading text-lg">Website Visitors</h3>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={fetchVisitors}
                    variant="outline"
                    size="sm"
                  >
                    🔄 Refresh
                  </Button>
                  <Button 
                    onClick={() => setShowVisitors(!showVisitors)}
                    variant="outline"
                  >
                    {showVisitors ? 'Hide' : '👁️ View'}
                  </Button>
                </div>
              </div>
              
              {/* Visitor Stats Grid */}
              {visitorStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-background rounded-lg p-3 border text-center">
                    <p className="text-xs text-muted-foreground">Last 24 Hours</p>
                    <p className="text-2xl font-bold">{visitorStats.last_24h || 0}</p>
                    <p className="text-xs text-muted-foreground">{visitorStats.unique_24h || 0} unique</p>
                  </div>
                  <div className="bg-background rounded-lg p-3 border text-center">
                    <p className="text-xs text-muted-foreground">Last 30 Days</p>
                    <p className="text-2xl font-bold">{visitorStats.last_30d || 0}</p>
                    <p className="text-xs text-muted-foreground">{visitorStats.unique_30d || 0} unique</p>
                  </div>
                  <div className="bg-background rounded-lg p-3 border text-center">
                    <p className="text-xs text-muted-foreground">Last 60 Days</p>
                    <p className="text-2xl font-bold">{visitorStats.last_60d || 0}</p>
                    <p className="text-xs text-muted-foreground">{visitorStats.unique_60d || 0} unique</p>
                  </div>
                  <div className="bg-background rounded-lg p-3 border text-center">
                    <p className="text-xs text-muted-foreground">Last 180 Days</p>
                    <p className="text-2xl font-bold">{visitorStats.last_180d || 0}</p>
                    <p className="text-xs text-muted-foreground">{visitorStats.unique_180d || 0} unique</p>
                  </div>
                </div>
              )}
              
              {showVisitors && (
                <div className="space-y-3">
                  {loadingVisitors ? (
                    <p className="text-muted-foreground">Loading...</p>
                  ) : visitors.length === 0 ? (
                    <p className="text-muted-foreground">No visitors recorded yet</p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground mb-3">
                        Recent unique visitors (by IP)
                      </p>
                      <div className="max-h-96 overflow-auto space-y-2">
                        {visitors.map((visitor, idx) => (
                          <div key={idx} className="bg-background border rounded-lg p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {/* Device Icon */}
                              <span className="text-2xl">
                                {visitor.device_type === 'mobile' ? '📱' : '💻'}
                              </span>
                              {/* Country Flag */}
                              <span className="text-2xl" title={visitor.country_name}>
                                {getCountryFlag(visitor.country_code)}
                              </span>
                              <div>
                                <p className="font-mono text-sm">{visitor.ip_address}</p>
                                <p className="text-xs text-muted-foreground">
                                  {visitor.country_name} • {visitor.visit_count} visit(s)
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">
                                {new Date(visitor.last_visit).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(visitor.last_visit).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'policies' && (
          <div className="space-y-8">
            <h2 className="font-heading text-2xl mb-6">Edit Policies</h2>
            
            {/* Privacy Policy */}
            <div className="border border-border rounded-lg p-6">
              <h3 className="font-heading text-xl mb-4">Privacy Policy</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">English</label>
                  <Textarea
                    value={policies.privacy_en}
                    onChange={e => setPolicies({ ...policies, privacy_en: e.target.value })}
                    placeholder="Privacy policy in English..."
                    className="mt-2"
                    rows={8}
                  />
                </div>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Italian</label>
                  <Textarea
                    value={policies.privacy_it}
                    onChange={e => setPolicies({ ...policies, privacy_it: e.target.value })}
                    placeholder="Informativa sulla privacy in italiano..."
                    className="mt-2"
                    rows={8}
                  />
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="border border-border rounded-lg p-6">
              <h3 className="font-heading text-xl mb-4">Terms & Conditions</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">English</label>
                  <Textarea
                    value={policies.terms_en}
                    onChange={e => setPolicies({ ...policies, terms_en: e.target.value })}
                    placeholder="Terms and conditions in English..."
                    className="mt-2"
                    rows={8}
                  />
                </div>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Italian</label>
                  <Textarea
                    value={policies.terms_it}
                    onChange={e => setPolicies({ ...policies, terms_it: e.target.value })}
                    placeholder="Termini e condizioni in italiano..."
                    className="mt-2"
                    rows={8}
                  />
                </div>
              </div>
            </div>

            {/* Refund Policy */}
            <div className="border border-border rounded-lg p-6">
              <h3 className="font-heading text-xl mb-4">Refund Policy</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">English</label>
                  <Textarea
                    value={policies.refund_en}
                    onChange={e => setPolicies({ ...policies, refund_en: e.target.value })}
                    placeholder="Refund policy in English..."
                    className="mt-2"
                    rows={8}
                  />
                </div>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Italian</label>
                  <Textarea
                    value={policies.refund_it}
                    onChange={e => setPolicies({ ...policies, refund_it: e.target.value })}
                    placeholder="Politica di rimborso in italiano..."
                    className="mt-2"
                    rows={8}
                  />
                </div>
              </div>
            </div>

            {/* Cookie Policy */}
            <div className="border border-border rounded-lg p-6 bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-950/10 dark:to-orange-950/10">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl">🍪</span>
                <h3 className="font-heading text-xl">Cookie Policy</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Definește politica de cookie-uri afișată pe pagina /cookie-policy. Lasă gol pentru a folosi textul implicit GDPR.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">English</label>
                  <Textarea
                    value={policies.cookie_en}
                    onChange={e => setPolicies({ ...policies, cookie_en: e.target.value })}
                    placeholder="Cookie policy in English... (leave empty for default GDPR text)"
                    className="mt-2"
                    rows={8}
                  />
                </div>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Italian</label>
                  <Textarea
                    value={policies.cookie_it}
                    onChange={e => setPolicies({ ...policies, cookie_it: e.target.value })}
                    placeholder="Politica sui cookie in italiano... (lascia vuoto per il testo GDPR predefinito)"
                    className="mt-2"
                    rows={8}
                  />
                </div>
              </div>
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <strong>Tip:</strong> Dacă lași câmpurile goale, pagina Cookie Policy va afișa automat un text standard conform GDPR cu lista tuturor cookie-urilor utilizate pe site.
                </p>
              </div>
            </div>

            {/* Cookie Consent Settings */}
            <div className="border border-border rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl">⚙️</span>
                <h3 className="font-heading text-xl">Cookie Consent Settings</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Configurează comportamentul banner-ului de cookie care apare vizitatorilor.
              </p>
              <div className="space-y-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">Categorii de cookie disponibile:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>🔒 <strong>Necesare</strong> - Mereu active (sesiune, autentificare, coș)</li>
                    <li>📊 <strong>Analitice</strong> - Google Analytics și tracking</li>
                    <li>📢 <strong>Marketing</strong> - Publicitate și remarketing</li>
                    <li>🌐 <strong>Externe</strong> - YouTube, video-uri încorporate</li>
                  </ul>
                </div>
                <div className="flex items-center gap-2 p-3 border rounded-lg">
                  <span className="text-green-600">✓</span>
                  <span className="text-sm">Banner-ul de cookie este activ și conform GDPR/ePrivacy</span>
                </div>
              </div>
            </div>

            <Button onClick={savePolicies} disabled={savingPolicies} className="w-full md:w-auto">
              {savingPolicies ? 'Saving...' : 'Save All Policies'}
            </Button>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="space-y-6">
            <h2 className="font-heading text-2xl mb-6">Contact Information</h2>
            <p className="text-muted-foreground text-sm mb-6">
              Edit your contact details here. These will be displayed across the website (Footer, Contact page).
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Basic Contact Info */}
              <div className="border border-border rounded-lg p-6 space-y-4">
                <h3 className="font-heading text-lg mb-2">Basic Info</h3>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Email</label>
                  <Input
                    type="email"
                    value={contactInfo.email}
                    onChange={e => setContactInfo({ ...contactInfo, email: e.target.value })}
                    placeholder="email@example.com"
                    className="mt-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Phone</label>
                  <Input
                    value={contactInfo.phone}
                    onChange={e => setContactInfo({ ...contactInfo, phone: e.target.value })}
                    placeholder="+39 041 123 4567"
                    className="mt-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Website</label>
                  <Input
                    value={contactInfo.website}
                    onChange={e => setContactInfo({ ...contactInfo, website: e.target.value })}
                    placeholder="www.yoursite.com"
                    className="mt-2"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="border border-border rounded-lg p-6 space-y-4">
                <h3 className="font-heading text-lg mb-2">Location</h3>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Address</label>
                  <Input
                    value={contactInfo.address}
                    onChange={e => setContactInfo({ ...contactInfo, address: e.target.value })}
                    placeholder="Via Example 123"
                    className="mt-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">City</label>
                  <Input
                    value={contactInfo.city}
                    onChange={e => setContactInfo({ ...contactInfo, city: e.target.value })}
                    placeholder="Spoleto"
                    className="mt-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Country</label>
                  <Input
                    value={contactInfo.country}
                    onChange={e => setContactInfo({ ...contactInfo, country: e.target.value })}
                    placeholder="Italy"
                    className="mt-2"
                  />
                </div>
              </div>

              {/* Social Media */}
              <div className="border border-border rounded-lg p-6 space-y-4 md:col-span-2">
                <h3 className="font-heading text-lg mb-2">Social Media</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Instagram URL</label>
                    <Input
                      value={contactInfo.instagram}
                      onChange={e => setContactInfo({ ...contactInfo, instagram: e.target.value })}
                      placeholder="https://instagram.com/yourpage"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Facebook URL</label>
                    <Input
                      value={contactInfo.facebook}
                      onChange={e => setContactInfo({ ...contactInfo, facebook: e.target.value })}
                      placeholder="https://facebook.com/yourpage"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">TikTok URL</label>
                    <Input
                      value={contactInfo.tiktok}
                      onChange={e => setContactInfo({ ...contactInfo, tiktok: e.target.value })}
                      placeholder="https://tiktok.com/@yourpage"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={saveContactInfo} disabled={savingContact} className="w-full md:w-auto">
              {savingContact ? 'Saving...' : 'Save Contact Info'}
            </Button>

            {/* Received Messages Section */}
            <div className="border-t border-border pt-8 mt-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-2xl">Received Messages ({contactMessages.length})</h2>
                {selectedMessages.length > 0 && (
                  <Button variant="destructive" onClick={deleteSelectedMessages}>
                    Delete Selected ({selectedMessages.length})
                  </Button>
                )}
              </div>
              
              {contactMessages.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No messages received yet</p>
              ) : (
                <div className="space-y-4">
                  {/* Select All */}
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <input
                      type="checkbox"
                      checked={selectedMessages.length === contactMessages.length && contactMessages.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-muted-foreground">Select All</span>
                  </div>
                  
                  {contactMessages.map(msg => (
                    <div key={msg.id} className={`border rounded-lg p-4 ${msg.is_read ? 'bg-background' : 'bg-muted/50 border-primary/30'}`}>
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedMessages.includes(msg.id)}
                          onChange={() => toggleMessageSelection(msg.id)}
                          className="w-4 h-4 mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold">{msg.name}</p>
                              <p className="text-sm text-muted-foreground">{msg.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {!msg.is_read && (
                                <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">NEW</span>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {new Date(msg.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <p className="font-medium mb-1">{msg.subject}</p>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{msg.message}</p>
                          <div className="flex gap-2 mt-3">
                            <a href={`mailto:${msg.email}?subject=Re: ${msg.subject}`} className="text-xs text-primary hover:underline">
                              Reply via Email
                            </a>
                            <button onClick={() => deleteContactMessage(msg.id)} className="text-xs text-red-500 hover:underline">
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'shipping' && (
          <div className="max-w-xl">
            <h2 className="font-heading text-2xl mb-6">Shipping Rates & Tax</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Italy (€)</label>
                <Input
                  type="number"
                  value={shippingRates.italy}
                  onChange={e => setShippingRates({ ...shippingRates, italy: parseFloat(e.target.value) || 0 })}
                  className="mt-2"
                  data-testid="shipping-italy"
                />
              </div>
              <div>
                <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Europe / EU (€)</label>
                <Input
                  type="number"
                  value={shippingRates.europe}
                  onChange={e => setShippingRates({ ...shippingRates, europe: parseFloat(e.target.value) || 0 })}
                  className="mt-2"
                  data-testid="shipping-europe"
                />
              </div>
              <div>
                <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Rest of World (€)</label>
                <Input
                  type="number"
                  value={shippingRates.worldwide}
                  onChange={e => setShippingRates({ ...shippingRates, worldwide: parseFloat(e.target.value) || 0 })}
                  className="mt-2"
                  data-testid="shipping-worldwide"
                />
              </div>
              <div>
                <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Custom Tax (%)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={shippingRates.custom_tax_percent}
                  onChange={e => setShippingRates({ ...shippingRates, custom_tax_percent: parseFloat(e.target.value) || 0 })}
                  className="mt-2"
                  data-testid="shipping-tax"
                />
                <p className="text-xs text-muted-foreground mt-1">Applied to subtotal (e.g., 22 for 22% VAT)</p>
              </div>
              <div className="border-t pt-4 mt-4">
                <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">
                  🌍 Customs Duty / Taxe Vamale (%)
                </label>
                <Input
                  type="number"
                  step="0.1"
                  value={shippingRates.customs_duty_percent || 0}
                  onChange={e => setShippingRates({ ...shippingRates, customs_duty_percent: parseFloat(e.target.value) || 0 })}
                  className="mt-2"
                  data-testid="shipping-customs"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Applied ONLY for "Rest of World" orders (outside EU). E.g., 15 for 15%
                </p>
              </div>
              <Button onClick={saveShippingRates} disabled={savingRates} className="mt-4" data-testid="save-shipping-btn">
                {savingRates ? 'Saving...' : 'Save Shipping Rates'}
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'paintings' && (
          <div>
            {/* Add New Painting Form */}
            {isAddingNew ? (
              <div className="max-w-2xl">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-heading text-2xl">Add New Painting</h2>
                  <Button variant="outline" onClick={() => setIsAddingNew(false)}>Cancel</Button>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Title (EN) *</label>
                      <Input
                        value={newPainting.title}
                        onChange={e => setNewPainting({ ...newPainting, title: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Title (IT)</label>
                      <Input
                        value={newPainting.title_it}
                        onChange={e => setNewPainting({ ...newPainting, title_it: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Description (EN)</label>
                    <Textarea
                      value={newPainting.description}
                      onChange={e => setNewPainting({ ...newPainting, description: e.target.value })}
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Description (IT)</label>
                    <Textarea
                      value={newPainting.description_it}
                      onChange={e => setNewPainting({ ...newPainting, description_it: e.target.value })}
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Price (€) *</label>
                      <Input
                        type="number"
                        value={newPainting.price}
                        onChange={e => setNewPainting({ ...newPainting, price: parseFloat(e.target.value) || 0 })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Dimensions</label>
                      <Input
                        value={newPainting.dimensions}
                        onChange={e => setNewPainting({ ...newPainting, dimensions: e.target.value })}
                        placeholder="100x80 cm"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Year</label>
                      <Input
                        type="number"
                        value={newPainting.year}
                        onChange={e => setNewPainting({ ...newPainting, year: parseInt(e.target.value) || 2024 })}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Category</label>
                      <select
                        value={newPainting.category}
                        onChange={e => setNewPainting({ ...newPainting, category: e.target.value })}
                        className="mt-2 w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                      >
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.name}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Images Section */}
                  <div className="border border-border rounded-lg p-4 space-y-4">
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground block">Images *</label>
                    
                    {/* Upload Buttons */}
                    <div className="flex items-center gap-4 flex-wrap">
                      <label className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md cursor-pointer hover:bg-primary/90">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        {uploadingImage ? 'Uploading...' : 'Upload Images'}
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (!newPainting.image_url) {
                              uploadImage(file, true, false);
                            } else {
                              uploadImage(file, false, false);
                            }
                          }}
                          disabled={uploadingImage}
                        />
                      </label>
                      <span className="text-muted-foreground text-sm">Click image to set as main</span>
                    </div>
                    
                    {/* All Images Preview */}
                    <div className="flex flex-wrap gap-3 mt-3">
                      {/* Main Image */}
                      {newPainting.image_url && (
                        <div className="relative">
                          <img src={newPainting.image_url} alt="Main" className="w-28 h-28 object-cover border-2 border-primary rounded" />
                          <span className="absolute bottom-0 left-0 right-0 bg-primary text-primary-foreground text-xs text-center py-1">MAIN</span>
                          <button
                            type="button"
                            onClick={() => setNewPainting({ ...newPainting, image_url: '' })}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                          >×</button>
                        </div>
                      )}
                      
                      {/* Additional Images */}
                      {newPainting.additional_images && newPainting.additional_images.map((img, idx) => (
                        <div key={idx} className="relative cursor-pointer" onClick={() => setAsMainImage(img, false)}>
                          <img src={img} alt={`Image ${idx + 2}`} className="w-28 h-28 object-cover border rounded hover:border-primary" />
                          <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center py-1">Click = Main</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeAdditionalImage(idx, false); }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                          >×</button>
                        </div>
                      ))}
                    </div>
                    
                    {!newPainting.image_url && (!newPainting.additional_images || newPainting.additional_images.length === 0) && (
                      <p className="text-muted-foreground text-sm">No images uploaded yet. Upload at least one image.</p>
                    )}
                  </div>

                  {/* Shipping Cost */}
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Shipping Cost (€)</label>
                    <Input
                      type="number"
                      value={newPainting.shipping_cost}
                      onChange={e => setNewPainting({ ...newPainting, shipping_cost: parseFloat(e.target.value) || 0 })}
                      placeholder="0 = Free shipping"
                      className="mt-2 max-w-xs"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Enter 0 for free shipping, or the shipping cost in euros</p>
                  </div>

                  {/* Payment Link */}
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Payment Link (Stripe/Klarna)</label>
                    <Input
                      value={newPainting.payment_link || ''}
                      onChange={e => setNewPainting({ ...newPainting, payment_link: e.target.value })}
                      placeholder="https://buy.stripe.com/..."
                      className="mt-2"
                      data-testid="new-painting-payment-link"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Link diretto di pagamento Stripe (con Klarna). Lascia vuoto per checkout standard.</p>
                  </div>

                  {/* Painting Options */}
                  <div className="border-2 border-purple-500 rounded-lg p-4 bg-purple-50">
                    <label className="text-lg font-bold text-purple-800 block mb-4">⬇️ PAINTING OPTIONS</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border-2 border-gray-300 cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={newPainting.is_sold}
                          onChange={e => setNewPainting({ ...newPainting, is_sold: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <span className="text-base font-semibold">❌ Sold</span>
                      </label>
                      <label className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border-2 border-gray-300 cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={newPainting.is_featured}
                          onChange={e => setNewPainting({ ...newPainting, is_featured: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <span className="text-base font-semibold">⭐ Featured</span>
                      </label>
                      <label className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border-2 border-gray-300 cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={newPainting.is_pinned}
                          onChange={e => setNewPainting({ ...newPainting, is_pinned: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <span className="text-base font-semibold">📌 Pin to Top</span>
                      </label>
                      <label className="flex items-center gap-3 bg-yellow-200 px-4 py-3 rounded-lg border-2 border-yellow-500 cursor-pointer hover:bg-yellow-300">
                        <input
                          type="checkbox"
                          checked={newPainting.is_promotion}
                          onChange={e => setNewPainting({ ...newPainting, is_promotion: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <span className="text-base font-semibold">✨ Promotion (Gold)</span>
                      </label>
                      <label className="flex items-center gap-3 bg-purple-200 px-4 py-3 rounded-lg border-2 border-purple-500 cursor-pointer hover:bg-purple-300">
                        <input
                          type="checkbox"
                          checked={newPainting.is_unique_art}
                          onChange={e => setNewPainting({ ...newPainting, is_unique_art: e.target.checked })}
                          className="w-5 h-5"
                        />
                        <span className="text-base font-semibold">🎨 UNIQUE ART</span>
                      </label>
                    </div>
                  </div>
                  <Button onClick={addPainting} disabled={savingPainting} className="mt-4 bg-primary">
                    {savingPainting ? 'Adding...' : 'Add Painting'}
                  </Button>
                </div>
              </div>
            ) : editingPainting ? (
              <div className="max-w-2xl">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-heading text-2xl">Edit Painting</h2>
                  <Button variant="outline" onClick={() => setEditingPainting(null)}>Cancel</Button>
                </div>
                
                {/* PAINTING OPTIONS - AT THE TOP */}
                <div className="border-2 border-purple-500 rounded-lg p-4 bg-purple-50 mb-6">
                  <label className="text-lg font-bold text-purple-800 block mb-4">🎨 PAINTING OPTIONS</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border-2 border-gray-300 cursor-pointer hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={editingPainting.is_sold || false}
                        onChange={e => setEditingPainting({ ...editingPainting, is_sold: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span className="text-base font-semibold">❌ Sold</span>
                    </label>
                    <label className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border-2 border-gray-300 cursor-pointer hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={editingPainting.is_featured || false}
                        onChange={e => setEditingPainting({ ...editingPainting, is_featured: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span className="text-base font-semibold">⭐ Featured</span>
                    </label>
                    <label className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border-2 border-gray-300 cursor-pointer hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={editingPainting.is_pinned || false}
                        onChange={e => setEditingPainting({ ...editingPainting, is_pinned: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span className="text-base font-semibold">📌 Pin to Top</span>
                    </label>
                    <label className="flex items-center gap-3 bg-yellow-200 px-4 py-3 rounded-lg border-2 border-yellow-500 cursor-pointer hover:bg-yellow-300">
                      <input
                        type="checkbox"
                        checked={editingPainting.is_promotion || false}
                        onChange={e => setEditingPainting({ ...editingPainting, is_promotion: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span className="text-base font-semibold">✨ Promotion (Gold Glow)</span>
                    </label>
                    <label className="flex items-center gap-3 bg-purple-200 px-4 py-3 rounded-lg border-2 border-purple-500 cursor-pointer hover:bg-purple-300 col-span-1 sm:col-span-2">
                      <input
                        type="checkbox"
                        checked={editingPainting.is_unique_art || false}
                        onChange={e => setEditingPainting({ ...editingPainting, is_unique_art: e.target.checked })}
                        className="w-6 h-6"
                      />
                      <span className="text-lg font-bold">🎨 UNIQUE ART</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Title (EN)</label>
                      <Input
                        value={editingPainting.title}
                        onChange={e => setEditingPainting({ ...editingPainting, title: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Title (IT)</label>
                      <Input
                        value={editingPainting.title_it}
                        onChange={e => setEditingPainting({ ...editingPainting, title_it: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Description (EN)</label>
                    <Textarea
                      value={editingPainting.description}
                      onChange={e => setEditingPainting({ ...editingPainting, description: e.target.value })}
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Description (IT)</label>
                    <Textarea
                      value={editingPainting.description_it}
                      onChange={e => setEditingPainting({ ...editingPainting, description_it: e.target.value })}
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Price (€)</label>
                      <Input
                        type="number"
                        value={editingPainting.price}
                        onChange={e => setEditingPainting({ ...editingPainting, price: parseFloat(e.target.value) || 0 })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Dimensions</label>
                      <Input
                        value={editingPainting.dimensions}
                        onChange={e => setEditingPainting({ ...editingPainting, dimensions: e.target.value })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Year</label>
                      <Input
                        type="number"
                        value={editingPainting.year}
                        onChange={e => setEditingPainting({ ...editingPainting, year: parseInt(e.target.value) || 2024 })}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Category</label>
                      <select
                        value={editingPainting.category}
                        onChange={e => setEditingPainting({ ...editingPainting, category: e.target.value })}
                        className="mt-2 w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                      >
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.name}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Images Section */}
                  <div className="border border-border rounded-lg p-4 space-y-4">
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground block">Images</label>
                    
                    {/* Upload Button */}
                    <div className="flex items-center gap-4 flex-wrap">
                      <label className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md cursor-pointer hover:bg-primary/90">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        {uploadingImage ? 'Uploading...' : 'Upload Images'}
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (!editingPainting.image_url) {
                              uploadImage(file, true, true);
                            } else {
                              uploadImage(file, false, true);
                            }
                          }}
                          disabled={uploadingImage}
                        />
                      </label>
                      <span className="text-muted-foreground text-sm">Click image to set as main</span>
                    </div>
                    
                    {/* All Images Preview */}
                    <div className="flex flex-wrap gap-3 mt-3">
                      {/* Main Image */}
                      {editingPainting.image_url && (
                        <div className="relative">
                          <img src={editingPainting.image_url} alt="Main" className="w-28 h-28 object-cover border-2 border-primary rounded" />
                          <span className="absolute bottom-0 left-0 right-0 bg-primary text-primary-foreground text-xs text-center py-1">MAIN</span>
                          <button
                            type="button"
                            onClick={() => setEditingPainting({ ...editingPainting, image_url: '' })}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            data-testid="edit-painting-delete-main-image"
                          >×</button>
                        </div>
                      )}
                      
                      {/* Additional Images */}
                      {editingPainting.additional_images && editingPainting.additional_images.map((img, idx) => (
                        <div key={idx} className="relative cursor-pointer" onClick={() => setAsMainImage(img, true)}>
                          <img src={img} alt={`Image ${idx + 2}`} className="w-28 h-28 object-cover border rounded hover:border-primary" />
                          <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center py-1">Click = Main</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeAdditionalImage(idx, true); }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Shipping Cost */}
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Shipping Cost (€)</label>
                    <Input
                      type="number"
                      value={editingPainting.shipping_cost || 0}
                      onChange={e => setEditingPainting({ ...editingPainting, shipping_cost: parseFloat(e.target.value) || 0 })}
                      placeholder="0 = Free shipping"
                      className="mt-2 max-w-xs"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Enter 0 for free shipping, or the shipping cost in euros</p>
                  </div>

                  {/* Payment Link */}
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Payment Link (Stripe/Klarna)</label>
                    <Input
                      value={editingPainting.payment_link || ''}
                      onChange={e => setEditingPainting({ ...editingPainting, payment_link: e.target.value })}
                      placeholder="https://buy.stripe.com/..."
                      className="mt-2"
                      data-testid="edit-painting-payment-link"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Link diretto di pagamento Stripe (con Klarna). Lascia vuoto per checkout standard.</p>
                  </div>

                  <Button onClick={savePainting} disabled={savingPainting} className="mt-4">
                    {savingPainting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-heading text-2xl">All Paintings ({paintings.length})</h2>
                  <Button onClick={() => setIsAddingNew(true)} className="bg-primary" data-testid="add-painting-btn">
                    + Add New Painting
                  </Button>
                </div>
                <div className="overflow-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b">
                        <th className="py-4 font-body text-sm uppercase tracking-wider">Image</th>
                        <th className="py-4 font-body text-sm uppercase tracking-wider">Title</th>
                        <th className="py-4 font-body text-sm uppercase tracking-wider">Category</th>
                        <th className="py-4 font-body text-sm uppercase tracking-wider">Price</th>
                        <th className="py-4 font-body text-sm uppercase tracking-wider">Status</th>
                        <th className="py-4 font-body text-sm uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paintings.map(p => (
                        <tr key={p.id} className="border-b" data-testid={`admin-painting-${p.id}`}>
                          <td className="py-4">
                            <img src={p.image_url} alt={p.title} className="w-16 h-16 object-cover" />
                          </td>
                          <td className="py-4">{p.title}</td>
                          <td className="py-4">{p.category}</td>
                          <td className="py-4">€{p.price.toLocaleString()}</td>
                          <td className="py-4">
                            <span className={`px-2 py-1 text-xs ${p.is_sold ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                              {p.is_sold ? 'Sold' : 'Available'}
                            </span>
                            {p.is_featured && <span className="ml-1 px-2 py-1 text-xs bg-blue-100 text-blue-800">Featured</span>}
                            {p.is_pinned && <span className="ml-1 px-2 py-1 text-xs bg-purple-100 text-purple-800">📌 Top</span>}
                            {p.is_promotion && <span className="ml-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-800">✨ Promo</span>}
                            {p.is_unique_art && <span className="ml-1 px-2 py-1 text-xs bg-indigo-100 text-indigo-800">🎨 Unique</span>}
                          </td>
                          <td className="py-4">
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => setEditingPainting(p)}>
                                Edit
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => deletePainting(p.id, p.title)}
                                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'about' && (
          <div className="space-y-8">
            {/* About Title & Content */}
            <div className="bg-muted/30 border border-border rounded-lg p-6">
              <h2 className="font-heading text-2xl mb-4">About the Artist - Title & Content</h2>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Title (English)</label>
                    <Input
                      value={aboutTitleEn}
                      onChange={e => setAboutTitleEn(e.target.value)}
                      placeholder="About the Artist"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Title (Italian)</label>
                    <Input
                      value={aboutTitleIt}
                      onChange={e => setAboutTitleIt(e.target.value)}
                      placeholder="L'Artista"
                      className="mt-2"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Content (English)</label>
                  <Textarea
                    value={aboutContentEn}
                    onChange={e => setAboutContentEn(e.target.value)}
                    placeholder="Write about the artist in English..."
                    className="mt-2"
                    rows={6}
                  />
                </div>
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Content (Italian)</label>
                  <Textarea
                    value={aboutContentIt}
                    onChange={e => setAboutContentIt(e.target.value)}
                    placeholder="Scrivi dell'artista in italiano..."
                    className="mt-2"
                    rows={6}
                  />
                </div>
                <Button onClick={saveAboutContent} disabled={savingAbout}>
                  {savingAbout ? 'Saving...' : 'Save About Content'}
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Artist Image */}
              <div>
                <h2 className="font-heading text-2xl mb-4">Artist Image</h2>
                <div className="border rounded-lg p-4 bg-muted/20">
                  {/* Current Image Preview with Save Button */}
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    {/* Image Preview */}
                    <div className="relative">
                      {artistImageUrl ? (
                        <>
                          <img 
                            src={resolveImageUrl(artistImageUrl)} 
                            alt="Artist preview" 
                            className="w-64 h-64 object-cover border rounded-lg shadow-md" 
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/256x256?text=No+Image';
                            }} 
                          />
                          <button
                            onClick={() => {
                              if (window.confirm('Delete this image?')) {
                                setArtistImageUrl('');
                              }
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                            title="Delete image"
                          >
                            ×
                          </button>
                        </>
                      ) : (
                        <div className="w-64 h-64 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50">
                          <span className="text-muted-foreground text-sm">No image set</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Controls */}
                    <div className="flex-1 space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Current artist image displayed on the About page
                      </p>
                      
                      {/* Upload Button */}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            setUploadingImage(true);
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            try {
                              const res = await axios.post(`${API}/upload-image`, formData, {
                                headers: { 
                                  Authorization: `Bearer ${token}`,
                                  'Content-Type': 'multipart/form-data'
                                }
                              });
                              // Store relative URL so it survives domain changes
                              setArtistImageUrl(res.data.url);
                              toast.success('Image uploaded!');
                            } catch (err) {
                              console.error('Upload error:', err);
                              toast.error('Failed to upload image');
                            } finally {
                              setUploadingImage(false);
                            }
                            e.target.value = '';
                          }}
                          className="hidden"
                          id="artist-image-upload"
                          disabled={uploadingImage}
                        />
                        <label 
                          htmlFor="artist-image-upload" 
                          className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-muted transition-colors ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {uploadingImage ? (
                            <>⏳ Uploading...</>
                          ) : (
                            <>📷 {artistImageUrl ? 'Change Image' : 'Upload Image'}</>
                          )}
                        </label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Recommended: 400×400px square image
                        </p>
                      </div>
                      
                      {/* URL Input */}
                      <div>
                        <label className="text-xs text-muted-foreground">Or paste image URL:</label>
                        <Input
                          value={artistImageUrl}
                          onChange={e => setArtistImageUrl(e.target.value)}
                          placeholder="https://..."
                          className="mt-1"
                        />
                      </div>
                      
                      {/* Save Button - Prominent */}
                      <Button 
                        onClick={saveArtistImage} 
                        disabled={savingAbout}
                        className="w-full md:w-auto bg-green-600 hover:bg-green-700"
                      >
                        {savingAbout ? '⏳ Saving...' : '💾 Save Artist Image'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Video Section */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl">🎬 Artist Video</h2>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={videoEnabled}
                      onChange={e => setVideoEnabled(e.target.checked)}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-sm font-medium">{videoEnabled ? '✅ Enabled' : '❌ Disabled'}</span>
                  </label>
                </div>
                
                {videoEnabled && (
                  <div className="space-y-4">
                    {/* Video Type Selector */}
                    <div>
                      <label className="text-sm text-muted-foreground mb-2 block">Video Source</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setVideoType('youtube')}
                          className={`px-4 py-2 rounded border-2 transition-all ${
                            videoType === 'youtube' 
                              ? 'border-red-500 bg-red-500 text-white' 
                              : 'border-border hover:border-red-300'
                          }`}
                        >
                          📺 YouTube Link
                        </button>
                        <button
                          onClick={() => setVideoType('upload')}
                          className={`px-4 py-2 rounded border-2 transition-all ${
                            videoType === 'upload' 
                              ? 'border-blue-500 bg-blue-500 text-white' 
                              : 'border-border hover:border-blue-300'
                          }`}
                        >
                          📤 Upload Video
                        </button>
                      </div>
                    </div>
                    
                    {/* Video Title */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Title (EN)</label>
                        <Input
                          value={videoTitleEn}
                          onChange={e => setVideoTitleEn(e.target.value)}
                          placeholder="Watch me paint"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Title (IT)</label>
                        <Input
                          value={videoTitleIt}
                          onChange={e => setVideoTitleIt(e.target.value)}
                          placeholder="Guardami dipingere"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    {/* YouTube Link Input */}
                    {videoType === 'youtube' && (
                      <div>
                        <label className="text-sm text-muted-foreground">YouTube URL</label>
                        <Input
                          value={videoUrl}
                          onChange={e => setVideoUrl(e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Paste the full YouTube video URL
                        </p>
                      </div>
                    )}
                    
                    {/* Upload Video */}
                    {videoType === 'upload' && (
                      <div className="space-y-3">
                        <div>
                          <input 
                            type="file" 
                            accept="video/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              // Check file size (max 100MB)
                              if (file.size > 100 * 1024 * 1024) {
                                toast.error('Video file too large. Max 100MB');
                                return;
                              }
                              
                              setUploadingVideo(true);
                              const formData = new FormData();
                              formData.append('file', file);
                              
                              try {
                                const res = await axios.post(`${API}/upload-video`, formData, {
                                  headers: { 
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'multipart/form-data'
                                  }
                                });
                                const uploadedUrl = `${BACKEND_URL}${res.data.url}`;
                                setVideoUrl(uploadedUrl);
                                toast.success('Video uploaded!');
                              } catch (err) {
                                console.error('Upload error:', err);
                                toast.error('Failed to upload video. Try a YouTube link instead.');
                              } finally {
                                setUploadingVideo(false);
                              }
                              e.target.value = '';
                            }}
                            className="hidden"
                            id="video-upload"
                            disabled={uploadingVideo}
                          />
                          <label 
                            htmlFor="video-upload" 
                            className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-muted transition-colors ${uploadingVideo ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {uploadingVideo ? '⏳ Uploading...' : '📤 Upload Video File'}
                          </label>
                          <p className="text-xs text-muted-foreground mt-1">Max 100MB. MP4, WebM supported.</p>
                        </div>
                        
                        <div>
                          <label className="text-sm text-muted-foreground">Or paste video URL:</label>
                          <Input
                            value={videoUrl}
                            onChange={e => setVideoUrl(e.target.value)}
                            placeholder="https://..."
                            className="mt-1"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Video Preview */}
                    {videoUrl && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium">Preview:</p>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => setVideoUrl('')}
                          >
                            🗑️ Remove Video
                          </Button>
                        </div>
                        <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                          {videoType === 'youtube' ? (
                            <iframe
                              src={`https://www.youtube.com/embed/${videoUrl.includes('youtu.be/') ? videoUrl.split('youtu.be/')[1]?.split('?')[0] : videoUrl.split('v=')[1]?.split('&')[0]}`}
                              className="w-full h-full"
                              allowFullScreen
                              title="Video preview"
                            />
                          ) : (
                            <video src={videoUrl} controls className="w-full h-full" />
                          )}
                          {/* Watermark overlay preview */}
                          {watermarkEnabled && watermarkText && (
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                              <div 
                                className="absolute whitespace-nowrap font-heading tracking-widest select-none"
                                style={{
                                  fontSize: '16px',
                                  color: `rgba(255, 255, 255, ${watermarkOpacity / 100})`,
                                  textShadow: `0 0 8px rgba(0, 0, 0, ${watermarkOpacity / 100})`,
                                  transform: 'rotate(-35deg) scale(1.5)',
                                  letterSpacing: '4px'
                                }}
                              >
                                {Array(5).fill(watermarkText).join('     ')}
                              </div>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {watermarkEnabled ? '✅ Watermark will appear on video' : '⚠️ Enable watermark in Theme settings to protect video'}
                        </p>
                      </div>
                    )}
                    
                    {/* Save Video Button */}
                    <Button 
                      onClick={saveVideoSettings} 
                      disabled={savingAbout}
                      className="w-full md:w-auto bg-purple-600 hover:bg-purple-700"
                    >
                      {savingAbout ? '⏳ Saving...' : '💾 Save Video Settings'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Critics Management */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-heading text-2xl">Critics & Reviews ({critics.length})</h2>
                  {!isAddingCritic && !editingCritic && (
                    <Button onClick={() => setIsAddingCritic(true)} className="bg-primary">+ Add Critic</Button>
                  )}
                </div>

              {isAddingCritic && (
                <div className="bg-muted p-4 mb-4">
                  <h3 className="font-heading text-lg mb-3">Add New Critic</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-muted-foreground">Quote (EN) *</label>
                      <Textarea
                        value={newCritic.text}
                        onChange={e => setNewCritic({ ...newCritic, text: e.target.value })}
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Quote (IT)</label>
                      <Textarea
                        value={newCritic.text_it}
                        onChange={e => setNewCritic({ ...newCritic, text_it: e.target.value })}
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Author *</label>
                        <Input
                          value={newCritic.author}
                          onChange={e => setNewCritic({ ...newCritic, author: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Publication</label>
                        <Input
                          value={newCritic.publication}
                          onChange={e => setNewCritic({ ...newCritic, publication: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={addCritic} disabled={savingAbout}>{savingAbout ? 'Adding...' : 'Add'}</Button>
                      <Button variant="outline" onClick={() => { setIsAddingCritic(false); setNewCritic({ text: '', text_it: '', author: '', publication: '' }); }}>Cancel</Button>
                    </div>
                  </div>
                </div>
              )}

              {editingCritic && (
                <div className="bg-muted p-4 mb-4">
                  <h3 className="font-heading text-lg mb-3">Edit Critic</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-muted-foreground">Quote (EN)</label>
                      <Textarea
                        value={editingCritic.text}
                        onChange={e => setEditingCritic({ ...editingCritic, text: e.target.value })}
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Quote (IT)</label>
                      <Textarea
                        value={editingCritic.text_it || ''}
                        onChange={e => setEditingCritic({ ...editingCritic, text_it: e.target.value })}
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Author</label>
                        <Input
                          value={editingCritic.author}
                          onChange={e => setEditingCritic({ ...editingCritic, author: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Publication</label>
                        <Input
                          value={editingCritic.publication}
                          onChange={e => setEditingCritic({ ...editingCritic, publication: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={updateCritic} disabled={savingAbout}>{savingAbout ? 'Saving...' : 'Save'}</Button>
                      <Button variant="outline" onClick={() => setEditingCritic(null)}>Cancel</Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3 max-h-96 overflow-auto">
                {critics.map(critic => (
                  <div key={critic.id} className="border p-3 bg-background">
                    <p className="text-sm italic mb-2">"{critic.text.substring(0, 100)}..."</p>
                    <p className="text-xs text-muted-foreground">— {critic.author}, {critic.publication}</p>
                    <div className="flex gap-2 mt-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingCritic(critic)}>Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => deleteCritic(critic.id, critic.author)} className="text-red-600">Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
              </div>

              {/* Exhibitions & Presentations */}
              <div className="border border-border rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-heading text-2xl">Exhibitions & Presentations ({exhibitions.length})</h2>
                  <Button onClick={() => setIsAddingExhibition(!isAddingExhibition)}>
                    {isAddingExhibition ? 'Cancel' : '+ Add'}
                  </Button>
                </div>

                {isAddingExhibition && (
                  <div className="mb-4 p-4 border rounded-lg bg-muted/50 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Title (EN) *</label>
                        <Input
                          value={newExhibition.title}
                          onChange={e => setNewExhibition({ ...newExhibition, title: e.target.value })}
                          placeholder="Exhibition title"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Title (IT)</label>
                        <Input
                          value={newExhibition.title_it}
                          onChange={e => setNewExhibition({ ...newExhibition, title_it: e.target.value })}
                          placeholder="Titolo mostra"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Location *</label>
                        <Input
                          value={newExhibition.location}
                          onChange={e => setNewExhibition({ ...newExhibition, location: e.target.value })}
                          placeholder="Venice, Italy"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Date/Year</label>
                        <Input
                          value={newExhibition.date}
                          onChange={e => setNewExhibition({ ...newExhibition, date: e.target.value })}
                          placeholder="2024"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Description (EN)</label>
                        <Textarea
                          value={newExhibition.description}
                          onChange={e => setNewExhibition({ ...newExhibition, description: e.target.value })}
                          placeholder="Brief description..."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Description (IT)</label>
                        <Textarea
                          value={newExhibition.description_it}
                          onChange={e => setNewExhibition({ ...newExhibition, description_it: e.target.value })}
                          placeholder="Breve descrizione..."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <Button onClick={addExhibition} disabled={savingAbout}>
                      {savingAbout ? 'Adding...' : 'Add Exhibition'}
                    </Button>
                  </div>
                )}

                {editingExhibition && (
                  <div className="mb-4 p-4 border rounded-lg bg-blue-50 space-y-3">
                    <h3 className="font-semibold">Edit Exhibition</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Title (EN)</label>
                        <Input
                          value={editingExhibition.title}
                          onChange={e => setEditingExhibition({ ...editingExhibition, title: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Title (IT)</label>
                        <Input
                          value={editingExhibition.title_it || ''}
                          onChange={e => setEditingExhibition({ ...editingExhibition, title_it: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Location</label>
                        <Input
                          value={editingExhibition.location}
                          onChange={e => setEditingExhibition({ ...editingExhibition, location: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Date/Year</label>
                        <Input
                          value={editingExhibition.date || ''}
                          onChange={e => setEditingExhibition({ ...editingExhibition, date: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Description (EN)</label>
                        <Textarea
                          value={editingExhibition.description || ''}
                          onChange={e => setEditingExhibition({ ...editingExhibition, description: e.target.value })}
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Description (IT)</label>
                        <Textarea
                          value={editingExhibition.description_it || ''}
                          onChange={e => setEditingExhibition({ ...editingExhibition, description_it: e.target.value })}
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={updateExhibition} disabled={savingAbout}>{savingAbout ? 'Saving...' : 'Save'}</Button>
                      <Button variant="outline" onClick={() => setEditingExhibition(null)}>Cancel</Button>
                    </div>
                  </div>
                )}

                <div className="space-y-3 max-h-96 overflow-auto">
                  {exhibitions.map(exhibition => (
                    <div key={exhibition.id} className="border p-3 bg-background rounded">
                      <p className="font-semibold">{exhibition.title}</p>
                      <p className="text-sm text-muted-foreground">{exhibition.location} • {exhibition.date}</p>
                      {exhibition.description && <p className="text-sm mt-1">{exhibition.description}</p>}
                      <div className="flex gap-2 mt-2">
                        <Button variant="outline" size="sm" onClick={() => setEditingExhibition(exhibition)}>Edit</Button>
                        <Button variant="outline" size="sm" onClick={() => deleteExhibition(exhibition.id, exhibition.title)} className="text-red-600">Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'newsletter' && (
          <div className="space-y-8">
            {/* Subscribers List with Delete */}
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-heading text-2xl">Subscribers ({subscribers.length})</h2>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setShowAddSubscriberModal(true)}>
                      ➕ Add Manual
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowImportModal(true)}>
                      📁 Import File
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={importSubscribersFromClients}
                      disabled={importingFromClients || clients.length === 0}
                      title={clients.length === 0 ? 'No clients with email addresses' : `Import from ${clients.filter(c => c.email).length} clients`}
                    >
                      {importingFromClients ? '⏳ Importing...' : `👥 Import from Clients (${clients.filter(c => c.email).length})`}
                    </Button>
                  </div>
                </div>
                {/* Search */}
                <div className="relative mb-4">
                  <Input
                    type="text"
                    placeholder="🔍 Search subscribers..."
                    value={subscriberSearch}
                    onChange={(e) => setSubscriberSearch(e.target.value)}
                    className="pr-8"
                  />
                  {subscriberSearch && (
                    <button
                      onClick={() => setSubscriberSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {/* Select All & Delete Selected */}
                {subscribers.length > 0 && (
                  <div className="flex items-center gap-3 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSubscribers.length === subscribers.filter(sub => sub.email.toLowerCase().includes(subscriberSearch.toLowerCase())).length && subscribers.length > 0}
                        onChange={selectAllSubscribers}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm">Select All</span>
                    </label>
                    {selectedSubscribers.length > 0 && (
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={deleteSelectedSubscribers}
                        disabled={deletingSubscribers}
                      >
                        {deletingSubscribers ? '⏳ Deleting...' : `🗑️ Delete Selected (${selectedSubscribers.length})`}
                      </Button>
                    )}
                  </div>
                )}
                <div className="bg-muted p-4 max-h-96 overflow-auto rounded-lg">
                  {subscribers.length === 0 ? (
                    <p className="text-muted-foreground">No subscribers yet</p>
                  ) : (
                    <ul className="space-y-2">
                      {subscribers
                        .filter(sub => sub.email.toLowerCase().includes(subscriberSearch.toLowerCase()))
                        .map((sub, i) => (
                        <li key={i} className="flex justify-between items-center py-2 px-3 bg-background rounded border border-border/50">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedSubscribers.includes(sub.email)}
                              onChange={() => toggleSubscriberSelection(sub.email)}
                              className="w-4 h-4 rounded"
                            />
                            <div>
                              <span className="font-body text-sm">{sub.email}</span>
                              <span className="text-xs text-muted-foreground ml-2">({new Date(sub.subscribed_at).toLocaleDateString()})</span>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => deleteSubscriber(sub.email)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div>
                <h2 className="font-heading text-2xl mb-4">Compose Newsletter</h2>
                <div className="space-y-4">
                  {/* Language Selector */}
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Language / Limba</label>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => setNewsletter({ ...newsletter, language: 'en' })}
                        className={`px-4 py-2 rounded border-2 transition-all ${
                          newsletter.language === 'en' 
                            ? 'border-primary bg-primary text-primary-foreground' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        🇬🇧 English
                      </button>
                      <button
                        onClick={() => setNewsletter({ ...newsletter, language: 'it' })}
                        className={`px-4 py-2 rounded border-2 transition-all ${
                          newsletter.language === 'it' 
                            ? 'border-primary bg-primary text-primary-foreground' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        🇮🇹 Italiano
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Subject</label>
                    <Input
                      value={newsletter.subject}
                      onChange={e => setNewsletter({ ...newsletter, subject: e.target.value })}
                      placeholder={newsletter.language === 'it' ? "Oggetto newsletter..." : "Newsletter subject..."}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Content</label>
                    <Textarea
                      value={newsletter.content}
                      onChange={e => setNewsletter({ ...newsletter, content: e.target.value })}
                      placeholder={newsletter.language === 'it' ? "Scrivi il contenuto della newsletter..." : "Write your newsletter content..."}
                      className="mt-2"
                      rows={6}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Image (optional)</label>
                    <div className="flex items-center gap-4 mt-2">
                      <label className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md cursor-pointer hover:bg-primary/90">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setUploadingImage(true);
                            const formData = new FormData();
                            formData.append('file', file);
                            try {
                              const res = await axios.post(`${API}/upload-image`, formData, {
                                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                              });
                              const imageUrl = `${BACKEND_URL}${res.data.url}`;
                              setNewsletter({ ...newsletter, image_url: imageUrl });
                              toast.success('Image uploaded!');
                            } catch (err) {
                              toast.error('Failed to upload image');
                            } finally {
                              setUploadingImage(false);
                            }
                          }}
                          disabled={uploadingImage}
                        />
                      </label>
                      <span className="text-muted-foreground text-sm">or paste URL below</span>
                    </div>
                    <Input
                      value={newsletter.image_url}
                      onChange={e => setNewsletter({ ...newsletter, image_url: e.target.value })}
                      placeholder="https://... (paste image link)"
                      className="mt-2"
                    />
                    {newsletter.image_url && (
                      <div className="mt-3 space-y-3">
                        <div className="flex items-center gap-4">
                          <label className="text-sm text-muted-foreground whitespace-nowrap">Image Size:</label>
                          <input
                            type="range"
                            min="20"
                            max="100"
                            value={newsletter.image_width || 100}
                            onChange={e => setNewsletter({ ...newsletter, image_width: parseInt(e.target.value) })}
                            className="flex-1"
                          />
                          <span className="text-sm font-mono w-12">{newsletter.image_width || 100}%</span>
                          <button
                            type="button"
                            onClick={() => setNewsletter({ ...newsletter, image_url: '', image_width: 100 })}
                            className="bg-red-500 text-white rounded px-2 py-1 text-xs hover:bg-red-600"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="border rounded p-2 bg-muted/30">
                          <p className="text-xs text-muted-foreground mb-2">Preview at {newsletter.image_width || 100}% width:</p>
                          <img 
                            src={newsletter.image_url} 
                            alt="Preview" 
                            style={{ width: `${newsletter.image_width || 100}%`, maxWidth: '100%' }}
                            className="object-cover rounded" 
                            onError={(e) => e.target.style.display='none'} 
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={sendNewsletter} disabled={sendingNewsletter || subscribers.length === 0}>
                      {sendingNewsletter ? 'Sending...' : `Send to ${subscribers.length} subscribers`}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowNewsletterTestModal(true)}
                      disabled={!newsletter.subject || !newsletter.content}
                    >
                      📧 Test Email
                    </Button>
                  </div>
                  {subscribers.length === 0 && (
                    <p className="text-sm text-orange-600 mt-2">⚠️ No subscribers yet. Add subscribers or import from clients below.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Email Template Preview */}
            <div>
              <h2 className="font-heading text-2xl mb-4">Email Preview</h2>
              <p className="text-sm text-muted-foreground mb-4">This is how your newsletter will look to subscribers:</p>
              <div className="border-2 border-black rounded-lg p-6 bg-white max-w-2xl">
                <div className="border-b border-black pb-4 mb-4">
                  <div className="text-center">
                    <h1 className="font-heading text-2xl text-foreground">Elena Candalia</h1>
                  </div>
                </div>
                <div className="space-y-4">
                  <h2 className="font-heading text-xl text-foreground">
                    {newsletter.subject || 'Your Newsletter Subject'}
                  </h2>
                  {newsletter.image_url && (
                    <div style={{ width: `${newsletter.image_width || 100}%` }}>
                      <img src={newsletter.image_url} alt="Newsletter" className="w-full object-cover rounded" />
                    </div>
                  )}
                  <div className="font-body text-muted-foreground whitespace-pre-line">
                    {newsletter.content || 'Your newsletter content will appear here. Write something in the content field above to preview it.'}
                  </div>
                </div>
                <div className="border-t border-black pt-4 mt-6 text-center">
                  <p className="text-xs text-muted-foreground">
                    © {new Date().getFullYear()} Elena Candalia. All rights reserved.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {newsletter.language === 'it' 
                      ? "Hai ricevuto questa email perché sei iscritto alla nostra newsletter."
                      : "You received this email because you subscribed to our newsletter."
                    }
                  </p>
                  <p className="text-xs mt-2">
                    <a href="#" className="text-primary hover:underline">
                      {newsletter.language === 'it' 
                        ? "🔗 Clicca qui per annullare l'iscrizione"
                        : "🔗 Click here to unsubscribe"
                      }
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Add Subscriber Modal */}
            {showAddSubscriberModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background rounded-lg max-w-md w-full p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="font-heading text-xl">Add Subscriber Manually</h2>
                    <button onClick={() => setShowAddSubscriberModal(false)} className="text-2xl">&times;</button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Email Address</label>
                      <Input
                        type="email"
                        value={newSubscriberEmail}
                        onChange={e => setNewSubscriberEmail(e.target.value)}
                        placeholder="email@example.com"
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowAddSubscriberModal(false)}>Cancel</Button>
                      <Button onClick={addSubscriberManually}>➕ Add Subscriber</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Import Subscribers Modal */}
            {showImportModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background rounded-lg max-w-md w-full p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="font-heading text-xl">Import Subscribers from File</h2>
                    <button onClick={() => setShowImportModal(false)} className="text-2xl">&times;</button>
                  </div>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Upload a .txt or .csv file containing email addresses. The system will extract all valid email addresses from the file.
                    </p>
                    <div className="border-2 border-dashed rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept=".txt,.csv"
                        onChange={e => importSubscribersFromFile(e.target.files[0])}
                        disabled={importingSubscribers}
                        className="hidden"
                        id="import-file-input"
                      />
                      <label htmlFor="import-file-input" className="cursor-pointer">
                        <div className="text-4xl mb-2">📁</div>
                        <p className="font-medium">{importingSubscribers ? 'Importing...' : 'Click to select file'}</p>
                        <p className="text-xs text-muted-foreground mt-1">Accepts .txt, .csv files</p>
                      </label>
                    </div>
                    <Button variant="outline" className="w-full" onClick={() => setShowImportModal(false)}>Cancel</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Newsletter Test Modal */}
            {showNewsletterTestModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background rounded-lg max-w-md w-full p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="font-heading text-xl">Trimite Test Newsletter</h2>
                    <button onClick={() => setShowNewsletterTestModal(false)} className="text-2xl">&times;</button>
                  </div>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Selectați un destinatar pentru a trimite un email de test cu newsletter-ul curent.
                    </p>
                    <div>
                      <label className="text-sm text-muted-foreground">Selectează din baza de date</label>
                      <select
                        value={newsletterTestEmail}
                        onChange={e => setNewsletterTestEmail(e.target.value)}
                        className="w-full h-10 px-3 border border-input bg-background rounded-md mt-1"
                      >
                        <option value="">-- Selectează destinatar --</option>
                        <optgroup label="Subscribers">
                          {subscribers.slice(0, 30).map((sub, i) => (
                            <option key={`sub-${i}`} value={sub.email}>{sub.email}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Clients">
                          {clients.slice(0, 30).map((client, i) => (
                            <option key={`client-${i}`} value={client.email}>{client.name} ({client.email})</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Sau introducă manual</label>
                      <Input
                        type="email"
                        value={newsletterTestEmail}
                        onChange={e => setNewsletterTestEmail(e.target.value)}
                        placeholder="email@example.com"
                        className="mt-1"
                      />
                    </div>
                    <div className="bg-muted/50 p-3 rounded text-sm">
                      <p><strong>Subject:</strong> {newsletter.subject}</p>
                      <p className="text-muted-foreground truncate"><strong>Content:</strong> {newsletter.content?.substring(0, 100)}...</p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowNewsletterTestModal(false)}>Anulează</Button>
                      <Button 
                        onClick={sendNewsletterTestEmail}
                        disabled={sendingNewsletterTest || !newsletterTestEmail}
                      >
                        {sendingNewsletterTest ? '⏳ Se trimite...' : '📧 Trimite Test'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-2xl">Messages ({messages.length})</h2>
              <div className="flex gap-2">
                {selectedMessageIds.length > 0 && (
                  <Button variant="destructive" size="sm" onClick={deleteSelectedMessages}>
                    🗑️ Delete Selected ({selectedMessageIds.length})
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    if (selectedMessageIds.length === messages.length) {
                      setSelectedMessageIds([]);
                    } else {
                      setSelectedMessageIds(messages.map(m => m.id));
                    }
                  }}
                >
                  {selectedMessageIds.length === messages.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
            </div>

            {messages.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p className="text-4xl mb-2">📭</p>
                <p>No messages yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map(msg => (
                  <div 
                    key={msg.id} 
                    className={`border rounded-lg p-4 ${msg.is_read ? 'bg-muted/30' : 'bg-blue-50 dark:bg-blue-950/20 border-blue-200'}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedMessageIds.includes(msg.id)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedMessageIds([...selectedMessageIds, msg.id]);
                          } else {
                            setSelectedMessageIds(selectedMessageIds.filter(id => id !== msg.id));
                          }
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="font-medium">{msg.name}</span>
                            <span className="text-muted-foreground text-sm ml-2">&lt;{msg.email}&gt;</span>
                            {!msg.is_read && <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded">NEW</span>}
                            {msg.replied_at && <span className="ml-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded">REPLIED</span>}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(msg.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="font-medium text-sm mb-1">{msg.subject}</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">{msg.message}</p>
                        <div className="flex gap-2 mt-3">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => { setReplyingMessage(msg); setReplyContent(''); }}
                          >
                            ✉️ Reply
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteMessage(msg.id)}
                          >
                            🗑️ Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Reply Modal */}
            {replyingMessage && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background rounded-lg max-w-lg w-full p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="font-heading text-xl">Reply to {replyingMessage.name}</h2>
                    <button onClick={() => setReplyingMessage(null)} className="text-2xl">&times;</button>
                  </div>
                  <div className="bg-muted p-3 rounded mb-4">
                    <p className="text-sm font-medium">Original message:</p>
                    <p className="text-sm text-muted-foreground">{replyingMessage.message}</p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground">To: {replyingMessage.email}</label>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Your Reply</label>
                      <Textarea
                        value={replyContent}
                        onChange={e => setReplyContent(e.target.value)}
                        placeholder="Write your reply..."
                        rows={6}
                        className="mt-1"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Note: Reply will be sent using your configured SMTP server settings.
                    </p>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setReplyingMessage(null)}>Cancel</Button>
                      <Button onClick={sendReply} disabled={sendingReply || !replyContent.trim()}>
                        {sendingReply ? '⏳ Sending...' : '✉️ Send Reply'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* SEO TAB */}
        {activeTab === 'seo' && (
          <div className="max-w-2xl space-y-6">
            <h2 className="font-heading text-2xl">SEO Settings</h2>
            <p className="text-sm text-muted-foreground">
              Optimizați site-ul pentru motoarele de căutare și rețelele sociale.
            </p>

            {/* Basic SEO */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-heading text-lg">🔍 Meta Tags de Bază</h3>
              <div>
                <label className="text-sm text-muted-foreground">Titlu Site (tab browser)</label>
                <Input
                  value={seoSettings.site_title}
                  onChange={e => setSeoSettings({...seoSettings, site_title: e.target.value})}
                  placeholder="Elena Candalia - Modern Art Gallery"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Meta Description (descriere Google)</label>
                <Textarea
                  value={seoSettings.meta_description}
                  onChange={e => setSeoSettings({...seoSettings, meta_description: e.target.value})}
                  placeholder="Descriere scurtă pentru rezultatele Google..."
                  rows={3}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Recomandat: 150-160 caractere</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Meta Keywords (separate prin virgulă)</label>
                <Input
                  value={seoSettings.meta_keywords}
                  onChange={e => setSeoSettings({...seoSettings, meta_keywords: e.target.value})}
                  placeholder="art, painting, gallery, oil painting, italy"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Canonical URL (URL principal site)</label>
                <Input
                  value={seoSettings.canonical_url}
                  onChange={e => setSeoSettings({...seoSettings, canonical_url: e.target.value})}
                  placeholder="https://elenacandalia.it"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Open Graph */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-heading text-lg">📱 Open Graph (Social Media)</h3>
              <p className="text-xs text-muted-foreground">
                Aceste setări controlează cum apare site-ul când este distribuit pe Facebook, LinkedIn, etc.
              </p>
              <div>
                <label className="text-sm text-muted-foreground">OG Title</label>
                <Input
                  value={seoSettings.og_title}
                  onChange={e => setSeoSettings({...seoSettings, og_title: e.target.value})}
                  placeholder="Titlu pentru share pe social media"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">OG Description</label>
                <Textarea
                  value={seoSettings.og_description}
                  onChange={e => setSeoSettings({...seoSettings, og_description: e.target.value})}
                  placeholder="Descriere pentru share pe social media"
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">OG Image URL</label>
                <Input
                  value={seoSettings.og_image}
                  onChange={e => setSeoSettings({...seoSettings, og_image: e.target.value})}
                  placeholder="https://... (imagine pentru preview social)"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Robots.txt */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-heading text-lg">🤖 Robots.txt</h3>
              <Textarea
                value={seoSettings.robots_txt}
                onChange={e => setSeoSettings({...seoSettings, robots_txt: e.target.value})}
                placeholder="User-agent: *&#10;Allow: /"
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Controlează ce pagini pot fi indexate de motoarele de căutare.
              </p>
            </div>

            {/* Sitemap */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-heading text-lg">🗺️ Sitemap XML</h3>
              <p className="text-sm text-muted-foreground">
                Sitemap-ul este generat automat și include toate paginile și picturile.
              </p>
              <Button variant="outline" onClick={() => window.open(`${API}/sitemap.xml`, '_blank')}>
                📄 Vezi Sitemap
              </Button>
            </div>

            {/* Additional Scripts */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-heading text-lg">📝 Scripturi Adiționale (Head)</h3>
              <Textarea
                value={seoSettings.additional_head_scripts}
                onChange={e => setSeoSettings({...seoSettings, additional_head_scripts: e.target.value})}
                placeholder="<script>...</script> sau <meta>...</meta>"
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Adaugă scripturi Google Analytics, Facebook Pixel, etc.
              </p>
            </div>

            {/* Preview */}
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <h3 className="font-heading text-lg">👀 Preview Google</h3>
              <div className="bg-white p-4 rounded border">
                <p className="text-blue-700 text-lg hover:underline cursor-pointer">
                  {seoSettings.site_title || 'Elena Candalia - Modern Art Gallery'}
                </p>
                <p className="text-green-700 text-sm">
                  {seoSettings.canonical_url || 'https://elenacandalia.it'}
                </p>
                <p className="text-gray-600 text-sm">
                  {seoSettings.meta_description || 'Descrierea site-ului va apărea aici...'}
                </p>
              </div>
            </div>

            <Button onClick={saveSeoSettings} disabled={savingSeo} className="w-full">
              {savingSeo ? '⏳ Se salvează...' : '💾 Salvează Setările SEO'}
            </Button>
          </div>
        )}

        {/* THEME TAB */}
        {activeTab === 'theme' && (
          <div className="max-w-xl space-y-6">
            <h2 className="font-heading text-2xl">Theme Customization</h2>
            <p className="text-sm text-muted-foreground">
              Personalizați culorile site-ului și imaginea principală.
            </p>

            {/* Current Site Appearance */}
            <div className="border-2 border-primary rounded-lg p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
              <h3 className="font-heading text-lg mb-3">📌 Setări Curente pe Site</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="text-center">
                  <div 
                    className="w-12 h-12 rounded-lg border-2 border-black mx-auto mb-1"
                    style={{ backgroundColor: designSettings.text_color }}
                  />
                  <p className="text-xs text-muted-foreground">Text</p>
                  <p className="text-xs font-mono">{designSettings.text_color}</p>
                </div>
                <div className="text-center">
                  <div 
                    className="w-12 h-12 rounded-lg border-2 border-black mx-auto mb-1"
                    style={{ backgroundColor: designSettings.background_color }}
                  />
                  <p className="text-xs text-muted-foreground">Fundal</p>
                  <p className="text-xs font-mono">{designSettings.background_color}</p>
                </div>
                <div className="text-center">
                  <div 
                    className="w-12 h-12 rounded-lg border-2 border-black mx-auto mb-1"
                    style={{ backgroundColor: designSettings.button_color }}
                  />
                  <p className="text-xs text-muted-foreground">Butoane</p>
                  <p className="text-xs font-mono">{designSettings.button_color}</p>
                </div>
                <div className="text-center">
                  <div 
                    className="w-12 h-12 rounded-lg border-2 border-black mx-auto mb-1"
                    style={{ backgroundColor: designSettings.button_text_color }}
                  />
                  <p className="text-xs text-muted-foreground">Text Btn</p>
                  <p className="text-xs font-mono">{designSettings.button_text_color}</p>
                </div>
                <div className="text-center">
                  <div 
                    className="w-12 h-12 rounded-lg border-2 border-black mx-auto mb-1"
                    style={{ backgroundColor: designSettings.primary_color || '#8B2332' }}
                  />
                  <p className="text-xs text-muted-foreground">View Collection</p>
                  <p className="text-xs font-mono">{designSettings.primary_color || '#8B2332'}</p>
                </div>
              </div>
              
              {/* Current Hero Image */}
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">🖼️ Imagine Hero Curentă pe Site:</p>
                <img 
                  src={currentHeroImage || 'https://customer-assets.emergentagent.com/job_elena-art-gallery/artifacts/5pup7imy_FB_IMG_1766394920850.jpg'} 
                  alt="Current hero on site" 
                  className="w-full h-40 object-cover rounded-lg border-2 border-black"
                  onError={(e) => {e.target.src='https://customer-assets.emergentagent.com/job_elena-art-gallery/artifacts/5pup7imy_FB_IMG_1766394920850.jpg';}}
                />
                <p className="text-xs text-muted-foreground mt-1">Aceasta este imaginea afișată acum pe prima pagină</p>
              </div>
            </div>

            {/* Hero Title Settings */}
            <div className="border rounded-lg p-4 space-y-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
              <h3 className="font-heading text-lg">✨ Titlu Hero (Homepage)</h3>
              <p className="text-sm text-muted-foreground">
                Schimbă textul și culoarea titlului mare de pe prima pagină
              </p>
              
              <div className="space-y-4">
                {/* Hero Title Text */}
                <div>
                  <label className="text-sm text-muted-foreground">Text Titlu</label>
                  <Input
                    value={heroTitle}
                    onChange={e => setHeroTitle(e.target.value)}
                    placeholder="Elena Candalia"
                    className="mt-1"
                  />
                </div>
                
                {/* Hero Title Color */}
                <div>
                  <label className="text-sm text-muted-foreground">Culoare Titlu</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={heroTitleColor}
                      onChange={e => setHeroTitleColor(e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border"
                    />
                    <Input
                      value={heroTitleColor}
                      onChange={e => setHeroTitleColor(e.target.value)}
                      placeholder="#1a1a1a"
                    />
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <button onClick={() => setHeroTitleColor('#1a1a1a')} className="px-3 py-1 text-xs rounded border hover:bg-muted" style={{color: '#1a1a1a'}}>Negru</button>
                    <button onClick={() => setHeroTitleColor('#ffffff')} className="px-3 py-1 text-xs rounded border hover:bg-muted bg-gray-800 text-white">Alb</button>
                    <button onClick={() => setHeroTitleColor('#8B2332')} className="px-3 py-1 text-xs rounded border hover:bg-muted" style={{color: '#8B2332'}}>Roșu</button>
                    <button onClick={() => setHeroTitleColor('#1e3a5f')} className="px-3 py-1 text-xs rounded border hover:bg-muted" style={{color: '#1e3a5f'}}>Albastru</button>
                    <button onClick={() => setHeroTitleColor('#2d5016')} className="px-3 py-1 text-xs rounded border hover:bg-muted" style={{color: '#2d5016'}}>Verde</button>
                    <button onClick={() => setHeroTitleColor('#6b4423')} className="px-3 py-1 text-xs rounded border hover:bg-muted" style={{color: '#6b4423'}}>Maro</button>
                    <button onClick={() => setHeroTitleColor('#d4af37')} className="px-3 py-1 text-xs rounded border hover:bg-muted" style={{color: '#d4af37'}}>Auriu</button>
                  </div>
                </div>
                
                {/* Hero Button Text */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="text-sm text-muted-foreground">Text Buton (EN)</label>
                    <Input
                      value={heroButtonEn}
                      onChange={e => setHeroButtonEn(e.target.value)}
                      placeholder="View Collection"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Text Buton (IT)</label>
                    <Input
                      value={heroButtonIt}
                      onChange={e => setHeroButtonIt(e.target.value)}
                      placeholder="Scopri la Collezione"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                {/* Preview */}
                <div className="mt-4 p-6 rounded-lg relative overflow-hidden" style={{background: `url(${currentHeroImage || 'https://customer-assets.emergentagent.com/job_elena-art-gallery/artifacts/5pup7imy_FB_IMG_1766394920850.jpg'}) center/cover`}}>
                  <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/50 to-white/80"></div>
                  <div className="relative text-center">
                    <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                    <h2 
                      className="font-heading text-3xl md:text-4xl tracking-tight"
                      style={{ color: heroTitleColor }}
                    >
                      {heroTitle || 'Elena Candalia'}
                    </h2>
                    <p className="text-sm mt-2 text-muted-foreground">
                      EN: {heroButtonEn || 'View Collection'} | IT: {heroButtonIt || 'Scopri la Collezione'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-heading text-lg">🎨 Modifică Culorile</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Culoare Text</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={designSettings.text_color}
                      onChange={e => setDesignSettings({...designSettings, text_color: e.target.value})}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={designSettings.text_color}
                      onChange={e => setDesignSettings({...designSettings, text_color: e.target.value})}
                      placeholder="#1a1a1a"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Culoare Fundal</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={designSettings.background_color}
                      onChange={e => setDesignSettings({...designSettings, background_color: e.target.value})}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={designSettings.background_color}
                      onChange={e => setDesignSettings({...designSettings, background_color: e.target.value})}
                      placeholder="#ffffff"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Culoare Butoane</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={designSettings.button_color}
                      onChange={e => setDesignSettings({...designSettings, button_color: e.target.value})}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={designSettings.button_color}
                      onChange={e => setDesignSettings({...designSettings, button_color: e.target.value})}
                      placeholder="#1a1a1a"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Culoare Text Butoane</label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={designSettings.button_text_color}
                      onChange={e => setDesignSettings({...designSettings, button_text_color: e.target.value})}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={designSettings.button_text_color}
                      onChange={e => setDesignSettings({...designSettings, button_text_color: e.target.value})}
                      placeholder="#ffffff"
                    />
                  </div>
                </div>
              </div>
              
              {/* Primary Color - View Collection button */}
              <div className="pt-4 border-t">
                <label className="text-sm text-muted-foreground">🔴 Culoare Buton "View Collection"</label>
                <p className="text-xs text-muted-foreground mb-2">Butonul principal de pe prima pagină</p>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={designSettings.primary_color || '#8B2332'}
                    onChange={e => setDesignSettings({...designSettings, primary_color: e.target.value})}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={designSettings.primary_color || '#8B2332'}
                    onChange={e => setDesignSettings({...designSettings, primary_color: e.target.value})}
                    placeholder="#8B2332"
                  />
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-heading text-lg">🖼️ Schimbă Imaginea Hero</h3>
              <p className="text-sm text-muted-foreground">
                Încarcă o imagine nouă pentru prima pagină sau introdu un URL.
              </p>
              
              <div>
                <label className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md cursor-pointer hover:bg-primary/90 w-fit">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  {uploadingHeroImage ? '⏳ Se încarcă...' : 'Încarcă Imagine Nouă'}
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setUploadingHeroImage(true);
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const res = await axios.post(`${API}/upload-image`, formData, {
                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                        });
                        const imageUrl = `${BACKEND_URL}${res.data.url}`;
                        setCoverImageUrl(imageUrl);
                        toast.success('Imagine încărcată!');
                      } catch (err) {
                        toast.error('Eroare la încărcare');
                      } finally {
                        setUploadingHeroImage(false);
                      }
                    }}
                    disabled={uploadingHeroImage}
                  />
                </label>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Sau introduceți URL</label>
                <Input
                  value={coverImageUrl}
                  onChange={e => setCoverImageUrl(e.target.value)}
                  placeholder="https://... (link imagine)"
                  className="mt-1"
                />
              </div>
              {coverImageUrl && (
                <div className="relative">
                  <p className="text-sm text-muted-foreground mb-2">Preview imagine nouă:</p>
                  <img 
                    src={coverImageUrl} 
                    alt="Hero preview" 
                    className="w-full h-48 object-cover rounded"
                    onError={(e) => e.target.style.display='none'}
                  />
                  <button
                    onClick={() => setCoverImageUrl('')}
                    className="absolute top-8 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            {/* Watermark Protection */}
            <div className="border rounded-lg p-4 space-y-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
              <h3 className="font-heading text-lg">🔒 Protecție Imagini (Watermark)</h3>
              <p className="text-sm text-muted-foreground">
                Adaugă un text subtil pe diagonală peste imagini pentru a preveni copierea.
              </p>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="watermark-enabled"
                  checked={watermarkEnabled}
                  onChange={e => setWatermarkEnabled(e.target.checked)}
                  className="w-5 h-5 rounded"
                />
                <label htmlFor="watermark-enabled" className="text-sm font-medium cursor-pointer">
                  Activează watermark pe imagini
                </label>
              </div>
              
              {watermarkEnabled && (
                <div className="space-y-4 pt-2">
                  <div>
                    <label className="text-sm text-muted-foreground">Text Watermark</label>
                    <Input
                      value={watermarkText}
                      onChange={e => setWatermarkText(e.target.value)}
                      placeholder="ex: Elena Candalia © 2024"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-muted-foreground">Opacitate: {watermarkOpacity}%</label>
                    <input
                      type="range"
                      min="5"
                      max="40"
                      value={watermarkOpacity}
                      onChange={e => setWatermarkOpacity(parseInt(e.target.value))}
                      className="w-full mt-1"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Foarte subtil</span>
                      <span>Mai vizibil</span>
                    </div>
                  </div>
                  
                  {/* Watermark Preview */}
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Preview watermark:</p>
                    <div className="relative w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_elena-art-gallery/artifacts/5pup7imy_FB_IMG_1766394920850.jpg" 
                        alt="Watermark preview" 
                        className="w-full h-full object-cover"
                      />
                      {watermarkText && (
                        <div 
                          className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
                        >
                          <div 
                            className="absolute whitespace-nowrap text-center font-heading tracking-widest select-none"
                            style={{
                              fontSize: '24px',
                              color: `rgba(255, 255, 255, ${watermarkOpacity / 100})`,
                              textShadow: `0 0 10px rgba(0, 0, 0, ${watermarkOpacity / 100})`,
                              transform: 'rotate(-35deg) scale(2)',
                              letterSpacing: '8px'
                            }}
                          >
                            {Array(5).fill(watermarkText).join('     ')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-heading text-lg">👀 Preview</h3>
              <div 
                className="p-4 rounded"
                style={{ backgroundColor: designSettings.background_color, color: designSettings.text_color }}
              >
                <p className="font-heading text-lg mb-2">Exemplu text titlu</p>
                <p className="mb-4">Acesta este un exemplu de text normal pe site.</p>
                <button
                  style={{ 
                    backgroundColor: designSettings.button_color, 
                    color: designSettings.button_text_color 
                  }}
                  className="px-4 py-2 rounded"
                >
                  Exemplu Buton
                </button>
              </div>
            </div>

            <Button onClick={saveDesignSettings} disabled={savingDesign} className="w-full">
              {savingDesign ? '⏳ Se salvează...' : '💾 Salvează Setările de Design'}
            </Button>
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl mb-6">Manage Categories</h2>
            <p className="text-sm text-muted-foreground mb-6">Add, edit, or delete gallery filter categories. These categories will appear as filters in the Gallery page.</p>
            
            {/* Add New Category Form */}
            <div className="bg-muted p-4 rounded-lg mb-6">
              <h3 className="font-body uppercase tracking-wider text-sm mb-4">Add New Category</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Name (English)</label>
                  <Input
                    value={newCategory.name}
                    onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
                    placeholder="e.g., Landscape"
                    data-testid="new-category-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Name (Italian)</label>
                  <Input
                    value={newCategory.name_it}
                    onChange={e => setNewCategory({ ...newCategory, name_it: e.target.value })}
                    placeholder="e.g., Paesaggio"
                    data-testid="new-category-name-it"
                  />
                </div>
              </div>
              <Button
                onClick={async () => {
                  if (!newCategory.name) {
                    toast.error('Category name is required');
                    return;
                  }
                  try {
                    const res = await axios.post(`${API}/admin/categories`, newCategory, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    setCategories([...categories, res.data]);
                    setNewCategory({ name: '', name_it: '' });
                    toast.success('Category added!');
                  } catch (err) {
                    toast.error('Failed to add category');
                  }
                }}
                data-testid="add-category-btn"
              >
                <Plus size={16} className="mr-2" /> Add Category
              </Button>
            </div>
            
            {/* Categories List */}
            <div className="space-y-3">
              {categories.map(cat => (
                <div key={cat.id} className="flex items-center gap-4 bg-card p-4 border border-border rounded-lg">
                  {editingCategory?.id === cat.id ? (
                    <>
                      <div className="flex-1 grid grid-cols-2 gap-4">
                        <Input
                          value={editingCategory.name}
                          onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })}
                          placeholder="Name (English)"
                        />
                        <Input
                          value={editingCategory.name_it}
                          onChange={e => setEditingCategory({ ...editingCategory, name_it: e.target.value })}
                          placeholder="Name (Italian)"
                        />
                      </div>
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/categories/${cat.id}`, editingCategory, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            setCategories(categories.map(c => c.id === cat.id ? { ...c, ...editingCategory } : c));
                            setEditingCategory(null);
                            toast.success('Category updated!');
                          } catch (err) {
                            toast.error('Failed to update category');
                          }
                        }}
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingCategory(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1">
                        <span className="font-body font-medium">{cat.name}</span>
                        {cat.name_it && <span className="text-muted-foreground text-sm ml-2">({cat.name_it})</span>}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingCategory({ id: cat.id, name: cat.name, name_it: cat.name_it || '' })}
                        data-testid={`edit-category-${cat.id}`}
                        className="flex items-center gap-1"
                      >
                        <Edit size={14} />
                        <span>Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={async () => {
                          if (!window.confirm(`Delete category "${cat.name}"?`)) return;
                          try {
                            await axios.delete(`${API}/admin/categories/${cat.id}`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            setCategories(categories.filter(c => c.id !== cat.id));
                            toast.success('Category deleted!');
                          } catch (err) {
                            toast.error('Failed to delete category');
                          }
                        }}
                        data-testid={`delete-category-${cat.id}`}
                        className="flex items-center gap-1"
                      >
                        <Trash2 size={14} />
                        <span>Delete</span>
                      </Button>
                    </>
                  )}
                </div>
              ))}
              {categories.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No categories yet. Add your first category above.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-xl">
            <h2 className="font-heading text-2xl mb-6">Site Settings</h2>
            <div className="space-y-6">
              
              {/* Navigation & Menu Settings */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🧭</span>
                  <h3 className="font-heading text-lg">Navigare & Meniu</h3>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Personalizează vizibilitatea și numele butoanelor din meniu.
                </p>
                
                <div className="space-y-4">
                  {/* Visibility Toggles */}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="home-visible"
                        checked={homeVisible}
                        onChange={e => setHomeVisible(e.target.checked)}
                        className="w-5 h-5"
                      />
                      <label htmlFor="home-visible" className="text-sm cursor-pointer">
                        Arată butonul "Home"
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="whatsapp-visible"
                        checked={whatsappVisible}
                        onChange={e => setWhatsappVisible(e.target.checked)}
                        className="w-5 h-5"
                      />
                      <label htmlFor="whatsapp-visible" className="text-sm cursor-pointer">
                        Arată sigla WhatsApp
                      </label>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  {/* Menu Labels */}
                  <div>
                    <h4 className="font-medium text-sm mb-3">Nume Butoane Meniu</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Home */}
                      <div>
                        <label className="text-xs text-muted-foreground">Home (EN)</label>
                        <Input
                          value={menuHomeEn}
                          onChange={e => setMenuHomeEn(e.target.value)}
                          placeholder="Home"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Home (IT)</label>
                        <Input
                          value={menuHomeIt}
                          onChange={e => setMenuHomeIt(e.target.value)}
                          placeholder="Home"
                          className="mt-1"
                        />
                      </div>
                      {/* Gallery */}
                      <div>
                        <label className="text-xs text-muted-foreground">Gallery (EN)</label>
                        <Input
                          value={menuGalleryEn}
                          onChange={e => setMenuGalleryEn(e.target.value)}
                          placeholder="Gallery"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Gallery (IT)</label>
                        <Input
                          value={menuGalleryIt}
                          onChange={e => setMenuGalleryIt(e.target.value)}
                          placeholder="Galleria"
                          className="mt-1"
                        />
                      </div>
                      {/* About */}
                      <div>
                        <label className="text-xs text-muted-foreground">About (EN)</label>
                        <Input
                          value={menuAboutEn}
                          onChange={e => setMenuAboutEn(e.target.value)}
                          placeholder="About"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">About (IT)</label>
                        <Input
                          value={menuAboutIt}
                          onChange={e => setMenuAboutIt(e.target.value)}
                          placeholder="Chi Sono"
                          className="mt-1"
                        />
                      </div>
                      {/* Contact */}
                      <div>
                        <label className="text-xs text-muted-foreground">Contact (EN)</label>
                        <Input
                          value={menuContactEn}
                          onChange={e => setMenuContactEn(e.target.value)}
                          placeholder="Contact"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Contact (IT)</label>
                        <Input
                          value={menuContactIt}
                          onChange={e => setMenuContactIt(e.target.value)}
                          placeholder="Contatti"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={saveCoverImage} 
                    disabled={savingCover}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                  >
                    {savingCover ? '⏳ Se salvează...' : '💾 Salvează Setări Navigare'}
                  </Button>
                </div>
              </div>
              
              <Separator />
              
              {/* Security & Anti-Bot Settings */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🛡️</span>
                  <h3 className="font-heading text-lg">Security & Anti-Bot</h3>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Protejează site-ul de boți, scrapere și comenzi false.
                </p>
                
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">🤖 Blochează Boți Cunoscuți</p>
                        <p className="text-xs text-muted-foreground">HeadlessChrome, Selenium, Scrapers</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={securitySettings?.block_bots || false}
                        onChange={e => setSecuritySettings({...securitySettings, block_bots: e.target.checked})}
                        className="w-5 h-5"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">🌐 Blochează VPN/Datacenter</p>
                        <p className="text-xs text-muted-foreground">Google Cloud, AWS, DigitalOcean, etc.</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={securitySettings?.block_vpn_datacenter || false}
                        onChange={e => setSecuritySettings({...securitySettings, block_vpn_datacenter: e.target.checked})}
                        className="w-5 h-5"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">🕵️ Blochează User-Agent Suspicious</p>
                        <p className="text-xs text-muted-foreground">Requests fără browser valid</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={securitySettings?.block_suspicious_ua || false}
                        onChange={e => setSecuritySettings({...securitySettings, block_suspicious_ua: e.target.checked})}
                        className="w-5 h-5"
                      />
                    </div>
                  </div>
                  
                  {/* Blocked IPs */}
                  {securitySettings?.blocked_ips?.length > 0 && (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg">
                      <p className="font-medium text-sm mb-2">🚫 IP-uri Blocate ({securitySettings.blocked_ips.length})</p>
                      <div className="space-y-1">
                        {securitySettings.blocked_ips.map((ip, i) => (
                          <div key={i} className="flex justify-between items-center text-xs">
                            <span className="font-mono">{ip}</span>
                            <button 
                              onClick={async () => {
                                await axios.delete(`${API}/admin/unblock-ip/${ip}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setSecuritySettings({
                                  ...securitySettings, 
                                  blocked_ips: securitySettings.blocked_ips.filter(x => x !== ip)
                                });
                                toast.success(`IP ${ip} deblocat`);
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              ❌ Unblock
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/admin/security-settings`, securitySettings, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success('Setări de securitate salvate!');
                      } catch (err) {
                        toast.error('Eroare la salvare');
                      }
                    }}
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    🛡️ Salvează Setări Securitate
                  </Button>
                  
                  <p className="text-xs text-muted-foreground text-center">
                    💡 Poți bloca rapid un IP din secțiunea "Active Shopping Carts"
                  </p>
                </div>
              </div>
              
              <Separator />
              
              {/* Stripe Payment Settings */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20">
                <div className="flex items-center gap-2 mb-4">
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                  <h3 className="font-heading text-lg">Stripe Payment Settings</h3>
                  <span className={`text-xs px-2 py-1 rounded ${stripeKeyType === 'live' ? 'bg-green-100 text-green-800' : stripeKeyType === 'test' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                    {stripeKeyType === 'live' ? '🟢 LIVE' : stripeKeyType === 'test' ? '🟡 TEST' : '⚪ Not Set'}
                  </span>
                </div>
                
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Stripe API Key</label>
                  <Input
                    type="text"
                    value={stripeApiKey}
                    onChange={e => setStripeApiKey(e.target.value)}
                    placeholder="sk_live_... or sk_test_..."
                    className="mt-2 font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Get your API key from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Stripe Dashboard</a>
                  </p>
                </div>
                
                <Button 
                  onClick={saveStripeSettings} 
                  disabled={savingStripe || !stripeApiKey}
                  className="mt-4 bg-purple-600 hover:bg-purple-700"
                >
                  {savingStripe ? '⏳ Saving...' : '💳 Save Stripe Key'}
                </Button>
              </div>
              
              <Separator />
              
              {/* PayPal Settings */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">💳</span>
                  <h3 className="font-heading text-lg">PayPal Settings</h3>
                  <span className={`text-xs px-2 py-1 rounded ${paypalSettings.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {paypalSettings.enabled ? '🟢 ENABLED' : '⚪ DISABLED'}
                  </span>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Activează PayPal ca metodă de plată alternativă pe site.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="paypal-enabled"
                      checked={paypalSettings.enabled}
                      onChange={e => setPaypalSettings({...paypalSettings, enabled: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <label htmlFor="paypal-enabled" className="font-medium cursor-pointer">
                      Activează PayPal
                    </label>
                  </div>
                  
                  {paypalSettings.enabled && (
                    <>
                      <div>
                        <label className="text-sm text-muted-foreground">PayPal.me Link (recomandat)</label>
                        <Input
                          type="url"
                          value={paypalSettings.paypal_me_link}
                          onChange={e => setPaypalSettings({...paypalSettings, paypal_me_link: e.target.value})}
                          placeholder="https://paypal.me/username"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Link-ul tău PayPal.me - clienții vor fi redirecționați aici pentru plată
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">PayPal Seller Email (opțional)</label>
                        <Input
                          type="email"
                          value={paypalSettings.seller_email}
                          onChange={e => setPaypalSettings({...paypalSettings, seller_email: e.target.value})}
                          placeholder="seller@paypal.com"
                          className="mt-1"
                        />
                      </div>
                    </>
                  )}
                </div>
                
                <Button 
                  onClick={savePaypalSettings}
                  disabled={savingPaypal}
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                >
                  {savingPaypal ? '⏳ Saving...' : '💾 Save PayPal Settings'}
                </Button>
              </div>
              
              <Separator />
              
              {/* Revolut Settings */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🔄</span>
                  <h3 className="font-heading text-lg">Revolut Settings</h3>
                  <span className={`text-xs px-2 py-1 rounded ${revolutSettings.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {revolutSettings.enabled ? '🟢 ENABLED' : '⚪ DISABLED'}
                  </span>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Activează Revolut ca metodă de plată alternativă pe site.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="revolut-enabled"
                      checked={revolutSettings.enabled}
                      onChange={e => setRevolutSettings({...revolutSettings, enabled: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <label htmlFor="revolut-enabled" className="font-medium cursor-pointer">
                      Activează Revolut
                    </label>
                  </div>
                  
                  {revolutSettings.enabled && (
                    <>
                      <div>
                        <label className="text-sm text-muted-foreground">Revolut Payment Link</label>
                        <Input
                          type="url"
                          value={revolutSettings.revolut_link}
                          onChange={e => setRevolutSettings({...revolutSettings, revolut_link: e.target.value})}
                          placeholder="https://revolut.me/username"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Link-ul tău Revolut.me - clienții vor fi redirecționați aici pentru plată
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Revolut Tag (opțional)</label>
                        <Input
                          type="text"
                          value={revolutSettings.revolut_tag}
                          onChange={e => setRevolutSettings({...revolutSettings, revolut_tag: e.target.value})}
                          placeholder="@username"
                          className="mt-1"
                        />
                      </div>
                    </>
                  )}
                </div>
                
                <Button 
                  onClick={saveRevolutSettings}
                  disabled={savingRevolut}
                  className="mt-4 bg-purple-600 hover:bg-purple-700"
                >
                  {savingRevolut ? '⏳ Saving...' : '💾 Save Revolut Settings'}
                </Button>
              </div>
              
              <Separator />

              {/* Report Language / Limba Raport Cheltuieli */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20" data-testid="report-language-section">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">&#127760;</span>
                  <h3 className="font-heading text-lg">Limba Raport Cheltuieli</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Alege limba pentru rapoartele PDF si CSV exportate (pentru contabil).
                </p>
                <div className="flex items-center gap-3">
                  <select
                    value={reportLanguage}
                    onChange={async (e) => {
                      const lang = e.target.value;
                      setReportLanguage(lang);
                      try {
                        await axios.put(`${API}/admin/expense-settings`, { report_language: lang }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success(lang === 'it' ? 'Lingua salvata: Italiano' : lang === 'ro' ? 'Limba salvata: Romana' : 'Language saved: English');
                      } catch { toast.error('Errore'); }
                    }}
                    className="h-10 px-3 border border-input bg-background rounded-md w-48"
                    data-testid="report-language-select"
                  >
                    <option value="it">Italiano</option>
                    <option value="ro">Romana</option>
                    <option value="en">English</option>
                  </select>
                  <span className="text-sm text-muted-foreground">
                    {reportLanguage === 'it' ? 'Rapporto Spese' : reportLanguage === 'ro' ? 'Raport Cheltuieli' : 'Expense Report'}
                  </span>
                </div>
              </div>
              
              <Separator />
              
              {/* Email Settings (SMTP/IMAP) */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">📧</span>
                  <h3 className="font-heading text-lg">Email Settings (SMTP/IMAP)</h3>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Configurează setările email pentru trimitere de pe site (newsletter, notificări, facturi)
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* SMTP Settings */}
                  <div className="space-y-3">
                    <p className="font-medium text-sm">📤 SMTP (Trimitere)</p>
                    <div>
                      <label className="text-xs text-muted-foreground">SMTP Host</label>
                      <Input
                        value={emailSettings.smtp_host}
                        onChange={e => setEmailSettings({...emailSettings, smtp_host: e.target.value})}
                        placeholder="smtp.gmail.com"
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground">Port</label>
                        <Input
                          type="number"
                          value={emailSettings.smtp_port}
                          onChange={e => setEmailSettings({...emailSettings, smtp_port: parseInt(e.target.value) || 587})}
                          placeholder="587"
                          className="mt-1"
                        />
                      </div>
                      <div className="flex items-end pb-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={emailSettings.smtp_use_tls}
                            onChange={e => setEmailSettings({...emailSettings, smtp_use_tls: e.target.checked})}
                          />
                          <span className="text-sm">Use TLS</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Username/Email</label>
                      <Input
                        value={emailSettings.smtp_user}
                        onChange={e => setEmailSettings({...emailSettings, smtp_user: e.target.value})}
                        placeholder="your@email.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Password</label>
                      <Input
                        type="password"
                        value={emailSettings.smtp_password}
                        onChange={e => setEmailSettings({...emailSettings, smtp_password: e.target.value})}
                        placeholder="••••••••"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  {/* IMAP Settings */}
                  <div className="space-y-3">
                    <p className="font-medium text-sm">📥 IMAP (Primire - opțional)</p>
                    <div>
                      <label className="text-xs text-muted-foreground">IMAP Host</label>
                      <Input
                        value={emailSettings.imap_host}
                        onChange={e => setEmailSettings({...emailSettings, imap_host: e.target.value})}
                        placeholder="imap.gmail.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Port</label>
                      <Input
                        type="number"
                        value={emailSettings.imap_port}
                        onChange={e => setEmailSettings({...emailSettings, imap_port: parseInt(e.target.value) || 993})}
                        placeholder="993"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Username/Email</label>
                      <Input
                        value={emailSettings.imap_user}
                        onChange={e => setEmailSettings({...emailSettings, imap_user: e.target.value})}
                        placeholder="your@email.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Password</label>
                      <Input
                        type="password"
                        value={emailSettings.imap_password}
                        onChange={e => setEmailSettings({...emailSettings, imap_password: e.target.value})}
                        placeholder="••••••••"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
                
                {/* From Settings */}
                <div className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
                  <div>
                    <label className="text-xs text-muted-foreground">From Email</label>
                    <Input
                      value={emailSettings.from_email}
                      onChange={e => setEmailSettings({...emailSettings, from_email: e.target.value})}
                      placeholder="noreply@elenacandalia.it"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">From Name</label>
                    <Input
                      value={emailSettings.from_name}
                      onChange={e => setEmailSettings({...emailSettings, from_name: e.target.value})}
                      placeholder="Elena Candalia Art"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-4">
                  <Button 
                    onClick={saveEmailSettings}
                    disabled={savingEmailSettings}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {savingEmailSettings ? '⏳ Saving...' : '💾 Save Email Settings'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowTestEmailModal(true)}
                    disabled={testingEmail || !emailSettings.smtp_host}
                  >
                    {testingEmail ? '⏳ Testing...' : '📧 Send Test Email'}
                  </Button>
                </div>
                
                {/* Test Email Modal */}
                {showTestEmailModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-background rounded-lg max-w-md w-full p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="font-heading text-xl">Test Email</h2>
                        <button onClick={() => setShowTestEmailModal(false)} className="text-2xl">&times;</button>
                      </div>
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Selectați un destinatar pentru email-ul de test sau introduceți un email manual.
                        </p>
                        <div>
                          <label className="text-sm text-muted-foreground">Selectează din baza de date</label>
                          <select
                            value={testEmailRecipient}
                            onChange={e => setTestEmailRecipient(e.target.value)}
                            className="w-full h-10 px-3 border border-input bg-background rounded-md mt-1"
                          >
                            <option value="">-- Selectează destinatar --</option>
                            <optgroup label="Subscribers">
                              {subscribers.slice(0, 20).map((sub, i) => (
                                <option key={`sub-${i}`} value={sub.email}>{sub.email}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Clients">
                              {clients.slice(0, 20).map((client, i) => (
                                <option key={`client-${i}`} value={client.email}>{client.name} ({client.email})</option>
                              ))}
                            </optgroup>
                          </select>
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground">Sau introducă manual</label>
                          <Input
                            type="email"
                            value={testEmailRecipient}
                            onChange={e => setTestEmailRecipient(e.target.value)}
                            placeholder="email@example.com"
                            className="mt-1"
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" onClick={() => setShowTestEmailModal(false)}>Anulează</Button>
                          <Button 
                            onClick={sendTestEmailToRecipient}
                            disabled={testingEmail || !testEmailRecipient}
                          >
                            {testingEmail ? '⏳ Se trimite...' : '📧 Trimite Test'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <Separator />

              {/* Order Confirmation Email Template */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-950/20 dark:to-pink-950/20" data-testid="order-email-template-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">&#9993;</span>
                    <h3 className="font-heading text-lg">Email Conferma Ordine</h3>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowEmailPreview(!showEmailPreview)} data-testid="preview-email-btn">
                    {showEmailPreview ? 'Chiudi Preview' : 'Preview'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Variabili disponibili: <code className="bg-muted px-1 rounded">{'{customer_name}'}</code> <code className="bg-muted px-1 rounded">{'{order_id}'}</code> <code className="bg-muted px-1 rounded">{'{total}'}</code> <code className="bg-muted px-1 rounded">{'{items}'}</code> <code className="bg-muted px-1 rounded">{'{address}'}</code> <code className="bg-muted px-1 rounded">{'{site_name}'}</code>
                </p>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Oggetto (IT)</label>
                      <Input value={orderEmailTpl.subject_it} onChange={e => setOrderEmailTpl({...orderEmailTpl, subject_it: e.target.value})} className="mt-1" data-testid="email-subject-it" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Subject (EN)</label>
                      <Input value={orderEmailTpl.subject_en} onChange={e => setOrderEmailTpl({...orderEmailTpl, subject_en: e.target.value})} className="mt-1" data-testid="email-subject-en" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Saluto (IT)</label>
                      <Input value={orderEmailTpl.greeting_it} onChange={e => setOrderEmailTpl({...orderEmailTpl, greeting_it: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Greeting (EN)</label>
                      <Input value={orderEmailTpl.greeting_en} onChange={e => setOrderEmailTpl({...orderEmailTpl, greeting_en: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Messaggio (IT)</label>
                      <Textarea value={orderEmailTpl.message_it} onChange={e => setOrderEmailTpl({...orderEmailTpl, message_it: e.target.value})} rows={4} className="mt-1" data-testid="email-message-it" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Message (EN)</label>
                      <Textarea value={orderEmailTpl.message_en} onChange={e => setOrderEmailTpl({...orderEmailTpl, message_en: e.target.value})} rows={4} className="mt-1" data-testid="email-message-en" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Footer (IT)</label>
                      <Textarea value={orderEmailTpl.footer_it} onChange={e => setOrderEmailTpl({...orderEmailTpl, footer_it: e.target.value})} rows={2} className="mt-1" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Footer (EN)</label>
                      <Textarea value={orderEmailTpl.footer_en} onChange={e => setOrderEmailTpl({...orderEmailTpl, footer_en: e.target.value})} rows={2} className="mt-1" />
                    </div>
                  </div>
                  <Button onClick={saveOrderEmailTemplate} disabled={savingOrderEmail} data-testid="save-email-template-btn">
                    {savingOrderEmail ? 'Salvataggio...' : 'Salva Template Email'}
                  </Button>
                </div>

                {/* Color Customization */}
                <div className="mt-6 border-t pt-4">
                  <h4 className="font-medium text-sm mb-3">Personalizza Colori Email</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Banner Start</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={orderEmailTpl.color_banner || '#8B4513'} onChange={e => setOrderEmailTpl({...orderEmailTpl, color_banner: e.target.value})} className="w-8 h-8 rounded cursor-pointer border" data-testid="color-banner" />
                        <span className="text-xs text-muted-foreground">{orderEmailTpl.color_banner}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Banner End</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={orderEmailTpl.color_banner_end || '#A0522D'} onChange={e => setOrderEmailTpl({...orderEmailTpl, color_banner_end: e.target.value})} className="w-8 h-8 rounded cursor-pointer border" data-testid="color-banner-end" />
                        <span className="text-xs text-muted-foreground">{orderEmailTpl.color_banner_end}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Accent (Prezzo)</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={orderEmailTpl.color_accent || '#8B4513'} onChange={e => setOrderEmailTpl({...orderEmailTpl, color_accent: e.target.value})} className="w-8 h-8 rounded cursor-pointer border" data-testid="color-accent" />
                        <span className="text-xs text-muted-foreground">{orderEmailTpl.color_accent}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Background</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={orderEmailTpl.color_bg || '#ffffff'} onChange={e => setOrderEmailTpl({...orderEmailTpl, color_bg: e.target.value})} className="w-8 h-8 rounded cursor-pointer border" data-testid="color-bg" />
                        <span className="text-xs text-muted-foreground">{orderEmailTpl.color_bg}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Testo</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={orderEmailTpl.color_text || '#333333'} onChange={e => setOrderEmailTpl({...orderEmailTpl, color_text: e.target.value})} className="w-8 h-8 rounded cursor-pointer border" data-testid="color-text" />
                        <span className="text-xs text-muted-foreground">{orderEmailTpl.color_text}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Footer BG</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={orderEmailTpl.color_footer_bg || '#f5f0eb'} onChange={e => setOrderEmailTpl({...orderEmailTpl, color_footer_bg: e.target.value})} className="w-8 h-8 rounded cursor-pointer border" data-testid="color-footer-bg" />
                        <span className="text-xs text-muted-foreground">{orderEmailTpl.color_footer_bg}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Card BG</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={orderEmailTpl.color_card_bg || '#faf7f2'} onChange={e => setOrderEmailTpl({...orderEmailTpl, color_card_bg: e.target.value})} className="w-8 h-8 rounded cursor-pointer border" data-testid="color-card-bg" />
                        <span className="text-xs text-muted-foreground">{orderEmailTpl.color_card_bg}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Card Border</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={orderEmailTpl.color_card_border || '#e8dfd5'} onChange={e => setOrderEmailTpl({...orderEmailTpl, color_card_border: e.target.value})} className="w-8 h-8 rounded cursor-pointer border" data-testid="color-card-border" />
                        <span className="text-xs text-muted-foreground">{orderEmailTpl.color_card_border}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Le modifiche ai colori si vedranno nel Preview e nelle email inviate. Salva il template per applicare.</p>
                </div>

                {/* Email Preview */}
                {showEmailPreview && (
                  <div className="mt-4 border rounded-lg overflow-hidden" data-testid="email-preview">
                    <div className="bg-muted px-3 py-1.5 text-xs text-muted-foreground">Preview Email</div>
                    <div className="bg-white p-4" dangerouslySetInnerHTML={{ __html: `
                      <div style="font-family: Georgia, serif; max-width: 100%;">
                        <div style="background: linear-gradient(135deg, ${orderEmailTpl.color_banner || '#8B4513'} 0%, ${orderEmailTpl.color_banner_end || '#A0522D'} 100%); padding: 20px; text-align: center;">
                          <h1 style="color: #fff; margin: 0; font-size: 20px; letter-spacing: 2px;">Elena Candalia Art</h1>
                        </div>
                        <div style="padding: 20px; background: ${orderEmailTpl.color_bg || '#ffffff'};">
                          <p style="font-size: 14px; color: ${orderEmailTpl.color_text || '#333'};">${(orderEmailTpl.greeting_it || '').replace(/{customer_name}/g, 'Marco Rossi')}</p>
                          <p style="font-size: 13px; color: ${orderEmailTpl.color_text || '#333'}; opacity: 0.8; line-height: 1.6; white-space: pre-line;">${(orderEmailTpl.message_it || '').replace(/{customer_name}/g, 'Marco Rossi').replace(/{order_id}/g, 'abc123').replace(/{total}/g, '€3,200.00').replace(/{items}/g, 'La Dama in Rosso').replace(/{address}/g, 'Via Roma 1, Roma')}</p>
                          <div style="background: ${orderEmailTpl.color_card_bg || '#faf7f2'}; border: 1px solid ${orderEmailTpl.color_card_border || '#e8dfd5'}; border-radius: 8px; padding: 15px; margin: 15px 0;">
                            <table style="width: 100%; font-size: 13px; color: ${orderEmailTpl.color_text || '#444'};">
                              <tr><td style="padding:5px 0; font-weight:bold;">Ordine:</td><td>#abc123</td></tr>
                              <tr><td style="padding:5px 0; font-weight:bold;">Opere:</td><td>La Dama in Rosso</td></tr>
                              <tr><td style="padding:5px 0; font-weight:bold;">Totale:</td><td style="font-size:16px; font-weight:bold; color:${orderEmailTpl.color_accent || '#8B4513'};">€3,200.00</td></tr>
                              <tr><td style="padding:5px 0; font-weight:bold;">Indirizzo:</td><td>Via Roma 1, Roma</td></tr>
                            </table>
                          </div>
                          <hr style="border:none; border-top:1px solid #eee; margin:15px 0;">
                          <p style="font-size: 14px; color: ${orderEmailTpl.color_text || '#333'};">${(orderEmailTpl.greeting_en || '').replace(/{customer_name}/g, 'Marco Rossi')}</p>
                          <p style="font-size: 13px; color: ${orderEmailTpl.color_text || '#333'}; opacity: 0.8; line-height: 1.6; white-space: pre-line;">${(orderEmailTpl.message_en || '').replace(/{customer_name}/g, 'Marco Rossi').replace(/{order_id}/g, 'abc123').replace(/{total}/g, '€3,200.00').replace(/{items}/g, 'La Dama in Rosso').replace(/{address}/g, 'Via Roma 1, Roma')}</p>
                          <hr style="border:none; border-top:1px solid #eee; margin:15px 0;">
                          <p style="font-size: 11px; color: #999; white-space: pre-line;">${(orderEmailTpl.footer_it || '')}\n\n${(orderEmailTpl.footer_en || '')}</p>
                        </div>
                        <div style="background: ${orderEmailTpl.color_footer_bg || '#f5f0eb'}; padding: 10px; text-align: center;">
                          <p style="font-size: 10px; color: #888; margin: 0;">Elena Candalia Art</p>
                        </div>
                      </div>
                    ` }} />
                  </div>
                )}
              </div>
              
              <Separator />

              <div>
                <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Cover Image URL</label>
                <Input
                  value={coverImageUrl}
                  onChange={e => setCoverImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="mt-2"
                  data-testid="cover-image-input"
                />
                <p className="text-xs text-muted-foreground mt-1">This image will be displayed as the hero/cover on the homepage</p>
              </div>
              {coverImageUrl && (
                <div>
                  <label className="text-sm font-body uppercase tracking-wider text-muted-foreground mb-2 block">Preview</label>
                  <img 
                    src={coverImageUrl} 
                    alt="Cover preview" 
                    className="w-full max-w-md h-48 object-cover border border-border"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              <Separator />
              <div>
                <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">WhatsApp Number</label>
                <Input
                  value={whatsappNumber}
                  onChange={e => setWhatsappNumber(e.target.value)}
                  placeholder="+393206215750"
                  className="mt-2"
                  data-testid="whatsapp-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Include country code (e.g., +39 for Italy). This number will be used for the WhatsApp button.</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Footer Copyright Text</label>
                <Input
                  value={footerText}
                  onChange={e => setFooterText(e.target.value)}
                  placeholder="© 2024 Elena Candalia"
                  className="mt-2"
                />
                <p className="text-xs text-muted-foreground mt-1">This text appears in the website footer (e.g., "© 2024 Elena Candalia")</p>
              </div>
              <Button onClick={saveCoverImage} disabled={savingCover} data-testid="save-settings-btn">
                {savingCover ? 'Saving...' : 'Save Settings'}
              </Button>
              
              <Separator />
              
              {/* Full Backup Section */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">💾</span>
                  <h3 className="font-heading text-lg">Full Backup</h3>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Create a complete backup of your project including:
                </p>
                <ul className="text-sm text-muted-foreground mb-4 space-y-1">
                  <li>✅ Database (all collections)</li>
                  <li>✅ Backend code (server.py, requirements.txt)</li>
                  <li>✅ Frontend code (src/, public/, package.json)</li>
                  <li>✅ Uploaded media files</li>
                  <li>✅ Configuration files (.env)</li>
                </ul>
                
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={createFullBackup}
                    disabled={creatingBackup}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {creatingBackup ? '⏳ Creating Backup...' : '📦 Create Full Backup'}
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowBackupList(!showBackupList);
                      if (!showBackupList) fetchBackups();
                    }}
                  >
                    📋 {showBackupList ? 'Hide' : 'View'} Previous Backups
                  </Button>
                </div>
                
                {/* Previous Backups List */}
                {showBackupList && (
                  <div className="mt-4 space-y-2">
                    {backups.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No previous backups found</p>
                    ) : (
                      backups.map((backup, idx) => (
                        <div key={idx} className="bg-background border rounded p-3 flex justify-between items-center">
                          <div>
                            <p className="font-mono text-sm">{backup.filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {backup.size_mb} MB • {new Date(backup.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => downloadBackup(backup.filename)}
                            >
                              📥 Download
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-500"
                              onClick={() => deleteBackup(backup.filename)}
                            >
                              🗑️
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                
                {/* Restore Backup */}
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium mb-2">🔄 Restore Backup</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".zip"
                      onChange={(e) => restoreBackup(e.target.files?.[0])}
                      disabled={restoringBackup}
                      className="text-sm"
                      id="restore-backup-input"
                    />
                    {restoringBackup && <span className="text-sm">⏳ Restaurare în curs...</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Încarcă un fișier .zip pentru a restaura backup-ul
                  </p>
                </div>
              </div>
              
              <Separator />
              
              {/* File Explorer */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-950/20 dark:to-gray-950/20">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">📂</span>
                    <h3 className="font-heading text-lg">File Explorer (FTP Structure)</h3>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowFileExplorer(!showFileExplorer);
                      if (!showFileExplorer && !fileStructure) fetchFileStructure();
                    }}
                  >
                    {showFileExplorer ? 'Ascunde' : '👁️ Vezi Structura'}
                  </Button>
                </div>
                
                {showFileExplorer && (
                  <div className="space-y-4">
                    {loadingFiles ? (
                      <p className="text-muted-foreground">Se încarcă...</p>
                    ) : fileStructure ? (
                      <>
                        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                          <span>📍 Root: {fileStructure.root}</span>
                          <span>{fileStructure.stats.total_folders} foldere, {fileStructure.stats.total_files} fișiere</span>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* File Tree */}
                          <div className="border rounded p-3 bg-background max-h-96 overflow-auto">
                            <p className="text-xs text-muted-foreground mb-2 font-mono">/app/</p>
                            {renderFileTree(fileStructure.structure)}
                          </div>
                          
                          {/* File Content Preview */}
                          <div className="border rounded p-3 bg-background max-h-96 overflow-auto">
                            {selectedFile ? (
                              <>
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs text-muted-foreground font-mono truncate flex-1">{selectedFile}</p>
                                  <div className="flex gap-1">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => {
                                        navigator.clipboard.writeText(fileContent || '');
                                        toast.success('Conținut copiat!');
                                      }}
                                      title="Copiază conținut"
                                    >
                                      📋
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => {
                                        const blob = new Blob([fileContent || ''], { type: 'text/plain' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = selectedFile.split('/').pop();
                                        a.click();
                                        URL.revokeObjectURL(url);
                                        toast.success('Fișier descărcat!');
                                      }}
                                      title="Descarcă fișier"
                                    >
                                      ⬇️
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => {setSelectedFile(null); setFileContent(null);}}
                                    >
                                      ✕
                                    </Button>
                                  </div>
                                </div>
                                <pre className="text-xs font-mono whitespace-pre-wrap bg-muted p-2 rounded overflow-auto max-h-80">
                                  {fileContent || 'Se încarcă...'}
                                </pre>
                              </>
                            ) : (
                              <div className="text-center text-muted-foreground py-8">
                                <p>👆 Click pe un fișier pentru a vedea conținutul</p>
                                <p className="text-xs mt-2">Apoi poți copia 📋 sau descărca ⬇️</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={fetchFileStructure}
                        >
                          🔄 Refresh
                        </Button>
                      </>
                    ) : (
                      <p className="text-muted-foreground">Click "Vezi Structura" pentru a încărca</p>
                    )}
                  </div>
                )}
              </div>
              
              <Separator />
              
              {/* Modular Backup (VPS Portable) */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🚀</span>
                  <h3 className="font-heading text-lg">Backup Modular (VPS Portable)</h3>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Creează un backup portabil, restaurabil pe orice VPS Linux, fără dependențe Emergent.
                </p>
                
                {/* Component Selection */}
                <div className="space-y-3 mb-4">
                  <label className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modularBackupOptions.include_db}
                      onChange={e => setModularBackupOptions({...modularBackupOptions, include_db: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <div>
                      <span className="font-medium">📊 Export Database</span>
                      <p className="text-xs text-muted-foreground">MongoDB JSON dump + script restaurare</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modularBackupOptions.include_uploads}
                      onChange={e => setModularBackupOptions({...modularBackupOptions, include_uploads: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <div>
                      <span className="font-medium">🖼️ Export Uploads</span>
                      <p className="text-xs text-muted-foreground">Toate fișierele media încărcate</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modularBackupOptions.include_config}
                      onChange={e => setModularBackupOptions({...modularBackupOptions, include_config: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <div>
                      <span className="font-medium">⚙️ Export Config</span>
                      <p className="text-xs text-muted-foreground">Template .env (fără secrete expuse)</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer border-t pt-3">
                    <input
                      type="checkbox"
                      checked={modularBackupOptions.include_backend}
                      onChange={e => setModularBackupOptions({...modularBackupOptions, include_backend: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <div>
                      <span className="font-medium">🐍 Export Backend (opțional)</span>
                      <p className="text-xs text-muted-foreground">Cod server.py modificat pentru VPS (fără emergentintegrations)</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modularBackupOptions.include_frontend}
                      onChange={e => setModularBackupOptions({...modularBackupOptions, include_frontend: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <div>
                      <span className="font-medium">⚛️ Export Frontend (opțional)</span>
                      <p className="text-xs text-muted-foreground">Cod sursă React + package.json</p>
                    </div>
                  </label>
                </div>
                
                <div className="bg-muted/50 rounded p-3 mb-4 text-sm">
                  <p className="font-medium mb-1">📦 Backup-ul va include:</p>
                  <ul className="text-muted-foreground space-y-1">
                    {modularBackupOptions.include_db && <li>✅ Baza de date (JSON + script restaurare)</li>}
                    {modularBackupOptions.include_uploads && <li>✅ Fișiere media (uploads/)</li>}
                    {modularBackupOptions.include_config && <li>✅ Configurație (.env template)</li>}
                    {modularBackupOptions.include_backend && <li>✅ Backend (server.py portabil)</li>}
                    {modularBackupOptions.include_frontend && <li>✅ Frontend (src/, package.json)</li>}
                    <li>✅ README.md + DEPLOYMENT.md (instrucțiuni)</li>
                  </ul>
                </div>
                
                <Button 
                  onClick={createModularBackup}
                  disabled={creatingModularBackup || (!modularBackupOptions.include_db && !modularBackupOptions.include_uploads && !modularBackupOptions.include_config)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {creatingModularBackup ? '⏳ Se creează backup-ul...' : '🚀 Creează Backup Portabil'}
                </Button>
              </div>
              
              <Separator />
              
              {/* Gallery Data Backup */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🖼️</span>
                  <h3 className="font-heading text-lg">Gallery Data Backup</h3>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Backup complet cu toate datele galeriei: facturi, picturi, clienți, comenzi și toate imaginile.
                </p>
                
                <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border mb-4">
                  <p className="font-medium text-sm mb-2">Include:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>📄 Facturi (invoices)</li>
                    <li>🎨 Picturi (paintings)</li>
                    <li>👥 Clienți (clients)</li>
                    <li>📦 Comenzi (orders)</li>
                    <li>📧 Newsletter subscribers</li>
                    <li>💬 Mesaje contact</li>
                    <li>🖼️ Toate imaginile (uploads/)</li>
                    <li>⚙️ Setări site</li>
                  </ul>
                </div>
                
                <Button 
                  onClick={async () => {
                    setCreatingGalleryBackup(true);
                    try {
                      const res = await axios.post(`${API}/admin/backup/gallery`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Gallery backup created! Downloading...');
                      
                      // Download the backup with auth header
                      const downloadRes = await axios.get(`${API}${res.data.backup_file}`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      });
                      
                      // Create download link
                      const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', res.data.filename);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      
                      toast.success('✅ Backup descărcat!');
                    } catch (err) {
                      console.error('Gallery backup error:', err);
                      toast.error('Failed to create gallery backup: ' + (err.response?.data?.detail || err.message));
                    } finally {
                      setCreatingGalleryBackup(false);
                    }
                  }}
                  disabled={creatingGalleryBackup}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  {creatingGalleryBackup ? '⏳ Se creează backup-ul...' : '🖼️ Creează Gallery Backup'}
                </Button>
              </div>
              
              <Separator />
              
              {/* VPS Deployment Schema */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">📋</span>
                  <h3 className="font-heading text-lg">Schema Deployment VPS</h3>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Documentație completă pentru a instala galeria pe propriul server VPS Linux.
                </p>
                
                <div className="space-y-4">
                  {/* Architecture */}
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border">
                    <h4 className="font-medium text-sm mb-3">🏗️ Arhitectură</h4>
                    <pre className="text-xs font-mono bg-muted p-3 rounded overflow-x-auto whitespace-pre">{`
┌─────────────────────────────────────────────────┐
│                   NGINX (Port 80/443)           │
│              (Reverse Proxy + SSL)              │
└─────────────┬──────────────────┬────────────────┘
              │                  │
      ┌───────▼───────┐  ┌───────▼───────┐
      │   Frontend    │  │    Backend    │
      │  React:3000   │  │  FastAPI:8001 │
      │   (Static)    │  │   (API /api)  │
      └───────────────┘  └───────┬───────┘
                                 │
                         ┌───────▼───────┐
                         │   MongoDB     │
                         │  Port: 27017  │
                         └───────────────┘
`}</pre>
                  </div>
                  
                  {/* Requirements */}
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border">
                    <h4 className="font-medium text-sm mb-3">📦 Cerințe Server</h4>
                    <div className="grid md:grid-cols-2 gap-4 text-xs">
                      <div>
                        <p className="font-medium mb-1">Minimum:</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li>• Ubuntu 20.04+ / Debian 11+</li>
                          <li>• 1 CPU, 1GB RAM, 20GB SSD</li>
                          <li>• Domeniu cu DNS configurat</li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium mb-1">Software:</p>
                        <ul className="text-muted-foreground space-y-1">
                          <li>• Python 3.10+</li>
                          <li>• Node.js 18+ & Yarn</li>
                          <li>• MongoDB 6.0+</li>
                          <li>• Nginx + Certbot (SSL)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  {/* Installation Script */}
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm">🔧 Script Instalare (1/4)</h4>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(`#!/bin/bash
# Elena Art Gallery - VPS Setup Script
# Run as root: sudo bash setup.sh

set -e
echo "🎨 Elena Art Gallery - VPS Setup"

# Update system
apt update && apt upgrade -y

# Install dependencies
apt install -y python3 python3-pip python3-venv nodejs npm nginx certbot python3-certbot-nginx git curl

# Install yarn
npm install -g yarn

# Install MongoDB
curl -fsSL https://www.mongodb.org/static/pgp/server-6.0.asc | gpg --dearmor -o /usr/share/keyrings/mongodb-server-6.0.gpg
echo "deb [ signed-by=/usr/share/keyrings/mongodb-server-6.0.gpg ] http://repo.mongodb.org/apt/ubuntu jammy/mongodb-org/6.0 multiverse" | tee /etc/apt/sources.list.d/mongodb-org-6.0.list
apt update && apt install -y mongodb-org
systemctl enable mongod && systemctl start mongod

echo "✅ Dependencies installed!"`);
                          toast.success('Script copiat!');
                        }}
                      >
                        📋 Copiază
                      </Button>
                    </div>
                    <pre className="text-xs font-mono bg-muted p-3 rounded overflow-x-auto max-h-48">{`#!/bin/bash
# Elena Art Gallery - VPS Setup Script
# Run as root: sudo bash setup.sh

set -e
echo "🎨 Elena Art Gallery - VPS Setup"

# Update system
apt update && apt upgrade -y

# Install dependencies
apt install -y python3 python3-pip python3-venv nodejs npm nginx certbot python3-certbot-nginx git curl

# Install yarn
npm install -g yarn

# Install MongoDB
curl -fsSL https://www.mongodb.org/static/pgp/server-6.0.asc | \\
  gpg --dearmor -o /usr/share/keyrings/mongodb-server-6.0.gpg
echo "deb [ signed-by=/usr/share/keyrings/mongodb-server-6.0.gpg ] \\
  http://repo.mongodb.org/apt/ubuntu jammy/mongodb-org/6.0 multiverse" \\
  | tee /etc/apt/sources.list.d/mongodb-org-6.0.list
apt update && apt install -y mongodb-org
systemctl enable mongod && systemctl start mongod

echo "✅ Dependencies installed!"`}</pre>
                  </div>
                  
                  {/* Deploy Script */}
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm">📂 Script Deploy App (2/4)</h4>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(`#!/bin/bash
# Deploy Elena Art Gallery
# Run as: bash deploy.sh

APP_DIR="/var/www/elena-gallery"
DOMAIN="yourdomain.com"

# Create directories
mkdir -p $APP_DIR/{backend,frontend}
cd $APP_DIR

# Backend setup
cd backend
python3 -m venv venv
source venv/bin/activate
pip install fastapi uvicorn motor python-multipart bcrypt pyjwt aiofiles python-dotenv httpx

# Create .env
cat > .env << 'EOF'
MONGO_URL=mongodb://localhost:27017
DB_NAME=elena_gallery
JWT_SECRET=$(openssl rand -hex 32)
STRIPE_API_KEY=your_stripe_key_here
EOF

# Frontend setup
cd ../frontend
yarn install
yarn build

# Create systemd service
cat > /etc/systemd/system/elena-backend.service << 'EOF'
[Unit]
Description=Elena Gallery Backend
After=network.target mongod.service

[Service]
Type=simple
User=www-data
WorkingDirectory=/var/www/elena-gallery/backend
Environment=PATH=/var/www/elena-gallery/backend/venv/bin
ExecStart=/var/www/elena-gallery/backend/venv/bin/uvicorn server:app --host 0.0.0.0 --port 8001
Restart=always

[Install]
WantedBy=multi-user.target
EOF

systemctl daemon-reload
systemctl enable elena-backend
systemctl start elena-backend

echo "✅ App deployed!"`);
                          toast.success('Script copiat!');
                        }}
                      >
                        📋 Copiază
                      </Button>
                    </div>
                    <pre className="text-xs font-mono bg-muted p-3 rounded overflow-x-auto max-h-48">{`#!/bin/bash
# Deploy Elena Art Gallery
APP_DIR="/var/www/elena-gallery"

# Create directories
mkdir -p $APP_DIR/{backend,frontend}
cd $APP_DIR

# Backend setup
cd backend
python3 -m venv venv
source venv/bin/activate
pip install fastapi uvicorn motor python-multipart \\
  bcrypt pyjwt aiofiles python-dotenv httpx

# Create .env file
cat > .env << 'EOF'
MONGO_URL=mongodb://localhost:27017
DB_NAME=elena_gallery
JWT_SECRET=$(openssl rand -hex 32)
STRIPE_API_KEY=your_stripe_key_here
EOF

# Frontend build
cd ../frontend
yarn install && yarn build

# Create systemd service for backend
# (see full script for details)`}</pre>
                  </div>
                  
                  {/* Nginx Config */}
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm">🌐 Config Nginx (3/4)</h4>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(`# /etc/nginx/sites-available/elena-gallery
server {
    listen 80;
    server_name yourdomain.com www.yourdomain.com;
    
    # Frontend (React build)
    root /var/www/elena-gallery/frontend/build;
    index index.html;
    
    # API Proxy to Backend
    location /api/ {
        proxy_pass http://127.0.0.1:8001/api/;
        proxy_http_version 1.1;
        proxy_set_header Upgrade $http_upgrade;
        proxy_set_header Connection 'upgrade';
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
        proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
        proxy_cache_bypass $http_upgrade;
        client_max_body_size 50M;
    }
    
    # Uploads directory
    location /uploads/ {
        alias /var/www/elena-gallery/backend/uploads/;
    }
    
    # React Router - serve index.html for all routes
    location / {
        try_files $uri $uri/ /index.html;
    }
    
    # Gzip compression
    gzip on;
    gzip_types text/plain text/css application/json application/javascript text/xml application/xml;
}

# Enable site:
# ln -s /etc/nginx/sites-available/elena-gallery /etc/nginx/sites-enabled/
# nginx -t && systemctl reload nginx

# SSL with Certbot:
# certbot --nginx -d yourdomain.com -d www.yourdomain.com`);
                          toast.success('Config Nginx copiat!');
                        }}
                      >
                        📋 Copiază
                      </Button>
                    </div>
                    <pre className="text-xs font-mono bg-muted p-3 rounded overflow-x-auto max-h-48">{`# /etc/nginx/sites-available/elena-gallery
server {
    listen 80;
    server_name yourdomain.com;
    
    # Frontend (React build)
    root /var/www/elena-gallery/frontend/build;
    index index.html;
    
    # API Proxy to Backend
    location /api/ {
        proxy_pass http://127.0.0.1:8001/api/;
        proxy_http_version 1.1;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
        client_max_body_size 50M;
    }
    
    # Uploads
    location /uploads/ {
        alias /var/www/elena-gallery/backend/uploads/;
    }
    
    # React Router
    location / {
        try_files $uri $uri/ /index.html;
    }
}

# SSL: certbot --nginx -d yourdomain.com`}</pre>
                  </div>
                  
                  {/* Database Structure */}
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm">🗃️ Structură Database MongoDB (4/4)</h4>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(`// MongoDB Collections Structure
// Database: elena_gallery

// 1. PAINTINGS - Operele de artă
{
  id: "uuid",
  title: "La Dama in Rosso",
  description: "Oil on canvas...",
  price: 2500,
  technique: "Oil on Canvas",
  dimensions: "80x100 cm",
  year: 2024,
  category: "Portraits",
  image_url: "/uploads/painting.jpg",
  additional_images: [],
  is_sold: false,
  is_featured: true,
  created_at: "2024-01-15T..."
}

// 2. ORDERS - Comenzi
{
  id: "uuid",
  painting_id: "...",
  customer_name: "John Doe",
  customer_email: "john@email.com",
  customer_phone: "+39...",
  shipping_address: "...",
  region: "italy",
  subtotal: 2500,
  shipping: 50,
  total: 2550,
  payment_status: "paid",
  stripe_session_id: "...",
  created_at: "..."
}

// 3. INVOICES - Facturi
{
  id: "uuid",
  invoice_number: "INV-2024-001",
  customer_name: "...",
  items: [{ description: "...", quantity: 1, price: 2500 }],
  subtotal: 2500,
  shipping: 50,
  tax_rate: 22,
  total: 3111,
  payment_method: "stripe",
  status: "paid",
  created_at: "..."
}

// 4. NEWSLETTER - Abonați
{
  email: "subscriber@email.com",
  subscribed_at: "..."
}

// 5. CONTACTS - Mesaje formular contact
{
  id: "uuid",
  name: "...",
  email: "...",
  subject: "...",
  message: "...",
  is_read: false,
  created_at: "..."
}

// 6. SETTINGS - Diverse setări
// Types: site_settings, email_settings, paypal_settings, seo_settings, design_settings
{
  type: "site_settings",
  cover_image_url: "...",
  whatsapp_number: "+39...",
  footer_text: "© 2024 Elena Candalia"
}

// 7. USERS - Utilizatori admin
{
  id: "uuid",
  email: "admin",
  password: "bcrypt_hash",
  name: "Elena Candalia",
  is_admin: true,
  created_at: "..."
}`);
                          toast.success('Structură DB copiată!');
                        }}
                      >
                        📋 Copiază
                      </Button>
                    </div>
                    <pre className="text-xs font-mono bg-muted p-3 rounded overflow-x-auto max-h-48">{`// MongoDB Collections
// Database: elena_gallery

paintings {
  id, title, description, price, technique,
  dimensions, year, category, image_url,
  additional_images[], is_sold, is_featured
}

orders {
  id, painting_id, customer_name, customer_email,
  shipping_address, region, subtotal, shipping,
  total, payment_status, stripe_session_id
}

invoices {
  id, invoice_number, customer_name, items[],
  subtotal, shipping, tax_rate, total,
  payment_method, status
}

newsletter { email, subscribed_at }

contacts { id, name, email, subject, message, is_read }

settings { type, ...dynamic_fields }
// Types: site_settings, email_settings, 
//   paypal_settings, seo_settings, design_settings

users { id, email, password, name, is_admin }`}</pre>
                  </div>
                  
                  {/* Quick Commands */}
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border">
                    <h4 className="font-medium text-sm mb-3">⚡ Comenzi Rapide</h4>
                    <div className="grid md:grid-cols-2 gap-2 text-xs font-mono">
                      <div className="bg-muted p-2 rounded">
                        <p className="text-muted-foreground mb-1"># Start backend</p>
                        <code>systemctl start elena-backend</code>
                      </div>
                      <div className="bg-muted p-2 rounded">
                        <p className="text-muted-foreground mb-1"># View logs</p>
                        <code>journalctl -u elena-backend -f</code>
                      </div>
                      <div className="bg-muted p-2 rounded">
                        <p className="text-muted-foreground mb-1"># Restart nginx</p>
                        <code>systemctl reload nginx</code>
                      </div>
                      <div className="bg-muted p-2 rounded">
                        <p className="text-muted-foreground mb-1"># MongoDB shell</p>
                        <code>mongosh elena_gallery</code>
                      </div>
                      <div className="bg-muted p-2 rounded">
                        <p className="text-muted-foreground mb-1"># SSL certificate</p>
                        <code>certbot --nginx -d domain.com</code>
                      </div>
                      <div className="bg-muted p-2 rounded">
                        <p className="text-muted-foreground mb-1"># Backup DB</p>
                        <code>mongodump --db elena_gallery</code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DEPLOY TAB */}
        {activeTab === 'deploy' && (
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl mb-6">Deploy & Export</h2>
            
            <div className="space-y-6">
              {/* Deploy Package */}
              <div className="border rounded-lg p-6 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">🚀</span>
                  <div>
                    <h3 className="font-heading text-xl">Proiect Complet (ZIP)</h3>
                    <p className="text-sm text-muted-foreground">Ready to Deploy pe VPS</p>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Descarca arhiva completa cu tot proiectul pregatit pentru instalare pe VPS via SSH:
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/70 dark:bg-black/20 rounded-lg p-4">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <span>📁</span> Backend
                    </h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>server.py + models.py + database.py</li>
                      <li>requirements.txt + .env.example</li>
                      <li>uploads/ (toate imaginile)</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white/70 dark:bg-black/20 rounded-lg p-4">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <span>🌐</span> Frontend
                    </h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>build/ (pre-built, gata de servit)</li>
                      <li>.env.example cu domeniu</li>
                      <li className="text-emerald-600 font-medium">Nu necesita yarn/npm build!</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white/70 dark:bg-black/20 rounded-lg p-4">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <span>💾</span> Baza de Date
                    </h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>Export MongoDB complet (JSON)</li>
                      <li>Paintings, Settings, Clients, etc.</li>
                      <li className="text-emerald-600 font-medium">Import automat la instalare!</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white/70 dark:bg-black/20 rounded-lg p-4">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <span>⚙️</span> Configurare
                    </h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>nginx.conf (SSL + proxy)</li>
                      <li>systemd service (auto-restart)</li>
                      <li>install.sh (instalare automata)</li>
                    </ul>
                  </div>
                </div>
                
                <Button 
                  onClick={async () => {
                    try {
                      toast.loading('Se genereaza pachetul...', { id: 'deploy' });
                      const res = await axios.get(`${API}/admin/deploy/package`, {
                        headers: { Authorization: `Bearer ${token}` },
                        timeout: 300000
                      });
                      toast.loading('Se descarca...', { id: 'deploy' });
                      // Download the file via blob - download_url already has /api prefix
                      const downloadRes = await axios.get(`${BACKEND_URL}${res.data.download_url}`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob',
                        timeout: 300000
                      });
                      const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = res.data.filename;
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success(`Descarcat: ${res.data.filename} (${res.data.size_mb} MB)`, { id: 'deploy' });
                    } catch (err) {
                      console.error('Deploy error:', err);
                      toast.error(err.response?.data?.detail || 'Eroare la generare/descarcare', { id: 'deploy' });
                    }
                  }}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  size="lg"
                  data-testid="download-deploy-package"
                >
                  Descarca Proiect Complet (ZIP)
                </Button>
                
                <p className="text-xs text-center text-muted-foreground mt-3">
                  ✅ Arhiva include frontend-ul pre-built - instalare directă fără reconstruire!
                </p>
              </div>
              
              {/* Info Box */}
              <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/20">
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <span>💡</span> Cum sa instalezi pe VPS (SSH)
                </h4>
                <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                  <li>Conecteaza-te la server: <code className="bg-black/10 px-1 rounded">ssh root@ip-server</code></li>
                  <li>Incarca ZIP-ul: <code className="bg-black/10 px-1 rounded">scp elena_gallery_deploy_*.zip root@ip-server:/tmp/</code></li>
                  <li>Dezarhiveaza: <code className="bg-black/10 px-1 rounded">cd /tmp && unzip elena_gallery_deploy_*.zip</code></li>
                  <li>Ruleaza instalarea: <code className="bg-black/10 px-1 rounded">sudo bash install.sh</code></li>
                  <li>Introdu domeniul si email-ul (pentru SSL)</li>
                  <li>Editeaza <code className="bg-black/10 px-1 rounded">/var/www/elena-gallery/backend/.env</code> cu cheile API</li>
                  <li>Restart: <code className="bg-black/10 px-1 rounded">sudo systemctl restart elena-gallery</code></li>
                </ol>
              </div>
              
              {/* Requirements */}
              <div className="border rounded-lg p-4 bg-amber-50 dark:bg-amber-950/20">
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <span>📋</span> Cerinte Server
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li><strong>Linux</strong> (Ubuntu 20.04+, Debian 11+, CentOS 8+, Fedora, Arch)</li>
                  <li>Minim 1GB RAM + domeniu cu DNS configurat</li>
                  <li>Acces root (sudo) - scriptul instaleaza automat: MongoDB, Nginx, Python, SSL</li>
                </ul>
              </div>
              
              {/* Post-install */}
              <div className="border rounded-lg p-4 bg-emerald-50 dark:bg-emerald-950/20">
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <span>🔑</span> Dupa Instalare - Editeaza .env
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li><code className="bg-black/10 px-1 rounded">JWT_SECRET</code> - Schimba cu un string random</li>
                  <li><code className="bg-black/10 px-1 rounded">STRIPE_API_KEY</code> - Cheia Stripe (pentru plati)</li>
                  <li><code className="bg-black/10 px-1 rounded">RESEND_API_KEY</code> - Cheia Resend (pentru emailuri)</li>
                  <li><code className="bg-black/10 px-1 rounded">EMERGENT_LLM_KEY</code> - Cheia AI (pentru extragere facturi)</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <h2 className="font-heading text-2xl">Orders & Invoices</h2>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-80">
                  <input
                    type="text"
                    placeholder="Search by name, email, phone, or order..."
                    value={orderSearchQuery}
                    onChange={e => { setOrderSearchQuery(e.target.value); setOrderPage(1); setInvoicePage(1); }}
                    className="w-full h-10 pl-10 pr-4 border border-input bg-background rounded-md text-sm"
                  />
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <Button onClick={() => startNewInvoice()}>
                  + Create New Invoice
                </Button>
                <Button variant="outline" onClick={() => setShowExportModal(true)}>
                  📊 Export Invoices
                </Button>
              </div>
            </div>

            {/* Export Invoices Modal */}
            {showExportModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="font-heading text-2xl">Export Invoices</h2>
                    <button onClick={() => setShowExportModal(false)} className="text-2xl">&times;</button>
                  </div>
                  
                  {/* Select Year and Month */}
                  <div className="border rounded-lg p-4 mb-4 bg-muted/30">
                    <p className="font-semibold mb-3">Select Year & Month</p>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-sm text-muted-foreground">Year</label>
                        <select
                          value={selectedYear}
                          onChange={e => setSelectedYear(parseInt(e.target.value))}
                          className="w-full h-10 px-3 border border-input bg-background rounded-md mt-1"
                        >
                          {getAvailableYears().map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Month</label>
                        <select
                          value={selectedMonth}
                          onChange={e => setSelectedMonth(parseInt(e.target.value))}
                          className="w-full h-10 px-3 border border-input bg-background rounded-md mt-1"
                        >
                          {monthNames.map(month => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Selected Month Stats */}
                    <div className="bg-background rounded-lg p-3 border">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {monthNames.find(m => m.value === selectedMonth)?.label} {selectedYear}
                          </p>
                          <p className="text-sm">{getInvoicesByYearMonth(selectedYear, selectedMonth).length} invoices</p>
                          <p className="text-lg font-bold text-primary">
                            Total: €{getYearMonthTotal(selectedYear, selectedMonth).toFixed(2)}
                          </p>
                        </div>
                        <Button onClick={() => exportByYearMonth(selectedYear, selectedMonth)} variant="default">
                          📥 Export Month
                        </Button>
                      </div>
                    </div>
                    
                    {/* Full Year Stats */}
                    <div className="bg-background rounded-lg p-3 border mt-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm text-muted-foreground">Full Year {selectedYear}</p>
                          <p className="text-sm">{getInvoicesByYear(selectedYear).length} invoices</p>
                          <p className="text-lg font-bold text-primary">
                            Total: €{getYearTotal(selectedYear).toFixed(2)}
                          </p>
                        </div>
                        <Button onClick={() => exportByYear(selectedYear)} variant="outline">
                          📥 Export Year
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <p className="font-semibold text-sm text-muted-foreground">Quick Export</p>
                    
                    {/* Last Week */}
                    <div className="border rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">Last Week</p>
                        <p className="text-sm text-muted-foreground">
                          {getFilteredInvoices('week').length} invoices • €{getFilteredTotal('week').toFixed(2)}
                        </p>
                      </div>
                      <Button onClick={() => exportInvoicesToCSV('week')} variant="outline" size="sm">
                        📥 CSV
                      </Button>
                    </div>
                    
                    {/* Last Month */}
                    <div className="border rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">Last 30 Days</p>
                        <p className="text-sm text-muted-foreground">
                          {getFilteredInvoices('month').length} invoices • €{getFilteredTotal('month').toFixed(2)}
                        </p>
                      </div>
                      <Button onClick={() => exportInvoicesToCSV('month')} variant="outline" size="sm">
                        📥 CSV
                      </Button>
                    </div>
                    
                    {/* Last Year */}
                    <div className="border rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">Last 12 Months</p>
                        <p className="text-sm text-muted-foreground">
                          {getFilteredInvoices('year').length} invoices • €{getFilteredTotal('year').toFixed(2)}
                        </p>
                      </div>
                      <Button onClick={() => exportInvoicesToCSV('year')} variant="outline" size="sm">
                        📥 CSV
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t">
                    <Button variant="outline" onClick={() => setShowExportModal(false)} className="w-full">
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Order Modal */}
            {showEditOrderModal && editingOrder && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="font-heading text-2xl">Edit Order</h2>
                    <button onClick={() => { setShowEditOrderModal(false); setEditingOrder(null); }} className="text-2xl">&times;</button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Customer Name</label>
                      <Input 
                        value={editingOrder.customer_name || ''} 
                        onChange={e => setEditingOrder({ ...editingOrder, customer_name: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Email</label>
                      <Input 
                        type="email"
                        value={editingOrder.customer_email || ''} 
                        onChange={e => setEditingOrder({ ...editingOrder, customer_email: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Phone</label>
                      <Input 
                        value={editingOrder.customer_phone || ''} 
                        onChange={e => setEditingOrder({ ...editingOrder, customer_phone: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Shipping Address</label>
                      <Textarea 
                        value={editingOrder.shipping_address || ''} 
                        onChange={e => setEditingOrder({ ...editingOrder, shipping_address: e.target.value })} 
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Total (€)</label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={editingOrder.total || 0} 
                        onChange={e => setEditingOrder({ ...editingOrder, total: parseFloat(e.target.value) || 0 })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Status</label>
                      <select
                        value={editingOrder.status || 'pending'}
                        onChange={e => setEditingOrder({ ...editingOrder, status: e.target.value })}
                        className="mt-1 w-full h-10 px-3 border border-input bg-background rounded-md"
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                    <Button variant="outline" onClick={() => { setShowEditOrderModal(false); setEditingOrder(null); }}>
                      Cancel
                    </Button>
                    <Button onClick={saveEditedOrder}>
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Invoice Form Modal */}
            {showInvoiceForm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="font-heading text-2xl">{editingInvoiceId ? 'Edit Invoice' : 'Create Invoice'}</h2>
                    <button onClick={() => { setShowInvoiceForm(false); setEditingInvoiceId(null); }} className="text-2xl">&times;</button>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Language</label>
                      <select
                        value={invoiceData.language}
                        onChange={e => setInvoiceData({ ...invoiceData, language: e.target.value })}
                        className="mt-2 w-full h-10 px-3 border border-input bg-background rounded-md"
                      >
                        <option value="en">English - Invoice</option>
                        <option value="it">Italian - Fattura</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Invoice Number</label>
                      <Input value={invoiceData.invoice_number} onChange={e => setInvoiceData({ ...invoiceData, invoice_number: e.target.value })} className="mt-2" />
                    </div>
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">Invoice Date</label>
                      <Input type="date" value={invoiceData.invoice_date} onChange={e => setInvoiceData({ ...invoiceData, invoice_date: e.target.value })} className="mt-2" />
                    </div>
                    <div>
                      <label className="text-sm font-body uppercase tracking-wider text-muted-foreground">{invoiceData.language === 'it' ? 'Termini di Pagamento (giorni)' : 'Payment Terms (days)'}</label>
                      <Input type="number" value={invoiceData.payment_terms} onChange={e => setInvoiceData({ ...invoiceData, payment_terms: parseInt(e.target.value) || 0 })} className="mt-2" min="0" />
                    </div>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="border rounded-lg p-4 mb-6">
                    <label className="text-sm font-body uppercase tracking-wider text-muted-foreground block mb-3">
                      {invoiceData.language === 'it' ? 'Metodo di Pagamento' : 'Payment Method'}
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-muted/50">
                        <input
                          type="radio"
                          name="payment_method"
                          value="stripe"
                          checked={invoiceData.payment_method === 'stripe'}
                          onChange={e => setInvoiceData({ ...invoiceData, payment_method: e.target.value })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">
                          {invoiceData.language === 'it' 
                            ? 'Pagamento effettuato tramite carta Debito/Credito con Stripe' 
                            : 'Payment made via Debit/Credit card with Stripe'}
                        </span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-muted/50">
                        <input
                          type="radio"
                          name="payment_method"
                          value="bank_transfer"
                          checked={invoiceData.payment_method === 'bank_transfer'}
                          onChange={e => setInvoiceData({ ...invoiceData, payment_method: e.target.value })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">
                          {invoiceData.language === 'it' 
                            ? 'Pagamento ricevuto tramite bonifico bancario' 
                            : 'Payment received via bank transfer'}
                        </span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-muted/50">
                        <input
                          type="radio"
                          name="payment_method"
                          value="cash"
                          checked={invoiceData.payment_method === 'cash'}
                          onChange={e => setInvoiceData({ ...invoiceData, payment_method: e.target.value })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">
                          {invoiceData.language === 'it' 
                            ? 'Pagamento in contanti alla consegna' 
                            : 'Cash payment on delivery'}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Customer Details */}
                  <div className="border rounded-lg p-4 mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-heading text-lg">Customer Details</h3>
                      <Button variant="outline" size="sm" onClick={() => setShowClientSelector(true)}>
                        👤 {invoiceData.language === 'it' ? 'Seleziona Cliente' : 'Select Client'}
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-muted-foreground">{invoiceData.language === 'it' ? 'Telefono (auto-compila dati cliente)' : 'Telephone (auto-fills customer data)'}</label>
                        <Input type="tel" value={invoiceData.customer_phone} onChange={e => handlePhoneChange(e.target.value)} className="mt-1" placeholder="+39 xxx xxx xxxx" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">{invoiceData.language === 'it' ? 'Nome' : 'Name'} *</label>
                        <Input value={invoiceData.customer_name} onChange={e => setInvoiceData({ ...invoiceData, customer_name: e.target.value })} className="mt-1" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Email *</label>
                        <Input type="email" value={invoiceData.customer_email} onChange={e => setInvoiceData({ ...invoiceData, customer_email: e.target.value })} className="mt-1" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">{invoiceData.language === 'it' ? 'Codice Fiscale (opzionale)' : 'Tax Code (optional)'}</label>
                        <Input value={invoiceData.customer_codice_fiscale} onChange={e => setInvoiceData({ ...invoiceData, customer_codice_fiscale: e.target.value })} className="mt-1" placeholder="RSSMRA80A01H501U" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">{invoiceData.language === 'it' ? 'Partita IVA (opzionale)' : 'VAT Number (optional)'}</label>
                        <Input value={invoiceData.customer_vat} onChange={e => setInvoiceData({ ...invoiceData, customer_vat: e.target.value })} className="mt-1" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm text-muted-foreground">{invoiceData.language === 'it' ? 'Indirizzo' : 'Address'}</label>
                        <Textarea value={invoiceData.customer_address} onChange={e => setInvoiceData({ ...invoiceData, customer_address: e.target.value })} className="mt-1" rows={2} />
                      </div>
                    </div>
                  </div>

                  {/* Client Selector Modal */}
                  {showClientSelector && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                      <div className="bg-background rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto p-6">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="font-heading text-xl">{invoiceData.language === 'it' ? 'Seleziona Cliente' : 'Select Client'}</h3>
                          <button onClick={() => setShowClientSelector(false)} className="text-2xl">&times;</button>
                        </div>
                        <input
                          type="text"
                          placeholder={invoiceData.language === 'it' ? 'Cerca per nome, email, telefono...' : 'Search by name, email, phone...'}
                          value={clientSearchQuery}
                          onChange={e => setClientSearchQuery(e.target.value)}
                          className="w-full h-10 px-4 mb-4 border border-input bg-background rounded-md text-sm"
                        />
                        <div className="space-y-2 max-h-[50vh] overflow-auto">
                          {clients
                            .filter(c => {
                              if (!clientSearchQuery) return true;
                              const q = clientSearchQuery.toLowerCase();
                              return (c.name?.toLowerCase().includes(q) || 
                                      c.email?.toLowerCase().includes(q) ||
                                      c.phone?.toLowerCase().includes(q));
                            })
                            .slice(0, 20)
                            .map(client => (
                              <div 
                                key={client.id} 
                                className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                                onClick={() => selectClientForInvoice(client)}
                              >
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium">{client.name || 'No name'}</p>
                                    <p className="text-sm text-muted-foreground">{client.email}</p>
                                    {client.phone && <p className="text-sm text-muted-foreground">{client.phone}</p>}
                                  </div>
                                  <div className="text-right text-xs text-muted-foreground">
                                    {client.invoice_count > 0 && <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded mr-1">{client.invoice_count} fatture</span>}
                                    {client.order_count > 0 && <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded">{client.order_count} ordini</span>}
                                  </div>
                                </div>
                                {client.address && <p className="text-xs text-muted-foreground mt-1 truncate">{client.address}</p>}
                              </div>
                            ))}
                          {clients.length === 0 && (
                            <p className="text-center py-8 text-muted-foreground">
                              {invoiceData.language === 'it' ? 'Nessun cliente trovato' : 'No clients found'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="border rounded-lg p-4 mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-heading text-lg">Items</h3>
                      <Button variant="outline" size="sm" onClick={addInvoiceItem}>+ Add Item</Button>
                    </div>
                    <div className="space-y-3">
                      {invoiceData.items.map((item, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-5">
                            <Input placeholder="Description" value={item.description} onChange={e => updateInvoiceItem(idx, 'description', e.target.value)} />
                          </div>
                          <div className="col-span-2">
                            <Input type="number" placeholder="Qty" value={item.quantity} onChange={e => updateInvoiceItem(idx, 'quantity', parseInt(e.target.value) || 0)} />
                          </div>
                          <div className="col-span-2">
                            <Input type="number" placeholder="Price" value={item.unit_price} onChange={e => updateInvoiceItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                          </div>
                          <div className="col-span-2 text-right font-medium">€{item.total.toFixed(2)}</div>
                          <div className="col-span-1">
                            {invoiceData.items.length > 1 && (
                              <button onClick={() => removeInvoiceItem(idx)} className="text-red-500 hover:text-red-700">×</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="text-sm text-muted-foreground">Notes (optional)</label>
                      <Textarea value={invoiceData.notes} onChange={e => setInvoiceData({ ...invoiceData, notes: e.target.value })} className="mt-1" rows={3} placeholder="Payment terms, thank you message, etc." />
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>€{invoiceData.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>Shipping:</span>
                        <Input type="number" value={invoiceData.shipping} onChange={e => updateInvoiceTotals('shipping', e.target.value)} className="w-24" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span>Tax %:</span>
                        <Input type="number" value={invoiceData.tax_rate} onChange={e => updateInvoiceTotals('tax_rate', e.target.value)} className="w-20" />
                        <span>= €{invoiceData.tax_amount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>Total:</span>
                        <span>€{invoiceData.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button onClick={saveInvoice} disabled={savingInvoice}>
                      {savingInvoice ? 'Saving...' : (editingInvoiceId ? 'Update Invoice' : 'Save Invoice')}
                    </Button>
                    <Button variant="outline" onClick={() => { setShowInvoiceForm(false); setEditingInvoiceId(null); }}>Cancel</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Orders List */}
            <div className="border rounded-lg p-6">
              <h3 className="font-heading text-xl mb-4">Recent Orders ({orders.filter(o => {
                if (!orderSearchQuery) return true;
                const q = orderSearchQuery.toLowerCase();
                return (o.customer_name?.toLowerCase().includes(q) || 
                        o.customer_email?.toLowerCase().includes(q) ||
                        o.customer_phone?.toLowerCase().includes(q) ||
                        o.id?.toLowerCase().includes(q));
              }).length})</h3>
              {orders.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No orders yet</p>
              ) : (
                <>
                  <div className="space-y-4">
                    {orders
                      .filter(o => {
                        if (!orderSearchQuery) return true;
                        const q = orderSearchQuery.toLowerCase();
                        return (o.customer_name?.toLowerCase().includes(q) || 
                                o.customer_email?.toLowerCase().includes(q) ||
                                o.customer_phone?.toLowerCase().includes(q) ||
                                o.id?.toLowerCase().includes(q));
                      })
                      .slice((orderPage - 1) * ITEMS_PER_PAGE, orderPage * ITEMS_PER_PAGE)
                      .map(order => (
                        <div key={order.id} className={`border rounded-lg p-4 ${order.payment_status === 'awaiting_payment' ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-950/10' : ''}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold">{order.customer_name}</p>
                              <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                              {order.customer_phone && <p className="text-sm text-muted-foreground">{order.customer_phone}</p>}
                              {order.shipping_address && <p className="text-xs text-muted-foreground mt-1">{order.shipping_address}</p>}
                            </div>
                            <div className="text-right">
                              <p className="font-bold">€{(order.amount || order.total || 0).toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                              {order.payment_method && order.payment_method !== 'stripe' && (
                                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 mr-1">
                                  {order.payment_method === 'paypal' ? 'PayPal' : order.payment_method === 'revolut' ? 'Revolut' : order.payment_method}
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                order.payment_status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 
                                order.payment_status === 'awaiting_payment' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                                order.payment_status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              }`}>
                                {order.payment_status === 'paid' ? 'Pagato' : 
                                 order.payment_status === 'awaiting_payment' ? 'In attesa' :
                                 order.payment_status === 'cancelled' ? 'Annullato' :
                                 order.payment_status || 'pending'}
                              </span>
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground mb-3">
                            {order.painting_titles?.join(', ') || order.items?.map((item, i) => (
                              <span key={i}>{item.title || item.name}{i < order.items.length - 1 ? ', ' : ''}</span>
                            ))}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {order.payment_status === 'awaiting_payment' && (
                              <>
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={async () => {
                                  try {
                                    await axios.put(`${API}/admin/orders/${order.id}/confirm-payment`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                    setOrders(orders.map(o => o.id === order.id ? { ...o, payment_status: 'paid' } : o));
                                    toast.success('Pagamento confermato!');
                                  } catch { toast.error('Errore'); }
                                }} data-testid={`confirm-payment-${order.id}`}>
                                  Conferma Pagamento
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-500" onClick={async () => {
                                  if (!window.confirm('Annullare questo ordine?')) return;
                                  try {
                                    await axios.put(`${API}/admin/orders/${order.id}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                    setOrders(orders.map(o => o.id === order.id ? { ...o, payment_status: 'cancelled' } : o));
                                    toast.success('Ordine annullato');
                                  } catch { toast.error('Errore'); }
                                }} data-testid={`cancel-order-${order.id}`}>
                                  Annulla
                                </Button>
                              </>
                            )}
                            {order.payment_status === 'paid' && (
                              <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50" onClick={async () => {
                                try {
                                  await axios.post(`${API}/admin/orders/${order.id}/send-confirmation`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success(`Email di conferma inviata a ${order.customer_email}`);
                                } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio email'); }
                              }} data-testid={`send-confirmation-${order.id}`}>
                                Invia Conferma
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => startNewInvoice(order)}>
                              Create Invoice
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openEditOrder(order)}>
                              Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteOrder(order.id)}>
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                  {/* Orders Pagination */}
                  {(() => {
                    const filteredOrders = orders.filter(o => {
                      if (!orderSearchQuery) return true;
                      const q = orderSearchQuery.toLowerCase();
                      return (o.customer_name?.toLowerCase().includes(q) || 
                              o.customer_email?.toLowerCase().includes(q) ||
                              o.customer_phone?.toLowerCase().includes(q) ||
                              o.id?.toLowerCase().includes(q));
                    });
                    const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
                    if (totalPages <= 1) return null;
                    return (
                      <div className="flex justify-center items-center gap-2 mt-6 pt-4 border-t">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setOrderPage(p => Math.max(1, p - 1))}
                          disabled={orderPage === 1}
                        >
                          ← Prev
                        </Button>
                        <span className="text-sm text-muted-foreground px-3">
                          Page {orderPage} of {totalPages}
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setOrderPage(p => Math.min(totalPages, p + 1))}
                          disabled={orderPage === totalPages}
                        >
                          Next →
                        </Button>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            {/* Invoices List */}
            <div className="border rounded-lg p-6">
              <h3 className="font-heading text-xl mb-4">Invoices ({invoices.filter(inv => {
                if (!orderSearchQuery) return true;
                const q = orderSearchQuery.toLowerCase();
                return (inv.customer_name?.toLowerCase().includes(q) || 
                        inv.customer_email?.toLowerCase().includes(q) ||
                        inv.customer_phone?.toLowerCase().includes(q) ||
                        inv.invoice_number?.toLowerCase().includes(q));
              }).length})</h3>
              {invoices.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No invoices created yet</p>
              ) : (
                <>
                  <div className="space-y-4">
                    {invoices
                      .filter(inv => {
                        if (!orderSearchQuery) return true;
                        const q = orderSearchQuery.toLowerCase();
                        return (inv.customer_name?.toLowerCase().includes(q) || 
                                inv.customer_email?.toLowerCase().includes(q) ||
                                inv.customer_phone?.toLowerCase().includes(q) ||
                                inv.invoice_number?.toLowerCase().includes(q));
                      })
                      .slice((invoicePage - 1) * ITEMS_PER_PAGE, invoicePage * ITEMS_PER_PAGE)
                      .map(invoice => (
                        <div key={invoice.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold">#{invoice.invoice_number}</p>
                              <p className="text-sm">{invoice.customer_name}</p>
                              <p className="text-xs text-muted-foreground">{invoice.customer_email}</p>
                              {invoice.customer_phone && <p className="text-xs text-muted-foreground">{invoice.customer_phone}</p>}
                            </div>
                            <div className="text-right">
                              <p className="font-bold">€{invoice.total?.toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">{invoice.invoice_date}</p>
                              <span className="text-xs bg-muted px-2 py-1 rounded">{invoice.language === 'it' ? '🇮🇹 IT' : '🇬🇧 EN'}</span>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <Button size="sm" variant="outline" onClick={() => editInvoice(invoice)}>
                              ✏️ Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => previewInvoice(invoice)}>
                              👁️ Preview
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => downloadInvoicePDF(invoice)}>
                              🖨️ Print/PDF
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => window.open(`${API}/invoice/${invoice.id}/qr`, '_blank')}>
                              📱 QR Code
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => window.open(`${API}/invoice/${invoice.id}?lang=${invoice.language || 'en'}`, '_blank')}>
                              🌐 Digital
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => sendInvoiceEmail(invoice.id)} disabled={sendingInvoice}>
                              📧 Send Email
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteInvoice(invoice.id)}>
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                  {/* Invoices Pagination */}
                  {(() => {
                    const filteredInvoices = invoices.filter(inv => {
                      if (!orderSearchQuery) return true;
                      const q = orderSearchQuery.toLowerCase();
                      return (inv.customer_name?.toLowerCase().includes(q) || 
                              inv.customer_email?.toLowerCase().includes(q) ||
                              inv.customer_phone?.toLowerCase().includes(q) ||
                              inv.invoice_number?.toLowerCase().includes(q));
                    });
                    const totalPages = Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE);
                    if (totalPages <= 1) return null;
                    return (
                      <div className="flex justify-center items-center gap-2 mt-6 pt-4 border-t">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setInvoicePage(p => Math.max(1, p - 1))}
                          disabled={invoicePage === 1}
                        >
                          ← Prev
                        </Button>
                        <span className="text-sm text-muted-foreground px-3">
                          Page {invoicePage} of {totalPages}
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setInvoicePage(p => Math.min(totalPages, p + 1))}
                          disabled={invoicePage === totalPages}
                        >
                          Next →
                        </Button>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <h2 className="font-heading text-2xl">Clienti ({clients.length})</h2>
              <div className="flex gap-3 items-center">
                <div className="relative flex-1 md:w-64">
                  <input
                    type="text"
                    placeholder="Search by name, email, phone..."
                    value={clientSearchQuery}
                    onChange={e => { setClientSearchQuery(e.target.value); setClientPage(1); }}
                    className="w-full h-10 pl-10 pr-4 border border-input bg-background rounded-md text-sm"
                  />
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <Button onClick={() => setShowAddClientModal(true)}>
                  + Nuovo Cliente
                </Button>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Nome</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Telefono</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Indirizzo</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Codice Fiscale</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Fatture</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Ordini</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Newsletter</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {clients
                      .filter(c => {
                        if (!clientSearchQuery) return true;
                        const q = clientSearchQuery.toLowerCase();
                        return (c.name?.toLowerCase().includes(q) || 
                                c.email?.toLowerCase().includes(q) ||
                                c.phone?.toLowerCase().includes(q) ||
                                c.address?.toLowerCase().includes(q));
                      })
                      .slice((clientPage - 1) * ITEMS_PER_PAGE, clientPage * ITEMS_PER_PAGE)
                      .map(client => (
                        <tr key={client.id} className="hover:bg-muted/30">
                          <td className="px-4 py-3 text-sm font-medium">
                            <div className="flex items-center gap-1.5">
                              {client.verified && (
                                <span className="inline-flex items-center justify-center w-4 h-4 bg-green-500 rounded-full flex-shrink-0" title="Client verificat (a platit)">
                                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                </span>
                              )}
                              {client.name || '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{client.email || '-'}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{client.phone || '-'}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px] truncate" title={client.address}>{client.address || '-'}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{client.codice_fiscale || '-'}</td>
                          <td className="px-4 py-3 text-sm text-center">
                            {client.invoice_count > 0 ? (
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">{client.invoice_count}</span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            {client.order_count > 0 ? (
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">{client.order_count}</span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            {client.newsletter ? '✅' : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex gap-1 justify-center">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openEditClient(client)}
                                title="Edit"
                              >
                                ✏️
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setActiveTab('orders');
                                  startNewInvoice();
                                  setTimeout(() => selectClientForInvoice(client), 500);
                                }}
                                title="New Invoice"
                              >
                                📄
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-500"
                                onClick={() => deleteClient(client.id)}
                                title="Delete"
                              >
                                🗑️
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              
              {/* Clients Pagination */}
              {(() => {
                const filteredClients = clients.filter(c => {
                  if (!clientSearchQuery) return true;
                  const q = clientSearchQuery.toLowerCase();
                  return (c.name?.toLowerCase().includes(q) || 
                          c.email?.toLowerCase().includes(q) ||
                          c.phone?.toLowerCase().includes(q) ||
                          c.address?.toLowerCase().includes(q));
                });
                const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE);
                if (totalPages <= 1) return null;
                return (
                  <div className="flex justify-center items-center gap-2 p-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setClientPage(p => Math.max(1, p - 1))}
                      disabled={clientPage === 1}
                    >
                      ← Prev
                    </Button>
                    <span className="text-sm text-muted-foreground px-3">
                      Page {clientPage} of {totalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setClientPage(p => Math.min(totalPages, p + 1))}
                      disabled={clientPage === totalPages}
                    >
                      Next →
                    </Button>
                  </div>
                );
              })()}
            </div>

            {clients.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <p>Nessun cliente trovato.</p>
                <p className="text-sm mt-2">I clienti vengono aggiunti automaticamente da ordini, fatture e newsletter.</p>
              </div>
            )}

            {/* Edit Client Modal */}
            {showEditClientModal && editingClient && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="font-heading text-2xl">Modifica Cliente</h2>
                    <button onClick={() => { setShowEditClientModal(false); setEditingClient(null); }} className="text-2xl">&times;</button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Nome</label>
                      <Input 
                        value={editingClient.name || ''} 
                        onChange={e => setEditingClient({ ...editingClient, name: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Email</label>
                      <Input 
                        type="email"
                        value={editingClient.email || ''} 
                        onChange={e => setEditingClient({ ...editingClient, email: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Telefono</label>
                      <Input 
                        value={editingClient.phone || ''} 
                        onChange={e => setEditingClient({ ...editingClient, phone: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Indirizzo</label>
                      <Textarea 
                        value={editingClient.address || ''} 
                        onChange={e => setEditingClient({ ...editingClient, address: e.target.value })} 
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Partita IVA</label>
                      <Input 
                        value={editingClient.vat || ''} 
                        onChange={e => setEditingClient({ ...editingClient, vat: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Codice Fiscale</label>
                      <Input 
                        value={editingClient.codice_fiscale || ''} 
                        onChange={e => setEditingClient({ ...editingClient, codice_fiscale: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                    <Button variant="outline" onClick={() => { setShowEditClientModal(false); setEditingClient(null); }}>
                      Annulla
                    </Button>
                    <Button onClick={saveClient}>
                      💾 Salva
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Add Client Modal */}
            {showAddClientModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-background rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="font-heading text-2xl">Nuovo Cliente</h2>
                    <button onClick={() => { setShowAddClientModal(false); setNewClient({ name: '', email: '', phone: '', address: '', vat: '', codice_fiscale: '' }); }} className="text-2xl">&times;</button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Nome *</label>
                      <Input 
                        value={newClient.name} 
                        onChange={e => setNewClient({ ...newClient, name: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Email</label>
                      <Input 
                        type="email"
                        value={newClient.email} 
                        onChange={e => setNewClient({ ...newClient, email: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Telefono</label>
                      <Input 
                        value={newClient.phone} 
                        onChange={e => setNewClient({ ...newClient, phone: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Indirizzo</label>
                      <Textarea 
                        value={newClient.address} 
                        onChange={e => setNewClient({ ...newClient, address: e.target.value })} 
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Partita IVA</label>
                      <Input 
                        value={newClient.vat} 
                        onChange={e => setNewClient({ ...newClient, vat: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Codice Fiscale</label>
                      <Input 
                        value={newClient.codice_fiscale} 
                        onChange={e => setNewClient({ ...newClient, codice_fiscale: e.target.value })} 
                        className="mt-1" 
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                    <Button variant="outline" onClick={() => { setShowAddClientModal(false); setNewClient({ name: '', email: '', phone: '', address: '', vat: '', codice_fiscale: '' }); }}>
                      Annulla
                    </Button>
                    <Button onClick={addNewClient}>
                      ➕ Aggiungi Cliente
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* EXPENSES TAB */}
        {activeTab === 'expenses' && (
          <div data-testid="expenses-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl">Cheltuieli / Spese</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowCategoryManager(!showCategoryManager)} data-testid="manage-categories-btn">
                  Categorie
                </Button>
                <Button size="sm" onClick={() => { setEditingExpense(null); setNewExpense({ description: '', amount: '', date: new Date().toISOString().split('T')[0], category: '', notes: '', attachment_url: '', attachment_filename: '' }); setShowExpenseModal(true); }} data-testid="add-expense-btn">
                  + Aggiungi Spesa
                </Button>
              </div>
            </div>

            {/* Monthly/Yearly Totals Widget */}
            <div className="border rounded-xl p-5 mb-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20" data-testid="expense-totals-widget">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <select value={totalsMonth} onChange={e => { const m = parseInt(e.target.value); setTotalsMonth(m); loadExpenseTotals(totalsYear, m || null); }} className="h-9 px-3 border border-input bg-background rounded-md text-sm" data-testid="totals-month-select">
                  <option value="0">Anno Intero</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i+1} value={i+1}>{new Date(2026, i).toLocaleString('it-IT', { month: 'long' })}</option>
                  ))}
                </select>
                <select value={totalsYear} onChange={e => { const y = parseInt(e.target.value); setTotalsYear(y); loadExpenseTotals(y, totalsMonth || null); }} className="h-9 px-3 border border-input bg-background rounded-md text-sm" data-testid="totals-year-select">
                  {[2024, 2025, 2026, 2027, 2028].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              {expenseTotals && (
                <div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="font-heading text-3xl font-bold">€{expenseTotals.total.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                    <span className="text-sm text-muted-foreground">({expenseTotals.count} spese)</span>
                  </div>
                  {Object.keys(expenseTotals.by_category || {}).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(expenseTotals.by_category).map(([cat, amount]) => (
                        <div key={cat} className="bg-background/70 border rounded-lg px-3 py-1.5 text-xs">
                          <span className="text-muted-foreground">{cat}:</span>{' '}
                          <span className="font-bold">€{amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!totalsMonth && Object.keys(expenseTotals.monthly || {}).length > 1 && (
                    <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800">
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
                        {Object.entries(expenseTotals.monthly).map(([m, data]) => (
                          <div key={m} className="bg-background/50 rounded px-2 py-1 text-center">
                            <div className="text-muted-foreground">{new Date(m + '-01').toLocaleString('it-IT', { month: 'short' })}</div>
                            <div className="font-bold">€{data.total.toLocaleString('it-IT', { minimumFractionDigits: 0 })}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Category Manager */}
            {showCategoryManager && (
              <div className="border rounded-lg p-4 mb-6 bg-muted/30" data-testid="category-manager">
                <h3 className="font-heading text-lg mb-3">Gestione Categorie</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newCategoryName}
                    onChange={e => setNewCategoryName(e.target.value)}
                    placeholder="Nome categoria..."
                    className="max-w-xs"
                    data-testid="category-name-input"
                    onKeyDown={e => e.key === 'Enter' && saveExpenseCategory()}
                  />
                  <Button size="sm" onClick={saveExpenseCategory} data-testid="save-category-btn">
                    {editingExpCat ? 'Aggiorna' : 'Aggiungi'}
                  </Button>
                  {editingExpCat && (
                    <Button size="sm" variant="outline" onClick={() => { setEditingExpCat(null); setNewCategoryName(''); }}>
                      Annulla
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {expenseCategories.map(cat => (
                    <div key={cat.id} className="flex items-center gap-1 bg-background border rounded-full px-3 py-1 text-sm" data-testid={`category-${cat.id}`}>
                      <span>{cat.name}</span>
                      <button onClick={() => { setEditingExpCat(cat); setNewCategoryName(cat.name); }} className="text-muted-foreground hover:text-foreground ml-1" title="Modifica">
                        ✎
                      </button>
                      <button onClick={() => deleteExpenseCategory(cat.id)} className="text-red-400 hover:text-red-600" title="Elimina">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filters & Export */}
            <div className="border rounded-lg p-4 mb-6 bg-muted/20">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                <div>
                  <label className="text-xs text-muted-foreground">Data Inizio</label>
                  <Input type="date" value={expenseFilterStart} onChange={e => setExpenseFilterStart(e.target.value)} data-testid="expense-filter-start" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Data Fine</label>
                  <Input type="date" value={expenseFilterEnd} onChange={e => setExpenseFilterEnd(e.target.value)} data-testid="expense-filter-end" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Categoria</label>
                  <select value={expenseFilterCat} onChange={e => setExpenseFilterCat(e.target.value)} className="w-full h-9 px-3 border border-input bg-background rounded-md text-sm" data-testid="expense-filter-cat">
                    <option value="">Tutte</option>
                    {expenseCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Button size="sm" onClick={loadExpenses} className="w-full" data-testid="expense-filter-btn">Filtra</Button>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => exportExpenses('csv')} data-testid="export-csv-btn">CSV</Button>
                  <Button size="sm" variant="outline" onClick={() => exportExpenses('pdf')} data-testid="export-pdf-btn">PDF</Button>
                </div>
              </div>
              {expenses.length > 0 && (
                <div className="mt-3 text-sm text-muted-foreground">
                  Totale: <span className="font-bold text-foreground">€{expenses.reduce((s, e) => s + (e.amount || 0), 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span> ({expenses.length} spese)
                </div>
              )}
            </div>

            {/* Expenses List */}
            <div className="space-y-2">
              {expenses.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <p className="text-lg mb-2">Nessuna spesa registrata</p>
                  <p className="text-sm">Aggiungi la tua prima spesa con il pulsante sopra</p>
                </div>
              )}
              {expenses.map(expense => (
                <div key={expense.id} className="border rounded-lg p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors" data-testid={`expense-row-${expense.id}`}>
                  <div className="text-sm text-muted-foreground w-24 flex-shrink-0">{expense.date}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{expense.description}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {expense.category && <span className="bg-muted px-2 py-0.5 rounded">{expense.category}</span>}
                      {expense.notes && <span className="truncate">{expense.notes}</span>}
                    </div>
                  </div>
                  {expense.attachment_url && (
                    <a href={`${BACKEND_URL}${expense.attachment_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex-shrink-0" data-testid={`expense-attachment-${expense.id}`}>
                      {expense.attachment_filename || 'Allegato'}
                    </a>
                  )}
                  <div className="font-heading text-lg font-bold w-28 text-right flex-shrink-0">€{expense.amount?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => { setEditingExpense(expense); setNewExpense({ description: expense.description, amount: expense.amount, date: expense.date, category: expense.category || '', notes: expense.notes || '', attachment_url: expense.attachment_url || '', attachment_filename: expense.attachment_filename || '' }); setShowExpenseModal(true); }} data-testid={`edit-expense-${expense.id}`}>
                      ✎
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteExpense(expense.id)} data-testid={`delete-expense-${expense.id}`}>
                      ×
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add/Edit Expense Modal */}
            {showExpenseModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="expense-modal">
                <div className="bg-background rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="font-heading text-2xl">{editingExpense ? 'Modifica Spesa' : 'Nuova Spesa'}</h2>
                    <button onClick={() => { setShowExpenseModal(false); setEditingExpense(null); }} className="text-2xl">&times;</button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Descrizione *</label>
                      <Input value={newExpense.description} onChange={e => setNewExpense({ ...newExpense, description: e.target.value })} placeholder="Es: Colori ad olio, tela..." className="mt-1" data-testid="expense-description" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-muted-foreground">Importo (EUR) *</label>
                        <Input type="number" step="0.01" value={newExpense.amount} onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })} placeholder="0.00" className="mt-1" data-testid="expense-amount" />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Data *</label>
                        <Input type="date" value={newExpense.date} onChange={e => setNewExpense({ ...newExpense, date: e.target.value })} className="mt-1" data-testid="expense-date" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Categoria</label>
                      <select value={newExpense.category} onChange={e => setNewExpense({ ...newExpense, category: e.target.value })} className="w-full h-9 px-3 border border-input bg-background rounded-md text-sm mt-1" data-testid="expense-category">
                        <option value="">Senza categoria</option>
                        {expenseCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Note</label>
                      <Textarea value={newExpense.notes} onChange={e => setNewExpense({ ...newExpense, notes: e.target.value })} placeholder="Note aggiuntive..." rows={2} className="mt-1" data-testid="expense-notes" />
                    </div>
                    
                    {/* File Upload */}
                    <div>
                      <label className="text-sm text-muted-foreground">Allegato (PDF / Immagine)</label>
                      <div className="mt-1">
                        {newExpense.attachment_url ? (
                          <div className="flex items-center gap-2 p-2 border rounded bg-muted/30">
                            <span className="text-sm truncate flex-1">{newExpense.attachment_filename || 'File allegato'}</span>
                            <a href={`${BACKEND_URL}${newExpense.attachment_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline">Vedi</a>
                            <button onClick={() => setNewExpense({ ...newExpense, attachment_url: '', attachment_filename: '' })} className="text-red-400 hover:text-red-600 text-sm">×</button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                onChange={e => { const f = e.target.files?.[0]; if (f) extractFromInvoice(f); e.target.value = ''; }}
                                className="hidden"
                                id="expense-ai-upload"
                                disabled={extractingInvoice || uploadingExpenseFile}
                              />
                              <label htmlFor="expense-ai-upload" className={`cursor-pointer flex-1 text-center px-4 py-3 border-2 border-dashed rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/20 hover:border-amber-400 transition-colors text-sm ${extractingInvoice ? 'opacity-50 animate-pulse' : ''}`} data-testid="ai-upload-btn">
                                {extractingInvoice ? 'AI analizza la fattura...' : 'Carica e Compila Automatico (AI)'}
                                <span className="block text-xs text-muted-foreground mt-1">PDF, JPG, PNG — estrae importo, data e descrizione</span>
                              </label>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span className="border-b flex-1"></span>
                              <span>oppure</span>
                              <span className="border-b flex-1"></span>
                            </div>
                            <div>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                onChange={e => { const f = e.target.files?.[0]; if (f) uploadExpenseAttachment(f); e.target.value = ''; }}
                                className="hidden"
                                id="expense-file-upload"
                                disabled={uploadingExpenseFile}
                              />
                              <label htmlFor="expense-file-upload" className={`cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 border rounded-md hover:bg-muted transition-colors text-xs ${uploadingExpenseFile ? 'opacity-50' : ''}`}>
                                {uploadingExpenseFile ? 'Caricamento...' : 'Solo Allega (senza AI)'}
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                    <Button variant="outline" onClick={() => { setShowExpenseModal(false); setEditingExpense(null); }}>
                      Annulla
                    </Button>
                    <Button onClick={saveExpense} data-testid="save-expense-btn">
                      {editingExpense ? 'Aggiorna' : 'Aggiungi'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ APP ============

function App() {
  useEffect(() => {
    // Seed data on first load
    axios.post(`${API}/seed`).catch(() => {});
    
    // Track visitor
    axios.post(`${API}/track-visitor`).catch(() => {});
    
    // Load and apply theme colors
    axios.get(`${API}/design-settings`).then(res => {
      const settings = res.data;
      const root = document.documentElement;
      if (settings.text_color) root.style.setProperty('--text-color', settings.text_color);
      if (settings.background_color) root.style.setProperty('--background-color', settings.background_color);
      if (settings.button_color) root.style.setProperty('--button-color', settings.button_color);
      if (settings.button_text_color) root.style.setProperty('--button-text-color', settings.button_text_color);
      if (settings.primary_color) root.style.setProperty('--primary-color', settings.primary_color);
    }).catch(() => {});
  }, []);

  return (
    <LanguageProvider>
      <SiteSettingsProvider>
        <AuthProvider>
          <CartProvider>
            <WatermarkProvider>
              <CookieConsentProvider>
                <BrowserRouter>
                  <div className="min-h-screen">
                    <div className="texture-overlay" />
                    <Navbar />
                    <main>
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/gallery" element={<GalleryPage />} />
                        <Route path="/painting/:id" element={<PaintingDetailPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/refund" element={<RefundPage />} />
                      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                      <Route path="/cart" element={<CheckoutPage />} />
                      <Route path="/checkout" element={<CheckoutPage />} />
                      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                      <Route path="/unsubscribe" element={<UnsubscribePage />} />
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/admin/login" element={<LoginPage />} />
                    </Routes>
                  </main>
                  <Footer />
                  <WhatsAppButton />
                  <CookieConsent />
                  <Toaster position="bottom-right" richColors />
                </div>
              </BrowserRouter>
            </CookieConsentProvider>
          </WatermarkProvider>
        </CartProvider>
      </AuthProvider>
    </SiteSettingsProvider>
  </LanguageProvider>
);
}

export default App;
